// Flat-grid tactical Monte-Carlo battle sim.
//
import { reachTargets, fftReach } from './fftPath.js';
import { standSearch, ELEMENTAL_BY_SURFACE, AI_HEALERS, LUNGING } from './abilityFlags.js';
import { zodiacCompat } from './zodiac.js';
import { abilityVsTarget, computeAbilityDamage } from './abilityFormula.js';
import { weaponAttackModel, unarmedAttackModel } from './damage.js';
//
// Two teams start on opposite rows of a flat grid facing each other; units take
// CT/Speed-ordered turns, move toward a target, and act (basic attack or an
// ability). Facing (front/side/back) drives the physical hit chance. Battles
// run to a wipe (or a turn cap → HP tiebreak); runBattles() repeats N times → win%.
//
// PHASE 1: grid + movement + facing + basic attack + death.
// PHASE 2 (this): abilities, MP, charge time, and a core status set, plus a
//   heuristic action chooser so abilities get used.
// NOT yet: a value-maximizing AI (P3), reactions (P4), positional AoE geometry,
//   height/terrain. The engine is pure + data-injected — it takes prepared
//   "combatant specs"; the app converts real units → specs elsewhere.
//
// Combatant spec (read-only; cloned into runtime units):
//   { name, hp, mp, speed, move, range, power, evade:{front,side,back,magic},
//     actions: [ {
//       name, kind:'damage'|'heal'|'revive'|'status'|'buff',
//       target:'enemy'|'ally'|'self', range, aoe, mp, charge,
//       power,        // damage / heal amount (revive = HP restored)
//       magical,      // true → uses magic evade + Shell mitigation
//       status,       // status name (kind status/buff); 'Death' = instant KO
//       duration,     // status turns
//       success,      // % to land a status (default 100)
//     } ] }

// Status groups. Stop/Sleep/Petrify fully incapacitate (no act, no move). Don't
// Act and Don't Move split the two halves; Charm acts for the enemy; Berserk
// forces an attack; Frog limits to a weak basic attack; Darkness halves physical
// accuracy; Reflect bounces single-target magic back toward the caster's side.
// AI tuning knobs read by the scorers, set from the battle config at the start of each battle
// (simulateBattle) or decision (decideTurn). statusWeight scales how much the AI values landing a
// status ailment against dealing damage.
const TUNE = { statusWeight: 1 };
const setTune = (config) => { TUNE.statusWeight = config.statusWeight ?? 1; };
const STOP_LIKE = ['Stop', 'Sleep', 'Petrify'];
const NO_ACT = ['Stop', 'Sleep', 'Petrify', "Don't Act"];
const NO_MOVE = ['Stop', 'Sleep', 'Petrify', "Don't Move"];
// Stat-mod bounds (songs/dances/talk/breaks). PA/MA cap the accumulated BONUS (re-baked);
// Speed/Brave/Faith cap the live value. `appliedDelta` = how much a mod actually moves the
// stat after the cap — so the AI stops re-singing once the team is maxed. STAT_VALUE prices
// a single point (Speed buys turns, so it's worth most).
const STAT_CAP = { pa: [-6, 6], ma: [-6, 6], speed: [4, 13], brave: [5, 95], faith: [5, 95] };
const STAT_VALUE = { pa: 9, ma: 9, speed: 14, brave: 4, faith: 4 };
// How much a stat-mod actually moves the stat, respecting the cap DIRECTIONALLY: a buff
// never reduces an already-high stat and a debuff never raises an already-low one (so the
// cap is a soft ceiling/floor, not a clamp that disturbs out-of-range base stats).
function appliedDelta(t, mod) {
  const cur = (mod.stat === 'pa' || mod.stat === 'ma') ? ((mod.stat === 'pa' ? t.paBonus : t.maBonus) || 0)
    : mod.stat === 'speed' ? t.speed : mod.stat === 'brave' ? (t.brave ?? 50) : (t.faith ?? 50);
  const [lo, hi] = STAT_CAP[mod.stat];
  return mod.delta > 0 ? Math.max(0, Math.min(mod.delta, hi - cur)) : Math.min(0, Math.max(mod.delta, lo - cur));
}
const DEAD_TURNS = 3; // a corpse lingers this many of its own ATs, then crystallizes
const crystalTicks = (d) => (d.dead == null || hasStatus(d, 'Reraise') ? Infinity : Math.ceil((100 * (d.dead + 1) - (d.ct || 0)) / Math.max(1, d.speed || 1)));
const manh = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
const opp = (team) => (team === 'A' ? 'B' : 'A');

// Direction the attacker comes from, relative to the target's (axis-aligned) facing.
// Which side of `target` an attacker at (ax, ay) is on, as FFTBG's evade setup decides it (0x801854FC): along
// the axis the attacker is farther out on, it's the front if the target faces that way, the back if it faces
// away, else the side; on an exact diagonal it's the front if the target faces either way toward it, else the
// side, never the back. (We used to sum both axes, so a foe three panels to the side and one ahead was in front.)
export function relativeDirection(ax, ay, target) {
  const dx = ax - target.x, dy = ay - target.y, f = target.facing, adx = Math.abs(dx), ady = Math.abs(dy);
  const on = (d, fd) => (fd && Math.sign(d) === fd ? 'front' : fd && Math.sign(d) === -fd ? 'back' : 'side');
  if (adx > ady) return on(dx, f.x);
  if (ady > adx) return on(dy, f.y);
  if (!adx) return 'back';
  return on(dx, f.x) === 'front' || on(dy, f.y) === 'front' ? 'front' : 'side';
}

function faceToward(u, t) {
  const dx = t.x - u.x, dy = t.y - u.y;
  if (Math.abs(dx) >= Math.abs(dy)) u.facing = { x: Math.sign(dx) || 0, y: 0 };
  else u.facing = { x: 0, y: Math.sign(dy) };
  if (!u.facing.x && !u.facing.y) u.facing = { x: 0, y: 1 };
}

const hasStatus = (u, s) => !!(u.status && u.status[s]);
const stopped = (u) => STOP_LIKE.some((s) => hasStatus(u, s));   // no act AND no move
const cantAct = (u) => NO_ACT.some((s) => hasStatus(u, s));
const cantMove = (u) => NO_MOVE.some((s) => hasStatus(u, s));
const cantReact = (u) => stopped(u) || berserk(u) || hasStatus(u, 'Confusion') || hasStatus(u, "Don't Act"); // also blocked by Berserk / Confusion / Don't Act (BMG)
const charmed = (u) => hasStatus(u, 'Charm');
const berserk = (u) => hasStatus(u, 'Berserk');
const frogged = (u) => hasStatus(u, 'Frog');
const blinded = (u) => hasStatus(u, 'Darkness');                  // halves physical accuracy
// AIRBORNE: a unit mid-Jump has LEFT the field — it can't be targeted, splashed, or
// math-hit by anyone until it lands (the jump action carries `jump:true`). This is the
// key Jump distinction: charging a SPELL keeps you standing there, exposed; a Jump
// removes you entirely, so the Lancer is untouchable while in the air (BMG / FFT).
const airborne = (u) => !!(u.charging && u.charging.action && u.charging.action.jump);
// Which evasion can dodge `action`: the game's per-ability answer when simPrep has it (`evade`, from the
// formula code: Holy and the summons none, Fire and Slow magic, Mutilate and Beak physical), else magic evade
// for magic and evade by facing for the rest, none for Jump and Throw.
const evadeOf = (action) => action.evade ?? (action.ignoreEvade ? 'none' : action.magical ? 'magic' : 'physical');
// A target that can't evade at all: asleep, stopped, Don't Act or confused (the game's evade setup, 0x801853F4).
// Charging doesn't stop it.
const EVADELESS = ['Sleep', 'Stop', "Don't Act", 'Confusion'];
// The chance `t` doesn't dodge `action` from `u` standing at `from`, as FFTBG's evade check works it out. Each
// source (class, shield, accessory, weapon; simPrep's evadeSrc) passes ⌊(base − evade) × 100 / base⌋ percent,
// and the pass rate is ⌊the product / 10⁶⌋ (0x801858D0). The base starts at 100 and moves with the fight:
// halved (every evade doubled) for a Defending target, an Abandon one that can react, and, against physical
// attacks, an attacker with Darkness or Confusion; doubled (evade halved) for a target that's Performing a
// song or dance. Concentrate skips physical evade, a Transparent attacker skips both. (Darkness used to
// halve the hit instead, which cut even back attacks on targets with no evade at all.)
function evadeHit(u, action, t, from) {
  const kind = evadeOf(action);
  if (kind === 'none' || hasStatus(u, 'Transparent') || (kind === 'physical' && u.concentrate) || EVADELESS.some((s) => hasStatus(t, s))) return 1;
  const dir = kind === 'magic' ? 'magic' : t.facing ? relativeDirection(from.x, from.y, t) : 'front';
  // A Brave-gated block reaction is one more evade, its rate the target's Brave, from any side: FFTBG's AI listed an
  // Attack into a Brave-54 Blade Grasp Samurai at 46 from behind and 33 where its class evade left 73 (⌊73 × 46%⌋),
  // and at 36 / 30 into a Brave-64 one. Arrow Guard does the same against a shot.
  const block = kind === 'physical' && !cantReact(t) && (t.reaction === 'Blade Grasp' || (t.reaction === 'Arrow Guard' && action.projectile)) ? (t.brave ?? 50) : 0;
  const src0 = t.evadeSrc && t.evadeSrc[dir];
  if (!src0) return Math.max(0, Math.floor(100 - ((t.evade && t.evade[dir]) || 0) + 1e-9) / 100 * (kind === 'physical' && blinded(u) ? 0.5 : 1) * (100 - block) / 100);
  const src = block ? [...src0, block] : src0;
  let base = 100;
  if (kind === 'physical' && (blinded(u) || hasStatus(u, 'Confusion'))) base = Math.floor(base / 2);
  if (t.abandon && !cantReact(t)) base = Math.floor(base / 2);
  if (hasStatus(t, 'Performing') || (t.charging && t.charging.action && fieldWide(t.charging.action))) base *= 2; // Performing
  if (hasStatus(t, 'Defending')) base = Math.floor(base / 2);
  let prod = 1, n = 0;
  for (const ev of src) {
    if (!ev) continue;
    if (ev >= base) return 0;
    prod *= Math.floor((base - ev) * 100 / base); n++;
  }
  return n ? Math.floor(prod / 100 ** (n - 1)) / 100 : 1;
}
// Physical damage is ×3/2 against a unit that can't defend itself: mid-charge, asleep,
// a frog, or a chicken (the BMG MOD-2 defender-side damage bonuses).
// Damage-taken statuses, as FFTBG's own status help (infostatus.txt) states them and its build
// confirms: Charging +25% from physical attacks, Sleep +50% physical, Frog and Chicken +50% from
// ALL attacks. (Was a flat +50% physical for all four; the emulator showed a charging target
// taking ×5/4.) Performing only halves evasion: no damage bonus.
const vulnMult = (u, physical) => {
  let m = 1;
  if (physical && !!u.charging && !airborne(u)) m *= 5 / 4;
  if (physical && hasStatus(u, 'Sleep')) m *= 3 / 2;
  if (frogged(u) || hasStatus(u, 'Chicken')) m *= 3 / 2;
  return m;
};
// Effective Faith of a target for faith-based magic: Innocent ⇒ 0 (near-immune),
// Faith ⇒ 100, else the unit's own Faith. simPrep bakes status-success/heal at a
// generic 50, so resolution rescales by effFaith/50 to use the REAL target faith.
const effFaith = (t) => (hasStatus(t, 'Innocent') ? 0 : hasStatus(t, 'Faith') ? 100 : (t.faith ?? 50));
const critical = (u) => u.alive && u.hp <= Math.max(1, u.maxHp * 0.2) && !hasStatus(u, 'Cursed'); // Cursed is immune to Critical
// The game's success rate for a "Hit:" clause against this target (FFT's MOD 1 / MOD 6, as abilityFormula's
// computeSuccess): the caster's stat part, for Faith-gated ones through Magic Attack Up, Magic Defense Up and
// Shell; plus the constant; plus the zodiac addend (⌊stat/4⌋ + ⌊constant/4⌋ for Good, halves for Best, the
// negatives for Bad/Worst); then for Faith ones ⌊caster Faith × target Faith × that / 10000⌋. FFTBG's Death
// Sentence, Hit (MA + 34)% from an MA-9 Squire, read 53 against a Good-compatibility target: 9 + 34 + 2 + 8.
// We used to take MA + K and scale it by the target's Faith / 50, so a Faith(MA + 195)% spell sat at 100%
// where the game has 86% (Faith 70 against 60, MA 10). Actions without the parts keep the old estimate.
const ZODIAC_ADD = { Good: 0.25, Best: 0.5, Bad: -0.25, Worst: -0.5 };
// Which of those steps a skill takes is its formula's (simPrep's `modMask`, see damageMods), in the game's order:
// Attack Up ×4/3 and Martial Arts ×3/2 (unarmed), Magic Attack Up ×4/3, the caster's Berserk ×3/2; then the
// target's Defense Up and Protect ×2/3, Sleep ×3/2 or else charging ×5/4 and Frog / Chicken ×3/2 (the physical
// ones: the Rends, Steals, Mutilate, Beak); Magic Defense Up and Shell ×2/3 and Frog / Chicken ×3/2 (Slow, Doom,
// Peck); zodiac; Faith. So Protect and Raise ignore the target's Shell, Doom feels it, a Rend feels Protect, and
// a song or dance takes nothing, not even zodiac. Without a mask, Faith clauses take the magic steps.
function successOf(u, action, t) {
  const h = action.hit;
  if (!h) return action.faith ? (action.success ?? 100) * effFaith(t) / 50 : (action.success ?? 100);
  const m = action.modMask ?? (h.faith ? MOD_ZODIAC | MOD_MAU | MOD_MDEF | MOD_FAITH : MOD_ZODIAC);
  const step = (x, n, d) => Math.floor(x * n / d);
  let st = h.s;
  if (m & MOD_AU) { if (h.au) st = step(st, 4, 3); if (h.ma) st = step(st, 3, 2); }
  if (m & MOD_MAU && h.mau) st = step(st, 4, 3);
  if (m & MOD_BERSERK && hasStatus(u, 'Berserk')) st = step(st, 3, 2);
  if (m & MOD_PDEF) {
    if (t.defenseUp) st = step(st, 2, 3);
    if (hasStatus(t, 'Protect')) st = step(st, 2, 3);
    if (hasStatus(t, 'Sleep')) st = step(st, 3, 2);
    else {
      if (t.charging && !airborne(t)) st = step(st, 5, 4);
      if (frogged(t) || hasStatus(t, 'Chicken')) st = step(st, 3, 2);
    }
  }
  if (m & MOD_MDEF) {
    if (t.magicDefenseUp) st = step(st, 2, 3);
    if (hasStatus(t, 'Shell')) st = step(st, 2, 3);
    if (frogged(t) || hasStatus(t, 'Chicken')) st = step(st, 3, 2);
  }
  const zc = m & MOD_ZODIAC && u.sign && t.sign ? zodiacCompat(u.gender, u.sign, t.gender, t.sign) : 'Neutral', f = ZODIAC_ADD[zc] || 0;
  const z = f ? Math.sign(f) * (Math.floor(st * Math.abs(f)) + Math.floor(h.k * Math.abs(f))) : 0;
  const raw = st + h.k + z;
  return Math.max(0, Math.min(100, m & MOD_FAITH ? Math.floor(effFaith(u) * effFaith(t) * raw / 10000) : raw));
}
// Undead (skeleton/ghost families flagged in prep, or the Zombie/Undead status):
// healing damages them, and on death they ~50% reanimate instead of crystallizing.
export const isUndead = (u) => !!(u.undead || hasStatus(u, 'Undead'));
// The team a unit attacks / supports — swapped while Charmed (acts for the foe).
const foeTeamOf = (u) => (charmed(u) ? u.team : opp(u.team));
const friendTeamOf = (u) => (charmed(u) ? opp(u.team) : u.team);
// A Frog can only flail: a weak basic attack (FFT frog damage is tiny).
const frogBasic = (u) => ({ ...BASIC(u), weapon: null, power: Math.max(1, Math.round((u.power || 1) * 0.25)) });
const effSpeed = (u) => Math.max(1, Math.round(u.speed * (hasStatus(u, 'Haste') ? 1.5 : 1) * (hasStatus(u, 'Slow') ? 0.5 : 1)));

// ---- Defender-side damage modifiers (applied at resolution, not baked in prep) ----
const ZODIAC_MULT = { Best: 1.5, Good: 1.25, Neutral: 1, Bad: 0.75, Worst: 0.5 };
const zodiacMult = (att, t) => (att.sign && t.sign ? (ZODIAC_MULT[zodiacCompat(att.gender, att.sign, t.gender, t.sign)] ?? 1) : 1);
// Element affinity from the target's gear: 0 = cancel/immune, -1 = absorb (heal),
// 0.5 = half, 2 = weak, 1 = none.
function elementMult(element, t) {
  const e = t.elements;
  if (!element || !e) return 1;
  if (e.cancel && e.cancel.has(element)) return 0;
  if (e.absorb && e.absorb.has(element)) return -1;
  if (e.half && e.half.has(element)) return 0.5;
  if (e.weak && e.weak.has(element)) return 2;
  return 1;
}
// Combined defender multiplier on raw damage. Magic also scales by the REAL target
// Faith (the prep baked in a generic 50; Innocent/faith-0 ⇒ ~immune). Negative ⇒
// the hit is absorbed (heals the target); 0 ⇒ cancelled.
// Does `action`'s damage take this modifier? simPrep's `modMask` is the game's own answer (MOD_* bits, from
// the formula code: Lifebreak takes none, Compress zodiac only); without one, every modifier applies.
const MOD_ZODIAC = 1, MOD_PDEF = 2, MOD_CRIT = 4, MOD_ELEMENT = 8, MOD_AU = 16, MOD_MAU = 32, MOD_FAITH = 64, MOD_BERSERK = 128, MOD_MDEF = 256;
const MOD_DEFENSE = MOD_PDEF | MOD_MDEF;
const takes = (action, bit) => action.modMask == null || (action.modMask & bit) !== 0;
// A heal's amount on t: a share of max HP (items, Elixir), else its power, scaled by the target's Faith for a
// "Faith(...)" heal and by zodiac where its formula takes it (the game's mask: Cure, Spirit of Life, Chakra do;
// items have none). FFTBG's Treant healed a Bad-match ally for 120 with Spirit of Life, MA 40 × 4 × 3/4.
const healOf = (u, action, t) => {
  if (action.pct) return t.maxHp * action.pct / 100;
  let h = action.faith ? action.power * effFaith(t) / 50 : action.power;
  if (action.modMask != null && (action.modMask & MOD_ZODIAC)) h *= zodiacMult(u, t);
  return h;
};
export function damageMult(att, action, t) {
  if (action.magical && action.faith && hasStatus(t, 'Innocent')) return 0; // Innocent → faith 0 → only FAITH magic null (flat MA spells still land)
  const elemental = takes(action, MOD_ELEMENT);
  let el = elemental ? elementMult(action.element, t) : 1;
  if (elemental && action.element === 'Earth' && hasStatus(t, 'Float')) el = 0;    // Float negates Earth
  if (el === 0) return 0;
  let m = (takes(action, MOD_ZODIAC) ? zodiacMult(att, t) : 1) * el;
  if (elemental && action.element === 'Fire' && hasStatus(t, 'Oil')) m *= 2;       // Oil doubles Fire
  if (action.magical && action.faith) m *= (t.faith ?? 50) / 50;      // only FAITH magic (Fire/Cure) scales with target faith; flat MA magic (Draw Out, Choco Meteor, breaths) does not
  return m;
}

// The game's own damage for a formula ability against this target (simPrep keeps the formula and the
// caster's numbers in action.formula): every modifier scales the stat term in turn, floored at each
// step, then ⌊CFa × TFa × Q × XA / 10000⌋ with both units' real Faith, then the target's element.
// The emulator's trials match it to the point (Shiva 59 where Faith-50-and-rescale said 59.28).
// Negative = absorbed, 0 = cancelled; null when the action has no formula. Cached per action, since
// the AI asks the same question many times a turn.
const formulaCache = new WeakMap(), elementIds = new WeakMap();
let elementSeq = 0;
// The basic Attack's model, damage.js's own (Dual Wield's second strike uses the off hand, or a fist).
const weaponModels = new WeakMap();
function weaponModel(w, strike) {
  let m = weaponModels.get(w);
  if (!m) weaponModels.set(w, (m = {}));
  if (!(strike in m)) {
    const item = strike === 2 ? w.off : w.main, who = { SupportSkill: strike === 2 ? '' : w.support, Class: w.cls };
    m[strike] = (item && weaponAttackModel(item, who)) || unarmedAttackModel(who);
  }
  return m[strike];
}
function formulaDamage(att, action, t) {
  const f = action.formula || (action.weapon ? { ctx: action.weapon.ctx } : null);
  if (!f) return null;
  const elemental = takes(action, MOD_ELEMENT), defended = takes(action, MOD_DEFENSE);
  if (elemental && action.element === 'Earth' && hasStatus(t, 'Float')) return 0;
  const zodiac = takes(action, MOD_ZODIAC) && att.sign && t.sign ? zodiacCompat(att.gender, att.sign, t.gender, t.sign) : 'Neutral';
  const flags = [hasStatus(t, 'Shell'), hasStatus(t, 'Protect'), !!t.magicDefenseUp, !!t.defenseUp, !!t.charging && !airborne(t), hasStatus(t, 'Sleep'), frogged(t) || hasStatus(t, 'Chicken')].map((x) => defended && x)
    .concat(elemental && action.element === 'Fire' && hasStatus(t, 'Oil'));
  const e = elemental ? t.elements : null;
  if (e && !elementIds.has(e)) elementIds.set(e, ++elementSeq);
  const strike = action.strike || 1, berserk = !!action.weapon && hasStatus(att, 'Berserk');
  const key = `${effFaith(att)}|${effFaith(t)}|${zodiac}|${flags.map(Number).join('')}|${e ? elementIds.get(e) : 0}|${strike}|${+berserk}`;
  let byKey = formulaCache.get(action);
  if (!byKey) formulaCache.set(action, (byKey = new Map()));
  if (byKey.has(key)) return byKey.get(key);
  const c = f.ctx;
  const mods = {
    // Martial Arts lifts a weapon-power formula (Holy Sword's PA × (WP + 1)) only bare-handed: FFTBG's armed Monk
    // did 140 with Stasis Sword (168, Protect, Zodiac Good), not 210 (VALTRACE). Punch Arts get it either way.
    martialArts: !!c.martialArts && !(c.wp && /\bWP\b/.test(f.info || '')), attackUp: !!c.attackUp, magicAttackUp: !!c.magicAttackUp, zodiac,
    ...(c.strengthen && c.strengthen.length ? { strengthen: new Set(c.strengthen) } : {}),
    shell: flags[0], protect: flags[1], magicDefendUp: flags[2], defenseUp: flags[3], charging: flags[4], sleeping: flags[5], frogChicken: flags[6],
    ...(e ? { cancel: e.cancel, weak: e.weak, half: e.half, absorb: e.absorb } : {}),
  };
  const cx = { ...c, faith: effFaith(att), targetFaith: effFaith(t) };
  let fin;
  if (action.weapon) { const model = weaponModel(action.weapon, strike); fin = computeAbilityDamage(model, cx, { ...mods, martialArts: false, berserk }).final; }
  else { const r = abilityVsTarget(f.info, cx, mods); fin = r && r.kind === 'damage' ? r.final : null; }
  let v = fin == null ? null : (Array.isArray(fin) ? (fin[0] + fin[1]) / 2 : fin);
  if (v != null && flags[7]) v *= 2; // Oil doubles Fire
  byKey.set(key, v);
  return v;
}

// How long statuses last, as FFT counts them. The timed ones count down in CLOCKTICKS, whether or
// not the unit gets a turn; these are the starting values of the 16 status counters in FFTBG's unit
// records (+0x5D..+0x6C), read off its build in the emulator. At Speed 10 a turn is ~10 ticks, so
// Stop lasts ~2 turns and Poison ~4. Death Sentence counts the victim's own turns (3); Defending
// ends at the unit's next turn; everything else (Darkness, Silence, Oil, Frog, Petrify, Confusion,
// Berserk, Float, Reraise…) lasts until cured. (Every status used to last 6 of the victim's turns:
// Stop three times too long, Darkness and Oil wearing off. Status-heavy monsters, Wyvern and both
// Malboros, were the units the sim over-rated most against real results.)
const STATUS_TICKS = { Poison: 40, Regen: 40, Protect: 36, Shell: 36, Haste: 28, Slow: 28, Stop: 20, Wall: 24, Faith: 36, Innocent: 36, Charm: 28, Sleep: 48, "Don't Move": 24, "Don't Act": 24, Reflect: 32 };
const OWN_TURNS = { 'Death Sentence': 3, Defending: 1 };
const PERMANENT = 999;
const immuneTo = (u, name) => !!(u.immune && u.immune.includes(name));
const isPermanent = (u, name) => !!(u.permStatus && u.permStatus.includes(name));
// What a status takes away when it's added, and what it can't be put on: bytes 6-10 and 11-15 of each
// status's 16-byte record in the game's status table (SCUS 0x80065DE4; byte 3 is the duration, as in
// STATUS_TICKS). Charging and Performing are a spell or a dance in progress (u.charging), so Sleep, Charm,
// Confusion, Berserk, Chicken and Petrify end one; Frog ends a dance but not a spell.
const STATUS_CANCELS = {
  Undead: ['Reraise', 'Death Sentence'], Cursed: ['Critical'],
  Petrify: ['Charging', 'Defending', 'Performing', 'Float', 'Transparent', 'Berserk', 'Stop', 'Sleep', 'Reflect', 'Death Sentence'],
  Confusion: ['Charging', 'Defending', 'Performing', 'Charm', 'Sleep'], 'Blood Suck': ['Charging', 'Defending', 'Performing'],
  Berserk: ['Charging', 'Defending', 'Performing', 'Sleep'], Chicken: ['Charging', 'Defending', 'Performing', 'Float'],
  Frog: ['Defending', 'Performing', 'Float'], Charm: ['Charging', 'Defending', 'Performing', 'Confusion'],
  Sleep: ['Charging', 'Defending', 'Performing', 'Confusion', 'Berserk'],
  Poison: ['Regen'], Regen: ['Poison'], Haste: ['Slow'], Slow: ['Haste'], Faith: ['Innocent'], Innocent: ['Faith'],
};
const STATUS_BLOCKED_BY = { Reraise: ['Undead'], 'Death Sentence': ['Undead', 'Death Sentence'], Critical: ['Cursed'] };
const blockedOn = (u, name) => (STATUS_BLOCKED_BY[name] || []).some((b) => (b === 'Undead' ? isUndead(u) : hasStatus(u, b)));
// Would adding `name` end the spell or dance u has under way?
const endsCharge = (u, name) => !!(u.charging && u.charging.action && !u.charging.action.jump
  && (STATUS_CANCELS[name] || []).includes(fieldWide(u.charging.action) ? 'Performing' : 'Charging'));
// The statuses one hit is adding: they all go on together, (old minus what they cancel) plus what they add,
// so Bad Breath's Petrify doesn't take away the Sleep it lands in the same breath.
let hitAdds = null;
function addStatus(u, name, length) {
  if (immuneTo(u, name) || blockedOn(u, name)) return;
  if (hitAdds) hitAdds.add(name);
  u.status = u.status || {};
  const n = length ?? STATUS_TICKS[name] ?? OWN_TURNS[name] ?? PERMANENT;
  u.status[name] = Math.max(u.status[name] || 0, n);
  if (endsCharge(u, name)) u.charging = null;
  for (const c of STATUS_CANCELS[name] || []) if (c !== name && u.status[c] != null && !isPermanent(u, c) && !(hitAdds && hitAdds.has(c))) delete u.status[c];
}
// One clocktick off every timed status.
function tickTimers(u) {
  if (!u.status) return;
  for (const k of Object.keys(u.status)) if (STATUS_TICKS[k] != null && u.status[k] < PERMANENT && --u.status[k] <= 0) delete u.status[k];
}

const statusTick = (u) => Math.floor(u.maxHp / 8);
// A turn that ends on a Poisoned marsh poisons the unit unless it floats: the turn-end step after the
// Regen/Poison tick (0x8018DA88) looks the panel's surface up in SCUS's surface table (0x8005EA10), where
// surface 11 is the only one flagged. The AI's per-panel trial counts it, so it stands elsewhere when it can.
const POISON_MARSH = 11;
const onPoisonMarsh = (u, f, x, y) => !!(f && f.surface && f.surface(x, y) === POISON_MARSH) && !hasStatus(u, 'Float');
function marshPoison(u, f, log) {
  if (!u.alive || airborne(u) || hasStatus(u, 'Petrify') || !onPoisonMarsh(u, f, u.x, u.y)) return;
  const had = hasStatus(u, 'Poison'); addStatus(u, 'Poison');
  if (log && !had && hasStatus(u, 'Poison')) log.push(`${u.name} is poisoned by the marsh`);
}
// At the start of the unit's own turn: count down Death Sentence and Defending.
function tickStatus(u, occupied, log) {
  if (!u.status) return;
  // Death Sentence is a 3-turn countdown to death, NOT an instant kill — when
  // its timer runs out on the unit's own turn, the unit finally dies.
  const dsLethal = u.status['Death Sentence'] != null && u.status['Death Sentence'] - 1 <= 0;
  for (const k of Object.keys(u.status)) if (OWN_TURNS[k] != null && u.status[k] < PERMANENT && --u.status[k] <= 0) delete u.status[k];
  if (dsLethal && u.alive) { u.hp = 0; kill(u, occupied, log); }
}
// At the end of the unit's turn (the turn sequence's step 19, 0x8018D7C8): Poison takes, else Regen gives,
// 12.5% of max HP, rounded down (Poison took 39 of 316 and 41 of 334 in our logged battles, Regen gave 50 of
// 407). All 95 ticks in our logs landed right after the unit's turn ended, none before it acted. Poison is
// damage, so it can kill. Not on a petrified unit.
function turnEndTick(u, occupied, log) {
  if (!u.alive || !u.status || airborne(u) || hasStatus(u, 'Petrify')) return;
  if (u.status.Poison) { u.hp -= statusTick(u); if (u.hp <= 0) { u.hp = 0; kill(u, occupied, log); if (log) log.push(`${u.name} succumbs to poison`); } }
  else if (u.status.Regen) u.hp = Math.min(u.maxHp, u.hp + statusTick(u));
}

// Snapshot of every unit's HP after an action — makes the log easy to follow.
function logState(units, log) {
  const side = (t) => units.filter((u) => u.team === t).map((u) => (u.alive ? `${u.name} ${u.hp}@${u.x},${u.y}` : `${u.name} KO`)).join(', ');
  log.push(`    · A: ${side('A')} | B: ${side('B')}`);
}

function kill(t, occupied, log) {
  if (hasStatus(t, 'Reraise')) {
    delete t.status.Reraise;
    t.hp = Math.ceil(t.maxHp / 10); // BMG: Reraise revives at MaxHP/10
    if (log) log.push(`${t.name} reraises`);
    return;
  }
  t.alive = false;
  t.status = {};        // Dead cancels every status, except the ones gear or class make permanent (a Cursed Ring's Undead)
  for (const st of t.permStatus || []) t.status[st] = PERMANENT;
  t.charging = null;
  t.dead = DEAD_TURNS;  // becomes a body that still blocks its tile (stays in `occupied`)
  if (log) log.push(`${t.name} falls`);
}

// A corpse's tile frees and leaves a walkable crystal; the body itself is gone.
function crystallize(t, occupied, crystals, log) {
  occupied.delete(`${t.x},${t.y}`);
  crystals.add(`${t.x},${t.y}`);
  t.dead = null; t.crystallized = true;
  if (log) log.push(`${t.name} crystallizes`);
}

// An undead corpse reanimates instead of crystallizing: back up at a random
// 1..MaxHP (BMG), keeping its tile (the body never left `occupied`).
function reviveUndead(u, rng, log) {
  u.alive = true; u.dead = null; u.charging = null;
  u.hp = 1 + Math.floor(rng() * u.maxHp);
  if (log) log.push(`${u.name} reanimates (${u.hp})`);
}

// A living unit ending its move on a crystal absorbs it → full HP + MP.
function absorbCrystal(u, crystals, log) {
  const k = `${u.x},${u.y}`;
  if (!crystals.has(k)) return false;
  crystals.delete(k);
  u.hp = u.maxHp; u.mp = u.maxMp ?? u.mp;
  if (log) log.push(`${u.name} absorbs the crystal → full HP/MP`);
  return true;
}

const POTION_HEAL = 100;

// One attack from `att` onto `t`, including the target's reactions (Brave%-gated).
// isReaction=true for reaction-attacks so they don't recursively re-trigger.
function dealAttack(att, action, t, occupied, rng, log, isReaction) {
  if (!t.alive || hasStatus(t, 'Petrify')) return; // petrified units are stone — immune to all damage
  if (action.throwOpts) { const v = throwPick(att, action, t, rng); if (log) log.push(`${att.name} throws ${v.item}`); return dealAttack(att, v, t, occupied, rng, log, isReaction); }
  const physical = !action.magical;
  const brave = t.brave || 50;
  const canReact = !isReaction && !cantReact(t) && att !== t; // no reacting to your own friendly fire

  // Hamedo (First Strike) pre-empts a physical attack with a basic of its own — but ONLY
  // if the reactor can actually reach its attacker (BMG: "your monk can't Hamedo an
  // archer"). Gate on the reactor's own weapon range, same as Counter below: melee↔melee
  // procs, ranged→melee doesn't (out of reach), ranged→in-range-ranged does. A ranged
  // (Equip Gun) Hamedo unit's longer `range` lets it strike back, so it's covered too.
  // It is ALSO useless against a monster's attack: a monster's species attack (and every
  // monster skill) isn't an Attack directive, so Hamedo never reacts to it — UNLESS the
  // monster is Berserked, which forces the generic basic Attack (so ~the only ~3% case a
  // monster can be First-Struck).
  const monsterPoke = att.gender === 'Monster' && !hasStatus(att, 'Berserk');
  if (canReact && physical && !monsterPoke && t.reaction === 'Hamedo' && att.alive && manh(t, att) <= (t.range || 1) && rng() * 100 < brave) {
    if (log) log.push(`${t.name} Hamedo!`);
    dealAttack(t, BASIC(t), att, occupied, rng, log, true); // strikes first, original negated
    return;
  }
  if (canReact && physical && t.reaction === 'Blade Grasp' && rng() * 100 < brave) {
    if (log) log.push(`${t.name} Blade Grasp negates ${att.name}`);
    return;
  }
  if (canReact && physical && att.projectile && t.reaction === 'Arrow Guard' && rng() * 100 < brave) {
    if (log) log.push(`${t.name} Arrow Guard deflects ${att.name}`);
    return;
  }

  const dir = relativeDirection(att.x, att.y, t);
  if (rng() >= evadeHit(att, action, t, att)) { if (log) log.push(`${att.name}'s ${action.name} misses ${t.name} (${dir})`); return; }
  // %-HP / drain attacks carry an inherent success% (faith-gated for Demi/Lich etc.).
  if (action.success != null) {
    const succ = successOf(att, action, t);
    if (rng() * 100 >= succ) { if (log) log.push(`${t.name} resists ${att.name}`); return; }
  }

  // Defender-side scaling: zodiac compatibility, real target Faith (magic), and
  // elemental affinity. mult 0 = cancelled (immune); mult < 0 = absorbed (heals).
  // %-of-MaxHP attacks (Demi/Lich/Hurricane) ignore zodiac/faith — only element applies.
  const exact = action.pctMaxHp ? null : formulaDamage(att, action, t);
  const mult = exact != null ? Math.sign(exact) : action.pctMaxHp ? elementMult(action.element, t) : damageMult(att, action, t);
  if (mult === 0) { if (log) log.push(`${t.name} is immune to ${att.name}`); return; }
  // base damage: %-of-MaxHP, the CASTER's missing HP (Goblin Punch & co., ×factor), the
  // TARGET's missing HP (Climhazzard), or a flat power.
  const missing = action.casterMissingHp ? (att.maxHp - att.hp) * action.casterMissingHp
    : action.targetMissingHp ? (t.maxHp - t.hp) : action.targetCurrentMp ? (t.mp || 0) : null;
  let dmg = exact != null ? Math.max(1, Math.round(Math.abs(exact)))
    : action.pctMaxHp
    ? Math.max(1, Math.ceil(t.maxHp * action.pctMaxHp / 100 * Math.abs(mult))) // rounded up: FFTBG's Demi took 230 → 172 and 274 → 205
    : missing != null
      ? Math.max(1, Math.round(missing * Math.abs(mult)))
      : Math.max(1, Math.round((action.power || 0) * Math.abs(mult)));
  if (action.drain === 'mp') { // MP damage/drain (Dark Sword, Spell Absorb) — hits MP, never HP
    const d = Math.min(dmg, t.mp || 0); t.mp -= d;
    att.mp = Math.min(att.maxMp ?? att.mp, (att.mp || 0) + d);
    if (log) log.push(`${att.name}'s ${action.name} drains ${d} MP from ${t.name}`);
    return;
  }
  // Protect/Shell and the status multipliers scale a normal hit's damage, NOT a %-HP attack's (a
  // formula ability already has them, in the game's order).
  const defended = takes(action, MOD_DEFENSE); // Lifebreak, Compress and co. ignore all of these
  if (defended && exact == null && !action.pctMaxHp && action.magical && (hasStatus(t, 'Shell') || t.magicDefenseUp)) dmg = Math.floor(dmg * 2 / 3); // Shell / Magic Defense Up
  if (defended && exact == null && !action.pctMaxHp && !action.magical && (hasStatus(t, 'Protect') || t.defenseUp)) dmg = Math.floor(dmg * 2 / 3);    // Protect / Defense Up
  if (defended && !action.pctMaxHp && hasStatus(t, 'Defending')) dmg = Math.floor(dmg * 3 / 4);                                       // Defending (Guard) — a modest all-around guard
  if (defended && exact == null && !action.pctMaxHp) dmg = Math.floor(dmg * vulnMult(t, physical)); // charging +25% physical, asleep +50% physical, frog/chicken +50% all
  // ~6% (1/16) of physical hits crit: BMG randomizes the attack stat up to ~2x, so
  // damage ranges from normal to ~double. Adds real variance to every melee exchange. Only formulas that
  // call the game's critical routine can (the weapon-style ones: not Dash, Throw Stone, Goblin Punch).
  let crit = false;
  if (physical && !action.pctMaxHp && takes(action, MOD_CRIT) && rng() < 0.0625) { crit = true; dmg = Math.max(dmg + 1, Math.floor(dmg * (1 + rng()))); }
  if (mult > 0 && canReact && physical && t.reaction === 'Damage Split' && rng() * 100 < brave) {    // Damage Split halves the blow
    dmg = Math.max(1, Math.floor(dmg / 2)); if (log) log.push(`${t.name} Damage Split`);
  }
  if (mult < 0) { // absorbed → heals the target
    const heal = Math.max(1, dmg);
    t.hp = Math.min(t.maxHp, t.hp + heal);
    if (log) log.push(`${t.name} absorbs ${att.name} → +${heal} (${Math.max(0, t.hp)})`);
    return;
  }
  if (canReact && t.reaction === 'Mana Shield' && t.mp > 0 && rng() * 100 < brave) {
    // FFT: the HP damage is redirected to MP. If MP can't cover it, MP bottoms out at
    // 0 and the REMAINDER IS LOST — it is never dealt to HP. So a triggered Mana
    // Shield always spares HP entirely (even 1 MP blocks a lethal blow).
    const absorb = Math.min(dmg, t.mp); t.mp -= absorb; dmg = 0;
    if (log) log.push(`${t.name} Mana Shield (-${absorb} MP)`);
  }
  t.hp -= dmg;
  if (dmg > 0) { // no HP lost (e.g. fully Mana-Shielded) ⇒ no drain feed, no wake, no kill
    if (action.drain === 'hp' && att.alive) { att.hp = Math.min(att.maxHp, att.hp + dmg); if (log) log.push(`${att.name} drains ${dmg} HP`); } // Night Sword / Mutilate / Drain
    if (log) log.push(`${att.name}'s ${action.name} hits ${t.name} for ${dmg}${crit ? ' CRIT' : ''} (${dir}) → ${Math.max(0, t.hp)}`);
    if (t.hp <= 0) { kill(t, occupied, log); return; }
    // Sicken (a Wyvern's innate, or equipped): its physical attacks add Poison, bar a gun's basic
    // Attack. FFTBG's Wyvern poisoned with both Triple Attacks that landed in our logged battles.
    if (att.sicken && physical && !(action.name === 'Attack' && att.gunBasic) && !hasStatus(t, 'Poison')) { addStatus(t, 'Poison'); if (log && hasStatus(t, 'Poison')) log.push(`${t.name} is poisoned`); }
    if (hasStatus(t, 'Sleep')) { delete t.status.Sleep; if (log) log.push(`${t.name} wakes`); } // a hit cancels Sleep
    if (hasStatus(t, 'Charm')) { delete t.status.Charm; if (log) log.push(`${t.name} breaks free of Charm`); } // HP damage ends Charm
    if (hasStatus(t, 'Confusion')) { delete t.status.Confusion; if (log) log.push(`${t.name} snaps out of confusion`); } // HP damage ends Confusion
    if (hasStatus(t, 'Transparent')) { delete t.status.Transparent; if (log) log.push(`${t.name} is no longer transparent`); } // and Transparent
    // A "Chance to add X" rider (Knife Hand's Undead, Tackle's Don't Move/Don't Act, Stasis Sword's Stop...)
    // lands after the hit, so a Sleep or Confusion rider isn't cancelled by its own damage. The AI doesn't
    // count it when choosing (the game's look-ahead predicts the sure effects only: scoring the rider made
    // our decisions match FFTBG's worse, 1062 -> 1057 of 2194 turns).
    if (action.rider && t.alive && rng() < RIDER_CHANCE) {
      const pool = action.rider.statuses.filter((st) => !hasStatus(t, st) && !immuneTo(t, st));
      const st = action.rider.mode === 'one' ? pool[Math.floor(rng() * pool.length)] : null;
      for (const x of (action.rider.mode === 'one' ? (st ? [st] : []) : pool)) {
        if (x === 'Death') { t.hp = 0; kill(t, occupied, log); if (log) log.push(`${t.name} is struck down by ${action.name}`); return; }
        addStatus(t, x); if (log && hasStatus(t, x)) log.push(`${t.name} gains ${x} (${action.name})`);
      }
    }
  }

  // Damage-triggered reactions (a unit has one), Brave%-gated. The AI stays blind to
  // these (faithful — vanilla can't see reactions); they just play out in resolution.
  if (canReact && t.reaction && rng() * 100 < brave) {
    const r = t.reaction, inRange = att.alive && manh(t, att) <= (t.range || 1);
    if (r === 'Auto-Potion') { t.hp = Math.min(t.maxHp, t.hp + POTION_HEAL); if (log) log.push(`${t.name} Auto-Potion → ${t.hp}`); }
    else if ((r === 'Counter' || r === 'Counter Tackle') && physical && inRange) { if (log) log.push(`${t.name} counters ${att.name}`); dealAttack(t, BASIC(t), att, occupied, rng, log, true); }
    else if (r === 'Meatbone Slash' && physical && inRange) { if (log) log.push(`${t.name} Meatbone Slash!`); dealAttack(t, { name: 'Meatbone Slash', power: t.maxHp, magical: false }, att, occupied, rng, log, true); }
    else if (r === 'Dragon Spirit') { if (!hasStatus(t, 'Reraise') && !hasStatus(t, 'Undead')) { addStatus(t, 'Reraise', 999); if (log) log.push(`${t.name} Dragon Spirit (Reraise)`); } }
    else if (r === 'Regenerator') { if (!hasStatus(t, 'Regen')) { addStatus(t, 'Regen'); if (log) log.push(`${t.name} Regenerator`); } }
    else if (r === 'HP Restore') { if (critical(t)) { t.hp = t.maxHp; if (log) log.push(`${t.name} HP Restore → full`); } }
    else if (r === 'MP Restore') { if (critical(t)) { t.mp = t.maxMp ?? t.mp; } }
    else if (r === 'Speed Save') { t.speed = Math.min(50, (t.speed || 1) + 1); }
    else if (r === 'Brave Up') { t.brave = Math.min(100, (t.brave || 50) + 3); }
    else if (r === 'Faith Up') { t.faith = Math.min(100, (t.faith ?? 50) + 3); }
    else if (r === 'PA Save' || r === 'MA Save') { t.power = Math.round((t.power || 1) * 1.04); } // power is the baked basic; ~+1 PA
  }

  // Dual Wield: the off-hand follows up with its own independent strike (its own
  // hit/evade roll + reactions). Not on reaction-attacks (a counter swings once).
  if (!isReaction && action.power2 > 0 && t.alive) {
    dealAttack(att, { ...action, power: action.power2, element: action.element2 || null, power2: 0, strike: 2 }, t, occupied, rng, log, false);
  }
}

// ---- Battlefield: a flat NxN box, or a real map (size + walls/void + height). ----
const flatField = (n) => ({ w: n, h: n, walkable: (x, y) => x >= 0 && y >= 0 && x < n && y < n, height: () => 0 });
// Build a `field` from config.terrain (mapTerrain.maps[n]) — walkable iff the tile
// has a height (null = void) and isn't a wall surface; or fall back to a flat box.
// With `config.tiles` (the map's raw tile table, fftPath.unpackTiles of src/data/mapTiles.json), the
// field is the game's own: heights and walkability from its tiles, and movement by its pathfinder
// (see reachableTiles). Only the lower layer: the sim has no second layer yet.
// The tiles must be the terrain's map: a number FFTBG has reused for another map (the old #125
// Checkerboard Stairs) keeps the terrain's own geometry and the old walking. Checked once per pair.
const tilesFit = new WeakMap();
function tilesMatch(t, mt) {
  if (!t) return true;
  if (t.w !== mt.w || t.h !== mt.h || !t.heights) return false;
  let memo = tilesFit.get(t);
  if (!memo) tilesFit.set(t, (memo = new WeakMap()));
  if (!memo.has(mt)) {
    let a = 0, c = 0;
    for (let y = 0; y < t.h; y++) for (let x = 0; x < t.w; x++) {
      const th = t.heights[y][x]; if (th == null) continue;
      const o = (y * t.w + x) * 8; c++; if (mt.tiles[o + 2] + (mt.tiles[o + 3] & 0x1f) / 2 === th) a++;
    }
    memo.set(mt, c > 0 && a / c >= 0.9);
  }
  return memo.get(mt);
}
export function makeField(config) {
  const t = config.terrain, mt = config.tiles;
  if (mt && mt.tiles && tilesMatch(t, mt)) {
    const { w, h, tiles } = mt, inB = (x, y) => x >= 0 && y >= 0 && x < w && y < h, at = (x, y) => (y * w + x) * 8;
    return {
      w, h, tiles: mt, waterCost: mt.waterCost ?? 1,
      walkable: (x, y) => inB(x, y) && !(tiles[at(x, y) + 6] & 3),
      // the height the game targets by (0x801792EC, in half steps 2·height + slope + 2·depth): a unit in water counts
      // at the water's surface
      height: (x, y) => (inB(x, y) ? tiles[at(x, y) + 2] + (tiles[at(x, y) + 3] & 0x1f) / 2 + (tiles[at(x, y) + 3] >> 5) : 0),
      surface: (x, y) => (inB(x, y) ? tiles[at(x, y)] & 0x3f : null), // the game's surface type
      depth: (x, y) => (inB(x, y) ? tiles[at(x, y) + 3] >> 5 : 0),       // water depth
    };
  }
  if (t && t.heights && t.w && t.h) {
    const { w, h, heights, surfaces } = t;
    const inB = (x, y) => x >= 0 && y >= 0 && x < w && y < h;
    return {
      w, h,
      walkable: (x, y) => inB(x, y) && heights[y][x] != null && !(surfaces && surfaces[y] && surfaces[y][x] === 'x'),
      height: (x, y) => (inB(x, y) && heights[y][x] != null ? heights[y][x] : 0),
    };
  }
  return flatField(config.gridSize ?? 10);
}
// Movement helpers accept either a `field` object or a plain gridSize number (→ flat box).
const asField = (g) => (typeof g === 'number' ? flatField(g) : g);

// Terrain path-distance from (tx,ty): a BFS over the field (walls/void + the
// unit's Jump climb limit) that IGNORES units — a static routing field, so a
// mover heads the SHORT way *around* obstacles. Manhattan distance can't do this:
// escaping a walled pocket means first stepping away from the target (Manhattan
// goes up), so a greedy Manhattan picker deadlocks and the unit never moves.
// Returns a Map "x,y"→steps; tiles the unit can't route to are simply absent.
// On an open/flat field every tile's value equals its Manhattan distance, so
// movement is unchanged there.
function pathDistanceField(tx, ty, gridOrField, jump, fly) {
  const f = asField(gridOrField);
  const j = jump ?? 99;
  const inBounds = (x, y) => x >= 0 && y >= 0 && x < f.w && y < f.h;
  const dist = new Map([[`${tx},${ty}`, 0]]);
  let frontier = [[tx, ty]];
  let d = 0;
  while (frontier.length) {
    d++;
    const next = [];
    for (const [cx, cy] of frontier) {
      const ch = f.height(cx, cy);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = cx + dx, ny = cy + dy, k = `${nx},${ny}`;
        if (dist.has(k)) continue;
        const walk = f.walkable(nx, ny);
        if (!walk && !(fly && inBounds(nx, ny))) continue;   // a flier routes over in-bounds void
        if (walk && Math.abs(f.height(nx, ny) - ch) > j) continue;
        dist.set(k, d); next.push([nx, ny]);
      }
    }
    frontier = next;
  }
  return dist;
}

// Move toward `target` as far as this turn allows: among the tiles the unit can
// stand on this turn, walk to the one that's nearest the target along the real
// terrain route (not as the crow flies), so it rounds walls/void instead of
// stalling against them. Manhattan distance is the tiebreak (and the fallback
// when terrain can't route to the target for this unit's Jump).
function moveToward(u, target, range, occupied, field, units = null) {
  const tiles = reachableTiles(u, occupied, field, units);
  const dist = pathDistanceField(target.x, target.y, field, u.jump ?? 99, !!u.canFly);
  const man = (n) => Math.abs(n.x - target.x) + Math.abs(n.y - target.y);
  let bestKey = null, bestPath = Infinity, bestMan = Infinity;
  for (const node of tiles.values()) {
    const k = `${node.x},${node.y}`;
    const pd = dist.has(k) ? dist.get(k) : Infinity;
    const m = man(node);
    if (pd < bestPath || (pd === bestPath && m < bestMan)) { bestPath = pd; bestMan = m; bestKey = k; }
  }
  // Terrain couldn't route to the target (disconnected for this Jump) → greedy
  // Manhattan so the unit at least edges closer rather than freezing.
  if (bestPath === Infinity) {
    bestMan = Infinity;
    for (const node of tiles.values()) { const m = man(node); if (m < bestMan) { bestMan = m; bestKey = `${node.x},${node.y}`; } }
  }
  if (bestKey && bestKey !== `${u.x},${u.y}`) moveTo(u, bestKey, tiles, occupied);
}

// All tiles the unit can stand on this turn — BFS out to `move`, with parent
// pointers so movement follows a real (obstacle-respecting) path. Honors walls/void
// and the unit's Jump (a step needs |Δheight| ≤ jump). Includes the unit's own tile.
// A `canFly` unit (Fly/Teleport innate) glides OVER in-bounds void gaps — it can't
// stand on a void tile, but it can traverse one to land on the real tile beyond
// (each gap tile still costs a step), so flat-out chasms don't wall it off.
// With `units`, an ally's tile can be walked THROUGH but not stopped on, as in FFT (the game's own
// pathfinder, read off the AI's reachable-panel mask at +2676, reached past teammates where ours stopped
// dead); an enemy's tile still blocks.
export function reachableTiles(u, occupied, gridOrField, units = null) {
  const f = asField(gridOrField);
  if (f.tiles && units) return gameReach(u, units, f);
  const allyAt = new Set(units ? units.filter((o) => o !== u && o.alive && o.team === u.team).map((o) => `${o.x},${o.y}`) : []);
  const jump = u.jump ?? 99; // flat field → no climb limit
  const fly = !!u.canFly;
  const inBounds = (x, y) => x >= 0 && y >= 0 && x < f.w && y < f.h;
  const start = `${u.x},${u.y}`;
  const tiles = new Map([[start, { x: u.x, y: u.y, parent: null, z: f.height(u.x, u.y) }]]);
  const visited = new Set([start]); // covers void tiles a flier passes through (never standable)
  // frontier entries: [x, y, wparent] — wparent = nearest WALKABLE ancestor key, so
  // a tile reached across a gap chains its move path back through real tiles only.
  let frontier = [[u.x, u.y, null]];
  for (let step = 0; step < (u.move || 0); step++) {
    const next = [];
    for (const [cx, cy, wp] of frontier) {
      const ch = f.height(cx, cy);
      const parentForChild = tiles.has(`${cx},${cy}`) ? `${cx},${cy}` : wp; // collapse void and allies' tiles out of the path
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = cx + dx, ny = cy + dy, k = `${nx},${ny}`;
        if (visited.has(k)) continue;
        const walk = f.walkable(nx, ny);
        const overVoid = fly && !walk && inBounds(nx, ny); // a flier glides over an in-bounds void tile
        if (!walk && !overVoid) continue;                   // wall / off-map / (non-flier) void blocks
        if (walk && Math.abs(f.height(nx, ny) - ch) > jump) continue; // can't scale past Jump
        const through = walk && occupied.has(k) && allyAt.has(k); // an ally: pass through, don't stop
        if (walk && occupied.has(k) && !through) continue;          // anyone else (a foe, a body) blocks
        visited.add(k);
        next.push([nx, ny, walk && !through ? k : parentForChild]); // void tiles and allies' tiles forward their standable ancestor
        if (walk && !through) tiles.set(k, { x: nx, y: ny, parent: parentForChild, z: f.height(nx, ny) }); // only real, free tiles are standable; z = tile height for combat math
      }
    }
    frontier = next;
  }
  return tiles;
}

// On a real map with its tile table: the panels FFT's own pathfinder lets the unit end its move on
// (fftPath.js, exact against FFTBG's routine). Allies are walked through, foes block, bodies and
// mid-Jump units can be passed but not stopped on, crystals are free. The mover is described in the
// game's own terms: Move and the raw Jump stat (`jumpStat`; `jump` is 99 for fliers, for the BFS
// above), the movement-ability bits (`moveBits`, simPrep) and the statuses the pathfinder reads.
const statusByte = (o) => (hasStatus(o, 'Float') ? 0x40 : 0) | (hasStatus(o, 'Chicken') ? 0x04 : 0) | (frogged(o) ? 0x02 : 0) | (critical(o) ? 0x01 : 0);
// A turn asks this several times on the same board (planning, then moving), so answers are kept per
// battle, keyed on everything the pathfinder reads; nothing downstream changes the returned map.
function gameReach(u, units, f) {
  const mb = u.moveBits || {};
  let key = `${units.indexOf(u)}:${u.x},${u.y},${u.move},${u.jumpStat ?? u.jump},${statusByte(u)},${mb.f93 | 0},${mb.f94 | 0},${mb.f95 | 0}`;
  for (const o of units) {
    if (o === u) continue;
    key += !o.alive ? (o.dead != null ? `|b${o.x},${o.y}` : '|-') : airborne(o) ? `|b${o.x},${o.y}` : `|${o.team === u.team ? 'a' : 'f'}${o.x},${o.y},${statusByte(o) & 0x46}`;
  }
  const cache = f.reachCache || (f.reachCache = new Map());
  let hit = cache.get(key);
  if (!hit) { if (cache.size > 64) cache.clear(); hit = gameReachRaw(u, units, f); cache.set(key, hit); }
  return hit;
}
function gameReachRaw(u, units, f) {
  const { w, h, tiles } = f.tiles;
  const others = [], bodies = [];
  for (const o of units) {
    if (o === u) continue;
    if (!o.alive) { if (o.dead != null) bodies.push({ x: o.x, y: o.y }); continue; } // a crystal is no obstacle
    if (airborne(o)) { bodies.push({ x: o.x, y: o.y }); continue; }
    // the height of its feet and head, in half steps (the pathfinder's init, as tools/emu-battle/pathcase.mjs reads it)
    const t = (o.y * w + o.x) * 8, dep = tiles[t + 3] >> 5, float = hasStatus(o, 'Float'), f95 = (o.moveBits && o.moveBits.f95) || 0;
    let base = 2 * tiles[t + 2] + (tiles[t + 3] & 0x1f);
    if (dep) base += float ? 2 * dep + 2 : (f95 & 0x80) ? 2 * dep : (f95 & 0x40) ? 2 * dep - 2 : 0;
    else if (float) base += 2;
    others.push({ x: o.x, y: o.y, layer: 0, ally: o.team === u.team, base, top: base + ((frogged(o) || hasStatus(o, 'Chicken')) ? 4 : 6) });
  }
  const mover = { x: u.x, y: u.y, layer: 0, move: u.move || 0, jump: u.jumpStat ?? u.jump ?? 3, ...(u.moveBits || {}), st5a: statusByte(u) };
  const { reach, targets } = reachTargets(f.tiles, mover, others, { bodies, waterCost: f.waterCost });
  // A path for moveTo (facing and height on the way): from each panel step back to the neighbour with the
  // most move left (a leap's take-off when no neighbour has more), keeping only the panels it may stop on.
  const rem = reach.remain, leap = reach.params.leap, start = `${u.x},${u.y}`;
  const back = (x, y) => {
    const r0 = rem[y * w + x];
    for (let d = 1; d <= 1 + leap; d++) {
      let best = null, br = r0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx * d, ny = y + dy * d;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        if (rem[ny * w + nx] > br) { br = rem[ny * w + nx]; best = [nx, ny]; }
      }
      if (best) return best;
    }
    return null;
  };
  const tilesOut = new Map([[start, { x: u.x, y: u.y, parent: null, z: f.height(u.x, u.y) }]]);
  for (const p of targets) {
    if (p >= 256) continue;
    const x = p % w, y = (p / w) | 0, k = `${x},${y}`;
    if (k === start) continue;
    let parent = start;
    for (let c = back(x, y); c && !(c[0] === u.x && c[1] === u.y); c = back(c[0], c[1])) {
      if (targets.has(c[1] * w + c[0])) { parent = `${c[0]},${c[1]}`; break; }
    }
    tilesOut.set(k, { x, y, parent, z: f.height(x, y) });
  }
  return tilesOut;
}

// Walk the unit to a reachable destination (a key from reachableTiles), updating
// occupancy and facing along the reconstructed path.
function moveTo(u, destKey, tiles, occupied) {
  const path = [];
  for (let k = destKey; k && k !== `${u.x},${u.y}`; k = tiles.get(k).parent) path.push(tiles.get(k));
  if (path.length && turnFlags && turnFlags.unit === u) turnFlags.moved = true;
  for (let i = path.length - 1; i >= 0; i--) {
    const node = path[i];
    occupied.delete(`${u.x},${u.y}`);
    u.facing = { x: Math.sign(node.x - u.x), y: Math.sign(node.y - u.y) };
    u.x = node.x; u.y = node.y; u.z = node.z ?? u.z; // keep height current for combat math
    occupied.add(`${u.x},${u.y}`);
  }
}

// A Rend at a slot the target has nothing in is a basic Attack instead ("If none is equipped, basic Attack
// instead"): valued, ranked by its hit and resolved as one (FFTBG listed such Rends at the Attack's hit, 83-100%,
// where the Rend's own would be 40-60%, monsters included, who wear nothing).
// A monster's locked skill can be used only from a panel beside a living allied Beastmaster (simPrep `locked`).
const lockedOk = (u, action, n, units) => !action.locked || units.some((o) => o !== u && o.alive && o.beastmaster && o.team === u.team && Math.abs(o.x - n.x) + Math.abs(o.y - n.y) === 1);
const slotEmpty = (t, slot) => (t.removed && t.removed.has(slot)) || (t.worn ? !t.worn[slot] : false);
const rendsAttack = (action, t) => action.kind === 'strip' && !!action.fallbackAttack && slotEmpty(t, action.slot);
const BASIC = (u) => ({ name: 'Attack', kind: 'damage', range: u.range, power: u.power, mp: 0, charge: 0, aoe: 0, magical: false, target: 'enemy', element: u.element || null, power2: u.power2 || 0, element2: u.element2 || null, projectile: u.projectile || false, ...(u.direct ? { direct: true } : {}), ...(u.weapon ? { weapon: u.weapon } : {}), ...(gunIn(u) ? { evade: 'none' } : {}), ...(u.weapon && u.weapon.main && LUNGING.has(u.weapon.main.type) ? { lunge: true } : {}) });
// A spear strikes only along a straight line (FFT's "lunging" weapons), so a range-2 spear can't reach a foe one
// step diagonally away: FFTBG's Knight with a Partisan attacked, and charged, from straight south of its target
// where ours stood on the diagonal. Skills that use the weapon's range lunge with it too (simPrep flags them).
// FFTBG's Poles and cloths are lunging too (range 2, and the lunge bit 0x40 in byte 1 of the weapon table at
// 0x80063AB8, as its spears have): its Critical Time Mage with an Octagon Rod listed no Attack at a foe on the
// diagonal, where ours had one that killed.
const lineOk = (action, a, b) => !action.lunge || a.x === b.x || a.y === b.y;

// ---- Line of fire (docs/fftbg-ai-guide.md, "Line of fire"). A "direct" attack needs a clear straight shot
// through the terrain and the other units. The game ray-casts it in world units (a panel 28 wide, a height step
// 12, y growing down): from 2/3 up the shooter's 36-unit body to 2/3 up the target's, sampled every 2 units, and
// a sample inside a panel's solid block or another unit's body stops it. Exact against FFTBG's routine
// (0x801B06D0, the stand search's check; 0x801B0818 when the action is carried out), run in a MIPS interpreter
// on 128 captured checks and 39k random placements (tools/emu-battle lofcheck.mjs).
// Which actions: the weapon's data flags Gun, Crossbow, Harp and Book "direct" (bows arc instead); simPrep tags
// the direct abilities (Throw Stone, the Souls and breaths...) `lof: 'aim'`, skills that take the weapon's range
// `lof: 'weapon'`; a Ninja Throw is always direct.
const DIRECT_WEAPONS = new Set(['Gun', 'Crossbow', 'Harp', 'Book']);
const directWeapon = (w) => !!(w && DIRECT_WEAPONS.has(w.type));
// Checked by the stand search (the AI's flag 0x400000): an Attack with a direct weapon in either hand, a direct
// ability, a Throw. Skills that take the weapon's range are NOT checked there.
const aimsDirect = (u, a) => (a.name === 'Attack' ? !!u.weapon && (directWeapon(u.weapon.main) || directWeapon(u.weapon.off)) : a.lof === 'aim' || !!a.thrown);
// Ray-cast when carried out (0x8017AC90): a direct ability or a Throw always; an Attack or a weapon-range skill
// by the weapon in the right hand, else the left. A Frog (and a monster, who has none) strikes bare-handed.
const shootsDirect = (u, a) => (a.lof === 'aim' || a.thrown ? true : (a.name === 'Attack' || a.lof === 'weapon') && !frogged(u) && !!u.weapon && directWeapon(u.weapon.main || u.weapon.off));
// libgte's square root (0x8001C268, 12-bit fixed point): its table is floor(sqrt(m / 64) * 4096), m = 64..255.
const SQRT12 = Array.from({ length: 192 }, (_, i) => Math.floor(Math.sqrt((i + 64) / 64) * 4096));
function gteSqrt(a) {
  a |= 0;
  const n = a < 0 ? Math.clz32(~a) : Math.clz32(a);
  if (a === 0 || n === 32) return 0;
  const e = n & ~1, sh = (19 - e) >> 1, v = SQRT12[(e >= 24 ? a << (e - 24) : a >> (24 - e)) - 64];
  return sh >= 0 ? v << sh : v >> -sh;
}
// Where a unit stands, in world units up: its panel's height with the slope (12·height + 6·slope). In water the
// feet are on the bottom, unless it floats or walks on water (the surface: + 12·depth) or swims (a step under it:
// + 12·(depth − 1)); Float's sprite hovers 12 above that. The layer is the one the unit REALLY stands on: the
// game's stand search moves only its x and y, so a candidate panel's height is read off the unit's own layer.
function standHeight(T, o, x, y) {
  const t = ((o.layer || 0) * 256 + y * T.w + x) * 8, d = T.tiles[t + 3] >> 5, f95 = (o.moveBits && o.moveBits.f95) | 0, fl = hasStatus(o, 'Float');
  let g = 12 * T.tiles[t + 2] + 6 * (T.tiles[t + 3] & 0x1f);
  if (d) g += fl || (f95 & 0x88) ? 12 * d : f95 & 0x40 ? 12 * (d - 1) : 0; // Float/Levitate 0x08, Waterwalking 0x80, Swim 0x40
  return g + (o.hover ?? (fl ? (o.cls === 'Revenant' ? 13 : 12) : 0)); // (a floating Revenant's sprite hovers 13)
}
// A panel's solid block: from 12·(height − thickness) up to 12·height, the slope above it see-through. Lower layer:
// thickness 0 means from the ground; upper layer: 0 means 1. The game doesn't test the "no panel" flag, so leftover
// upper-layer heights on panels marked absent (maps 6, 43, 120) block too. A point level with the top is blocked.
const solidAt = (tiles, t, A, upper) => { const h = tiles[t + 2], th = (tiles[t + 5] & 0x1f) || (upper ? 1 : h); return A <= 12 * h && A > 12 * (h - th); };
// The shot from `from` (the panel `u` would fire from) at `target`, a unit or a bare panel {x, y}. Returns
// { clear: true }, or { clear: false, unit } for the first unit in the way, or { clear: false } for terrain. Every
// other unit can be in the way but a corpse, a crystal and a unit mid-Jump. Without the game's tiles: clear.
export function lineOfFire(field, u, from, target, units) {
  const T = field && field.tiles;
  if (!T || !T.tiles) return { clear: true };
  const { w, h, tiles } = T, at = (x, y, l) => (x >= 0 && y >= 0 && x < w && y < h ? (l * 256 + y * w + x) * 8 : -1);
  const isUnit = target.alive !== undefined;
  const O = [from.x * 28 + 14, -standHeight(T, u, from.x, from.y) - 24, from.y * 28 + 14];
  // a unit is aimed at 2/3 of its body (a body-less one at 12); a bare panel at its block's top
  const ty = isUnit ? -standHeight(T, target, target.x, target.y) - (target.alive ? 24 : 12) : -12 * tiles[at(target.x, target.y, 0) + 2];
  const v = [target.x * 28 + 14 - O[0], ty - O[1], target.y * 28 + 14 - O[2]];
  const len = gteSqrt((v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) << 12), steps = Math.trunc(len / 0x2000), q = len >> 4;
  const bodies = units.filter((o) => o !== u && o !== target && o.alive && !airborne(o)).map((o) => ({ o, x: o.x * 28 + 14, y: -standHeight(T, o, o.x, o.y), z: o.y * 28 + 14 }));
  for (let k = 1; k <= steps; k++) {
    const f = Math.trunc((k * 0x200000) / q);
    const px = (Math.imul(f, v[0]) >> 12) + O[0], py = (Math.imul(f, v[1]) >> 12) + O[1], pz = (Math.imul(f, v[2]) >> 12) + O[2];
    const lo = at(Math.trunc(px / 28), Math.trunc(pz / 28), 0), up = at(Math.trunc(px / 28), Math.trunc(pz / 28), 1);
    if ((lo >= 0 && solidAt(tiles, lo, -py, false)) || (up >= 0 && solidAt(tiles, up, -py, true))) return { clear: false };
    for (const b of bodies) if (py < b.y && b.y - 36 < py && Math.abs(px - b.x) < 8 && Math.abs(pz - b.z) < 8) return { clear: false, unit: b.o };
  }
  return { clear: true };
}
// The stand search's check for a panel: a direct action must have a clear shot from it at the unit on the panel
// it's aimed at (an empty panel isn't checked).
function lofOk(field, u, action, node, c, units) {
  if (!field || !field.tiles || !aimsDirect(u, action)) return true;
  const t = c.alive !== undefined ? c : units.find((o) => o.alive && o.x === c.x && o.y === c.y);
  return !t || t === u || lineOfFire(field, u, node, t, units).clear;
}
// ---- Bow arcs (docs/fftbg-ai-guide.md, "Bows"). A bow's shot arcs: the game solves for the two launch slopes
// that bring an arrow from 2/3 up the shooter's body down onto the target (mid-body, else 3/4 up, else its feet;
// no solution, no shot), then flies each arc in equal horizontal steps (the steep one first) and tests every
// point as line of fire does, terrain then the other units. Either arc getting through clears the shot. Exact
// against FFTBG's routine (0x801AFD90; 0x801AFF18 when the shot is fired), run in a MIPS interpreter on 56k
// random placements and every captured check (tools/emu-battle arccheck.mjs). The fixed-point steps and libgte's
// trig are the game's own: rcos's table is the rounded sine, RotMatrix's is the same with its second and fourth
// quarter turns mirrored one entry off, and ratan2's table rounds 189 of its entries up by one.
const ARC_V = 688128, ARC_G = 1338; // the launch constants (0x801B8878 / 0x801B8874): an arc peaks at most 84 up
const SIN_Q = Array.from({ length: 2049 }, (_, i) => Math.round(Math.sin((i * 2 * Math.PI) / 4096) * 4096));
const ATAN_T = Array.from({ length: 1025 }, (_, i) => Math.round((Math.atan(i / 1024) * 4096) / (2 * Math.PI)));
for (const i of [2, 7, 10, 13, 18, 21, 24, 29, 32, 35, 40, 43, 46, 51, 54, 62, 65, 70, 73, 76, 81, 84, 92, 95, 100, 103, 111, 114, 119, 122, 127, 130, 135, 138, 143, 146, 151, 154, 159, 162, 167, 172, 175, 180, 183, 188, 193, 196, 201, 206, 211, 214, 219, 224, 229, 234, 239, 242, 244, 247, 252, 257, 259, 262, 264, 269, 274, 279, 284, 289, 291, 296, 301, 306, 308, 313, 318, 320, 325, 332, 339, 346, 353, 360, 367, 369, 376, 383, 385, 392, 394, 401, 403, 412, 414, 423, 425, 434, 436, 438, 447, 449, 451, 464, 466, 468, 483, 485, 487, 489, 491, 520, 522, 524, 526, 528, 530, 532, 534, 536, 538, 540, 542, 544, 546, 548, 550, 579, 581, 583, 585, 587, 602, 604, 606, 619, 621, 632, 634, 645, 647, 656, 665, 667, 674, 676, 683, 685, 692, 699, 706, 708, 715, 720, 722, 727, 734, 741, 746, 758, 763, 768, 780, 785, 790, 795, 808, 813, 818, 826, 831, 839, 844, 852, 860, 868, 871, 879, 890, 901, 904, 915, 918, 935, 938, 941, 1013, 1016, 1019]) ATAN_T[i]++;
const idiv = (a, b) => (b === 0 ? 0 : Math.trunc(a / b));
const rsin = (a) => { const n = a < 0, b = (n ? -a : a) & 0xfff, v = b <= 2048 ? SIN_Q[b] : -SIN_Q[b - 2048]; return n ? -v : v; };
const rcos = (a) => rsin(((a < 0 ? -a : a) & 0xfff) + 1024);
const rotSinCos = (a) => { const S = (i) => (i < 1024 ? SIN_Q[i] : i < 2048 ? SIN_Q[2047 - i] : i < 3072 ? -SIN_Q[i - 2048] : -SIN_Q[4095 - i]); const i = (a < 0 ? -a : a) & 0xfff; return [a < 0 ? -S(i) : S(i), S((i + 1024) & 4095)]; };
function ratan2(y, x) {
  let sx = false, sy = false;
  if (x < 0) { sx = true; x = -x; }
  if (y < 0) { sy = true; y = -y; }
  if (x === 0 && y === 0) return 0;
  let v = y < x ? ATAN_T[(y & 0x7fe00000) ? idiv(y, x >> 10) : idiv(y << 10, x)] : 1024 - ATAN_T[(x & 0x7fe00000) ? idiv(x, y >> 10) : idiv(x << 10, y)];
  if (sx) v = 2048 - v;
  return sy ? -v : v;
}
// the arc's height (x4096) at horizontal distance s for launch slope t (0x801AF59C)
function arcHeight(t, s) {
  const big = ((s + 0xfffff) >>> 0) > 0x1ffffe, small = ((t + 32767) >>> 0) <= 0xfffe, tt = small ? t : t >> 4;
  const a3 = small ? Math.imul(t, t) >> 12 : Math.imul(tt, tt) >> 4, v = big ? s >> 7 : s >> 5;
  const v1 = big ? Math.imul(v, v) << 2 : Math.imul(v, v) >> 2;
  const a0 = small ? (big ? Math.imul(t, v) >> 5 : Math.imul(t, v) >> 7) : (big ? Math.imul(tt, v) >> 1 : Math.imul(tt, v) >> 3);
  return a0 - idiv(Math.imul((a3 + 4096) >> 6, v1 >> 6), (ARC_V << 1) >> 12);
}
// the two launch slopes (0x801AF3DC): dy = the target's feet relative to the launch point (y down), floorTop the
// target panel's 12·(height + depth); null when no arc reaches
function arcSlopes(len, dy, floorTop) {
  const l6 = len >> 6, reach = (ARC_V >> 1) - idiv(Math.imul(l6, l6), ARC_V >> 11), top = (floorTop / 4) << 14;
  if (len === 0) return null;
  let aim = null;
  for (const v of [18, 27]) { const a0 = (v + dy) << 12; if (!(reach < a0) && !(top < a0)) { aim = v; break; } } // half, then 3/4 of a 36 body
  if (aim == null) { const a0 = dy << 12; if (reach < a0 || top < a0) return null; aim = 0; }
  const a0 = (aim + dy) << 12, r = gteSqrt(Math.imul((ARC_V >> 6) - (a0 >> 5), ARC_V >> 6) - Math.imul(l6, l6));
  return [idiv((r + ARC_V) << 8, len >> 4), idiv((ARC_V - r) << 8, len >> 4)];
}
// fly one arc (0x801AFB38): null when it gets through, else what stopped it ({ unit } or { terrain })
function flyArc(T, at, O, yaw, t, len, bodies) {
  const step = Math.imul(rcos(ratan2(t, 0x1000)), gteSqrt(Math.imul(ARC_V, ARC_G) >> 12) >> 8) >> 4;
  const n = step === 0 ? 0 : idiv(len + step - 1, step), [sy, cy] = rotSinCos(yaw);
  for (let i = 1, s = step; i <= n; i++, s += step) {
    const d = s >> 12, px = O[0] + (Math.imul(cy, d) >> 12), py = O[1] + ((-arcHeight(t, s)) >> 12), pz = O[2] + (Math.imul(-sy, d) >> 12);
    const lo = at(Math.trunc(px / 28), Math.trunc(pz / 28), 0), up = at(Math.trunc(px / 28), Math.trunc(pz / 28), 1);
    if ((lo >= 0 && solidAt(T.tiles, lo, -py, false)) || (up >= 0 && solidAt(T.tiles, up, -py, true))) return { terrain: true };
    for (const b of bodies) if (py < b.y && b.y - 36 < py && Math.abs(px - b.x) < 8 && Math.abs(pz - b.z) < 8) return { unit: b.o };
  }
  return null;
}
// An arrow from `from` (the panel `u` would shoot from) at the unit `target`: { clear: true }, or { clear: false }
// with the unit that takes it instead when it's fired (the steep arc's blocker if a foe of the shooter, else the
// flat arc's if a foe, else nobody when the flat arc hit terrain, else the flat arc's ally only if the steep arc
// hit an ally too). Without the game's tiles: clear.
export function bowArc(field, u, from, target, units) {
  const T = field && field.tiles;
  if (!T || !T.tiles) return { clear: true };
  const { w, h } = T, at = (x, y, l) => (x >= 0 && y >= 0 && x < w && y < h ? (l * 256 + y * w + x) * 8 : -1);
  const O = [from.x * 28 + 14, -standHeight(T, u, from.x, from.y) - 24, from.y * 28 + 14];
  const tx = target.x * 28 + 14, tyFeet = -standHeight(T, target, target.x, target.y), tz = target.y * 28 + 14;
  const dx = tx - O[0], dz = tz - O[2], len = gteSqrt((Math.imul(dx, dx) + Math.imul(dz, dz)) << 12), yaw = ratan2(-dz, dx);
  const tt = at(target.x, target.y, target.layer || 0);
  const slopes = arcSlopes(len, O[1] - tyFeet, tt >= 0 ? 12 * (T.tiles[tt + 2] + (T.tiles[tt + 3] >> 5)) : 0);
  const bodies = units.filter((o) => o !== u && o !== target && o.alive && !airborne(o)).map((o) => ({ o, x: o.x * 28 + 14, y: -standHeight(T, o, o.x, o.y), z: o.y * 28 + 14 }));
  const fired = slopes || [0x1000, 0x1000]; // (fired with no solution, the game shoots at 45 degrees)
  const a = flyArc(T, at, O, yaw, fired[0], len, bodies); if (!a && slopes) return { clear: true };
  const b = flyArc(T, at, O, yaw, fired[1], len, bodies); if (!b && slopes) return { clear: true };
  if (!slopes && (!a || !b)) return { clear: false, unit: target }; // (the stand search rules it out; fired anyway, it lands)
  const foe = (r) => r && r.unit && r.unit.team !== u.team;
  return { clear: false, unit: foe(a) ? a.unit : foe(b) ? b.unit : b.terrain ? null : a.unit ? b.unit : null };
}
// Which shots arc: a bow's Attack (checked by the stand search too) and a Charge with a bow (only when fired).
const bowIn = (u) => !!(u.weapon && ((u.weapon.main && u.weapon.main.type === 'Bow') || (!u.weapon.main && u.weapon.off && u.weapon.off.type === 'Bow')));
const aimsArc = (u, a) => a.name === 'Attack' && bowIn(u) && !frogged(u);
const shootsArc = (u, a) => (a.name === 'Attack' || /^Charge\+/.test(a.name)) && bowIn(u) && !frogged(u);
function arcOk(field, u, action, node, c, units) {
  if (!field || !field.tiles || !aimsArc(u, action)) return true;
  const t = c.alive !== undefined ? c : units.find((o) => o.alive && o.x === c.x && o.y === c.y);
  return !t || t === u || bowArc(field, u, node, t, units).clear;
}
// A gun's shot can't be evaded (FFTBG listed every Chemist and Equip Gun Bard Attack at 100%, against targets
// whose evade cut a bow or sword to 57-95%).
const gunIn = (u) => !!(u.weapon && u.weapon.main && u.weapon.main.type === 'Gun');
const nearest = (u, list) => list.reduce((b, e) => (manh(u, e) < manh(u, b) ? e : b), list[0]);

// Rough "how dangerous is this unit" — best attack power scaled by frequency.
function threat(u) {
  const dmgs = (u.actions || []).filter((a) => a.kind === 'damage').map((a) => a.power || 0);
  return Math.max((u.power || 0) + (u.power2 || 0), ...dmgs, 0) * (Math.max(1, u.speed) / 8); // power2 = Dual Wield
}
// ---- Height in combat (FFT vertical mechanics). Each unit carries `z` = the
// height of the tile it stands on. An action reaches a target only within its
// vertical tolerance (`vRange`); projectiles (bows/guns) arc over height freely
// and gain horizontal range firing DOWNHILL. They do NOT lose horizontal range
// firing UPHILL — a bow arcs up and keeps its full reach (a past bug shrank the
// range so much firing up — −3 at a 6-height target — that an archer a couple
// tiles BELOW a foe on a structure thought it was out of range and, when a void
// blocked it from closing in, just paced the edge forever instead of shooting).
const zOf = (o) => o.z || 0;
const VRANGE = 3;                                   // physical-melee vertical tolerance (FFT ≈ 3 down / 2 up)
// What CLEARS a height gap: bows/guns (projectile) AND spells (magic/faith) are loosed
// or cast through the air, so they ignore vertical almost entirely — FFT mages bombard
// from clifftops and archers arc over terrain. Physical melee keeps the short ±VRANGE
// reach; Jump carries its own `vRange`. (A past bug lumped spells in with melee at 3,
// so a ridge caster on a tall map — Bariaus Valley spans 0–15 — couldn't hit valley
// foes only a few tiles away but several heights below, and just walked forward.)
const arcs = (a) => a.projectile || a.magical || a.faith;
const vRangeOf = (a) => (a.vReach ?? (arcs(a) ? 99 : (a.vRange ?? VRANGE)));
const withinV = (az, tz, a) => Math.abs(az - tz) <= vRangeOf(a); // can the height gap be cleared?
// The game's own height limits where the ability record has them (abilityFlags verticalOf, set by simPrep):
// `vReach` = how far up or down it can be aimed (only abilities flagged for it; spells and the rest have
// none), `vSpread` = how far up or down its area reaches from the centre (Fire 1, Fire 3 3, Demi 2 4).
// Without them (weapon strikes, hand-built specs) both fall back to the old rule above.
const vSpreadOf = (a) => a.vSpread ?? (arcs(a) ? 99 : (a.vRange ?? VRANGE));
// dz = attacker − target height; downhill (+) extends reach; uphill keeps full range (the arc carries up). A gun fires
// direct, not in an arc, so its range stays put: FFTBG's Bard with a Mythril Gun (range 7) shot from 7 panels away, not
// from the 8 that 2.5 of height would have given a bow.
const rangeBonus = (a, dz) => (a.projectile && !a.direct ? Math.max(0, Math.floor(dz / 2)) : 0);
const effRange = (a, az, tz) => (a.range ?? 0) + rangeBonus(a, az - tz);

// ---- Math Skill (Calculator). A math-cast action carries `mathCast: {attr, key}`:
// it hits EVERY unit (both teams — friendly fire) whose CT (`ct`) or standing height
// (`z`) satisfies the number. Matching the Maps Calculator tool: only positive INTEGERS
// qualify (a sloped half-tile height or a 0 never does); `prime` → prime, else multiple.
const isPrime = (n) => { if (!Number.isInteger(n) || n < 2) return false; for (let i = 2; i * i <= n; i++) if (n % i === 0) return false; return true; };
const numberMatch = (v, key) => Number.isInteger(v) && v > 0 && (key === 'prime' ? isPrime(v) : v % (+key) === 0);
// CT is CAPPED at 100 for the calculation: a unit acting is at CT ≥100 (e.g. 107) but
// counts as exactly 100, so CT×4 / CT×5 ALWAYS catch the caster while CT-Prime / CT×3
// never do. (The turn still costs the RAW value — 107−100 = 7 — which the main loop
// already subtracts; so a mime echoing the same cast a beat later re-reads the caster at
// CT 7, and a Prime cast that spared it on the original WILL clip it on the mimic.)
const mathAttrVal = (u, attr) => (attr === 'z' ? (u.z || 0) : Math.min(u.ct || 0, 100));
// Every matching unit is hit — BOTH teams, AND the caster itself (a Calc reads its own
// real CT/height at cast time, ~100+ when it acts). Self/ally hits are friendly fire the
// AI weighs against the foe damage (a cast that does 400 to them and 200 to us is still
// a win); a math heal landing on the caster/allies is, of course, a plus.
export const mathTargets = (action, units) => units.filter((o) => o.alive && !airborne(o) && !hasStatus(o, 'Petrify') && numberMatch(mathAttrVal(o, action.mathCast.attr), action.mathCast.key));

// ---- Recompute-on-change: re-derive a unit's gear/PA·MA-baked fields after a stat-mod
// changed its paBonus/maBonus or a steal/rend changed its `removed` set. Builds a FRESH
// actions array of FRESH objects (never mutates the shared prep-time array — team specs
// are reused across every run). Preserves live state (current HP/MP, CT, brave/faith/
// speed, status, position, charging); max HP/MP shrink + clamp on a gear change. No-op for
// hand-built specs without `_rebake`. (Used by Phases B/C — never fires in a normal battle,
// so the no-change path is unchanged.)
export function rebake(u) {
  if (!u._rebake) return;
  const f = u._rebake({ paBonus: u.paBonus || 0, maBonus: u.maBonus || 0, removed: u.removed });
  u.power = f.power; u.power2 = f.power2; u.element = f.element; u.element2 = f.element2; u.weapon = f.weapon;
  u.elements = f.elements; u.evade = f.evade; if (f.evadeSrc) u.evadeSrc = f.evadeSrc; if (f.worn) u.worn = f.worn; if (f.rel) u.rel = f.rel; u.range = f.range; u.projectile = f.projectile; u.direct = f.direct;
  u.move = f.move; u.jump = f.jump; if (f.jumpStat != null) u.jumpStat = f.jumpStat;
  if (!u.mimic) u.actions = f.actions; // a mime keeps its empty action list (it acts only via mimicry)
  u.maxHp = f.hp; if (u.hp > u.maxHp) u.hp = u.maxHp; // lose armor → max HP drops, current clamps
  u.maxMp = f.mp; if ((u.mp || 0) > u.maxMp) u.mp = u.maxMp;
}

// "Field-wide": a homing ability that ALSO ignores range + height, sweeping its whole
// target team regardless of geometry. Only the 0-range performing songs/dances do this
// (Wiznaibus/Witch Hunt are 0-range 255-AoE and otherwise never fire). Summons are also
// tagged `homing` (= team-scoped, no friendly fire) but they are POSITIONAL — a real
// cast range (4) and a real height band — so they are NOT field-wide. The distinction is
// just range 0: every 255-AoE team sweeper is 0-range, every summon is range 4.
const fieldWide = (a) => !!a.homing && (a.range || 0) === 0;
// Can the unit get an action within reach this turn (move + height-adjusted range,
// and within vertical tolerance)? (ignores obstacles — a coarse planning gate.)
const reachable = (u, target, action) => {
  if (fieldWide(action)) return true;        // 0-range songs/dances seek a whole team — no range/height limit
  const az = zOf(u), tz = zOf(target);
  // (no height test here: the unit moves first, and the exact check, canReach, tests the height from each panel
  // it can reach; testing it from the unit's current panel dropped FFTBG's Squire's Dash at a foe 1.5 below it)
  if ((u.move || 0) === 0 && !withinV(az, tz, action)) return false; // can't move: its own panel is the only one
  return manh(u, target) - (u.move || 0) <= effRange(action, az, tz) + (splashAim(action) ? action.aoe : 0);
};
// Area spells aim at a PANEL, not a unit: FFT lets the caster centre a blast on any tile
// in range, so a unit just outside range is still caught by the splash of a tile nearer
// in (a range-4 radius-2 summon reaches 6 tiles out). The decision replay against the real
// game showed our Summoners/Wizards/Priests walking toward a foe 7-9 tiles away when the
// game cast at it from where it stood. Limited to spells/projectiles (which clear height
// anyway), so an off-unit centre never needs its own height.
const splashAim = (a) => (a.aoe || 0) > 0 && !a.centerSelf && !a.linear && !a.mathCast && !fieldWide(a);

// ---- FFT target priority (from the disassembled Target-Priority routine, 0019d6b4).
// The real AI does NOT pick the target it can damage most — it ranks foes by a
// priority value and guns for that one. The two signature drivers we can model:
//   • Caster Hate — (curMP% × #MP-using-abilities); the AI heavily prioritizes
//     mana-rich units with many spells ("it fucking hates mages").
//   • Lowest *current* HP, absolute — a 250/250 mage outranks a 300/450 knight
//     (250 < 450), so squishy low-max-HP units get focus-fired.
// Neutralized foes (can't act / charmed onto our side) are de-prioritized so the
// AI doesn't waste swings on a unit that's already out of the fight.
const mpAbilityCount = (u) => (u.actions || []).filter((a) => (a.mp || 0) > 0 && (a.mp || 0) <= u.mp).length;
function casterHate(t) {
  if (!t.maxMp || t.mp <= 0) return 0;
  const n = mpAbilityCount(t);
  return n ? (t.mp / t.maxMp) * n : 0; // ~0–N: a full-MP 3-spell mage ≈ 3
}
// Desirability of attacking foe `t` *beyond* the raw damage of the hit. Scaled as
// a nudge on the damage value (FFT's caster-hate peaks ~20 on a 0–128 HP scale,
// so ~16%), not a doubling — enough to focus-fire mages, not ignore everything else.
function targetBias(t) {
  let b = casterHate(t) * 7;             // gun for casters
  b += Math.max(0, 150 - t.hp) * 0.08;   // prefer low absolute current HP
  if (charmed(t)) b -= 80;               // Charmed onto our side — don't attack it (it's effectively an ally now)
  return b;                              // NB: Stop/Sleep stay fair game — a helpless foe is an easy kill, not a wasted hit

}

// Who an action actually hits within its AoE. FFT has FRIENDLY FIRE: a positional
// blast (plain diamond / linear) hits EVERYONE in the area — both teams, the
// caster's own allies included. Only `homing` abilities (songs/dances) are
// team-scoped (they seek out one whole team and ignore geometry's friendly fire).
export function collectTargets(u, action, primary, units, center = primary) {
  // Self-centered blasts (Draw Out, Monk punches, monster self-AoE — `0 range`): the
  // effect radiates from the CASTER's own tile, not an aimed point, and is INDISCRIMINATE
  // — it hits everyone in the ring, friend AND foe (Draw Out distinguishes nothing, unlike
  // friend/foe-filtered Summon Magic). The caster's own tile is hit by the BENEFICIAL
  // auras (Murasame/Kiyomori/Masamune heal & buff the user too, like Chakra/Spirit of
  // Life); damaging/debuff self-blasts (Asura/Koutetsu) hit everyone in range EXCEPT the
  // caster.
  if (action.centerSelf) {
    const hitsCaster = action.sparesCaster != null ? !action.sparesCaster
      : (action.kind === 'heal' || action.kind === 'buff' || action.kind === 'revive' || action.kind === 'mpHeal' || action.kind === 'cleanse' || (action.kind === 'statmod' && action.buff));
    const out = hitsCaster ? [u] : [];
    if (!(action.aoe > 0)) return out;
    for (const o of units) {
      if (o === u || manh(o, u) > action.aoe || hasStatus(o, 'Petrify') || airborne(o) || spared(u, action, o)) continue;
      if (Math.abs(zOf(o) - zOf(u)) > vSpreadOf(action)) continue;
      if (action.kind === 'revive') { if ((!o.alive && o.team === friendTeamOf(u)) || (o.alive && action.undeadKill && isUndead(o))) out.push(o); continue; } // raises fallen allies, kills living undead
      if (o.alive) out.push(o); // positional friendly fire — both teams in the ring around the caster
    }
    return out;
  }
  // `center` is the panel the blast is aimed at: the primary unit itself, or (splashAim) a
  // tile near it, in which case only the units actually in the blast are hit.
  const onUnit = center === primary;
  // a homing dance whose aim point died still sweeps the live team (don't hit the corpse); a summon aimed at an ally
  // catches the foes around it but spares the ally itself (the game's flags, as for everyone else in the area)
  const out = !onUnit || (action.homing && !primary.alive) || (primary !== u && spared(u, action, primary)) ? [] : [primary];
  if (!(action.aoe > 0)) return out;
  for (const o of units) {
    if ((onUnit && o === primary) || manh(o, center) > action.aoe || hasStatus(o, 'Petrify') || airborne(o)) continue; // friendly fire — even the caster; stone units aren't hit; airborne jumpers are off-field
    if (spared(u, action, o)) continue; // unless the game's flags say the area can't catch it (the caster, or one side)
    if (!fieldWide(action) && Math.abs(zOf(o) - zOf(center)) > vSpreadOf(action)) continue; // vertical AoE band — a positional blast (incl. summons) spreads only within its height tolerance; only 0-range songs/dances are team-wide and ignore height
    if (action.kind === 'revive') { if ((!o.alive && o.team === friendTeamOf(u)) || (o.alive && action.undeadKill && isUndead(o))) out.push(o); continue; } // raises fallen allies, kills living undead
    if (action.homing) {
      // team-scoped: a beneficial song seeks the caster's team, a hostile dance the foes
      const friendly = action.target === 'ally' || action.target === 'self';
      if (o.alive && o.team === (friendly ? friendTeamOf(u) : foeTeamOf(u))) out.push(o);
    } else if (o.alive) {
      out.push(o); // positional AoE → friendly fire, everyone in the blast
    }
  }
  return action.performance ? out.filter((o) => !hasStatus(o, 'Sleep')) : out; // songs and dances pass over sleepers
}
// Is `t` on the side this action is "meant" to help (heal/buff a friend, hit a foe)?
const intendedTarget = (u, action, t) => {
  const friendly = action.kind === 'heal' || action.kind === 'buff' || action.kind === 'revive';
  return t.team === (friendly ? friendTeamOf(u) : foeTeamOf(u));
};

// One strike of `action` on `t` from tile `from`, split into the damage it does when it lands
// (`dmg`, before HP clamping; negative ⇒ absorbed) and the chance it lands (`p`: hit% from
// evade + direction, times any success roll). `mp` is set instead for an MP-draining hit.
// A throw's concrete options (simPrep's throwOpts): the action as each item would land.
const throwVariants = (action) => action.throwOpts.map((o) => ({ ...action, throwOpts: null, power: o.power, element: o.element, item: o.item }));
// The item a throw lands as on `t`: the best for the target (Shuriken, Bomb: the AI weighs each item), or one of
// the pair at random (weapon throws, 50/50).
function throwPick(u, action, t, rng) {
  const vs = throwVariants(action);
  if (action.throwPick === 'random') return vs[Math.floor((rng ? rng() : 0) * vs.length)];
  return vs.reduce((b, v) => (expStrike(u, v, t, u) > expStrike(u, b, t, u) ? v : b));
}
function strikeOf(u, action, t, from) {
  if (action.throwOpts) { // a pair at random counts their average; otherwise the best item for this target
    const ss = throwVariants(action).map((v) => strikeOf(u, v, t, from));
    if (action.throwPick === 'random') return { dmg: ss.reduce((a, x) => a + x.dmg, 0) / ss.length, p: ss[0].p };
    return ss.reduce((b, x) => (x.dmg * x.p > b.dmg * b.p ? x : b));
  }
  if (hasStatus(t, 'Petrify')) return { dmg: 0, p: 0 }; // stone — immune; never worth aiming at
  const hit = evadeHit(u, action, t, from);
  if (action.pctMaxHp) { // Demi/Lich/Hurricane/Mutilate — a fraction of the target's MaxHP
    const succ = action.success != null ? successOf(u, action, t) / 100 : 1;
    const el = elementMult(action.element, t);
    return { dmg: Math.max(0, Math.sign(el) * Math.ceil(t.maxHp * action.pctMaxHp / 100 * Math.abs(el))), p: hit * Math.min(1, succ) };
  }
  if (action.drain === 'mp') return { dmg: 0, p: hit, mp: action.power || 0 }; // MP drain (Dark Sword) — never HP
  if (action.casterMissingHp || action.targetMissingHp || action.targetCurrentMp) { // missing-HP / target-MP damage — value off live HP/MP
    const base = action.casterMissingHp ? (u.maxHp - u.hp) * action.casterMissingHp : action.targetCurrentMp ? (t.mp || 0) : (t.maxHp - t.hp);
    const succ = action.success != null ? successOf(u, action, t) / 100 : 1;
    return { dmg: Math.max(0, base * damageMult(u, action, t)), p: hit * Math.min(1, succ) };
  }
  const exact = formulaDamage(u, action, t);
  if (exact != null) return { dmg: exact + (action.power2 > 0 && action.weapon ? formulaDamage(u, { ...action, strike: 2, element: action.element2 }, t) || 0 : 0), p: hit };
  let dmg = action.power * damageMult(u, action, t); // zodiac/faith/element (negative ⇒ absorbed)
  if (action.power2 > 0) dmg += action.power2 * damageMult(u, { ...action, element: action.element2 }, t); // Dual Wield 2nd strike
  if (takes(action, MOD_DEFENSE)) {
    if (action.magical && (hasStatus(t, 'Shell') || t.magicDefenseUp)) dmg *= 2 / 3;
    if (!action.magical && (hasStatus(t, 'Protect') || t.defenseUp)) dmg *= 2 / 3;
    dmg *= vulnMult(t, !action.magical); // charging +25% physical, asleep +50% physical, frog/chicken +50% all
  }
  return { dmg, p: hit };
}
// Expected HP damage of ONE strike (hit-weighted).
function expStrike(u, action, t, from) {
  const s = strikeOf(u, action, t, from);
  return s.mp ? s.mp * s.p * 0.2 : s.dmg * s.p;
}

// ---- FFT's own board evaluation, read from FFTBG's BATTLE.BIN (routine 0x8019D37C; FFTBG's is
// vanilla's). After trying an action the game scores the whole board: for each unit, 128 × HP/MaxHP,
// plus a weight for every status it carries (STATUS_W, the s16 table at 0x8019F308), plus an MP
// term for casters and 51 for each piece of gear still worn; the enemy's units count negative. An
// action is worth the CHANGE it makes. So a full bar of damage is worth 128, a kill adds Dead's
// 192, landing Stop 90, Protect on an ally 25, breaking a sword 51. Every scorer below values its
// effects on that one scale through effectValue. Notes: tools/emu-battle/refs/ai_board_evaluation.md.
const STATUS_W = {
  Crystal: -192, Dead: -192, Undead: -39, Petrify: -116, Invite: -231, Darkness: -64, Confusion: -64,
  Silence: -90, 'Blood Suck': -116, Treasure: -192, Oil: -7, Float: 12, Reraise: 51, Transparent: 38,
  Berserk: -39, Chicken: -26, Frog: -52, Critical: -32, Poison: -26, Regen: 25, Protect: 25, Shell: 25,
  Haste: 19, Slow: -39, Stop: -90, Wall: 64, Faith: 6, Innocent: -7, Charm: -64, Sleep: -39,
  "Don't Move": -39, "Don't Act": -64, Reflect: 25, 'Death Sentence': -103,
};
const HP_BAR = 128, GEAR_W = 51;
// The chance a damaging skill's "Chance to add X" rider lands: FFT's 19% added-effect chance. In FFTBG's build
// 31 of 200 such hits that didn't kill added the status (15.5%), the rest of the gap being targets that
// already had it or were immune.
const RIDER_CHANCE = 0.19;
// HP a revive brings a unit back with: "Heal (N)%" of its own max HP (Raise 50, Raise 2 100, Revive 25), or
// a flat amount (Phoenix Down's Random(1,20), ~10).
const reviveHp = (action, t) => Math.max(1, Math.min(t.maxHp, action.pct ? Math.ceil(t.maxHp * action.pct / 100) : (action.power || Math.ceil(t.maxHp * 0.5))));
// Does Reflect bounce it? The game's per-ability flag when simPrep has it, else any magic.
const bounces = (action) => action.reflectable ?? !!action.magical;
// Can an area of `action` cast by `u` catch `o`? The game's per-ability flags when simPrep has them.
// "Can't hit allies" spares the caster too: FFTBG's Summoner, standing in its own Odin, Titan or Shiva, lost only the
// spell's MP in the game's trial (ours had it take the blast, -238).
const spared = (u, action, o) => (o === u && (action.sparesCaster || action.sparesAllies)) || (o !== u && ((action.sparesAllies && sideOf(u, o) > 0) || (action.sparesEnemies && sideOf(u, o) < 0)));
// Does Silence block it? The game's per-ability flag when simPrep has it, else any magic or faith skill
// (Silence doesn't stop a monster's MA-based skills, e.g. Mutilate, Eye Gouge).
const muted = (action) => action.silenceable ?? !!(action.magical || action.faith);
const HELD = ['Slow', 'Stop', 'Sleep', "Don't Move", "Don't Act"];
const TURNED = ['Confusion', 'Blood Suck', 'Charm'];
// The game scales Silence by how much the unit relies on magic, Darkness by how much it relies on evadable
// (physical) attacks, and the MP term by how much it relies on MP: per-unit AI bytes, 0-4.
const mpSpells = (t) => (t.actions || []).filter((a) => (a.mp || 0) > 0);
// (simPrep's `rel`, the game's own per-unit bytes; an older estimate from the kit when a spec lacks it)
const magicReliance = (t) => (t.rel ? t.rel.magic : (() => { const n = mpSpells(t).length; return n >= 3 ? 2 : n ? 1 : 0; })());
const physReliance = (t) => (t.rel ? t.rel.phys : (mpSpells(t).length ? 0 : 1));
const mpReliance = (t) => (t.rel ? t.rel.mp : magicReliance(t));
// Status part of a unit's board value, for the set of statuses S it carries.
function statusScore(t, S) {
  const turned = TURNED.some((s) => S.has(s)), held = HELD.some((s) => S.has(s));
  let v = 0;
  for (const s of S) {
    let w = STATUS_W[s] || 0;
    if (!w || (turned && w > 0)) continue;        // a Confused/Charmed unit's buffs don't count
    if (s === 'Darkness') w = w * physReliance(t) / 4;
    else if (s === 'Silence') w = w * magicReliance(t) / 4;
    else if (TURNED.includes(s) && held) w /= 2;  // turned, but can't act on it anyway
    else if (HELD.includes(s) && turned) continue; // already neutralised by the Charm/Confusion
    v += w;
  }
  return v;
}
// Critical: 20% HP or less, unless Cursed ("Evil: immune to Critical"; FFTBG's trial left it off a Cursed Geomancer
// that a Shake Off took to 46 of 233)
const isCrit = (t, hp) => hp > 0 && hp <= Math.max(1, t.maxHp * 0.2) && !hasStatus(t, 'Cursed');
const statusSetOf = (t) => {
  const S = new Set(Object.keys(t.status || {}).filter((s) => hasStatus(t, s)));
  if (isCrit(t, t.hp)) S.add('Critical');
  return S;
};
// Change in t's status value from adding `add` and dropping `drop` (TUNE.statusWeight scales it).
function statusDelta(t, add = [], drop = []) {
  return statusDelta0(t, add, drop) + sleepSwing(t, add, drop);
}
function statusDelta0(t, add = [], drop = []) {
  const S = statusSetOf(t), before = statusScore(t, S);
  for (const s of drop) if (!isPermanent(t, s)) S.delete(s);
  const adding = add.filter((s) => !immuneTo(t, s) && !blockedOn(t, s));
  for (const s of adding) for (const c of STATUS_CANCELS[s] || []) if (!isPermanent(t, c)) S.delete(c); // Petrify on a Stopped unit takes its Stop away
  for (const s of adding) S.add(s);
  return (statusScore(t, S) - before) * TUNE.statusWeight;
}
// A "(Random)" list in the look-ahead: the game's trial lands the whole list, one status after another from the last
// status bit to the first (0x5C's Death Sentence ... 0x58), each cancelling what it cancels. Read off its trial in the
// interpreter: a Plague's Look of Devil left Petrify, Darkness, Don't Move and Don't Act on its target (scored 235,
// where we had counted only the worst one, 116), and a Porky's Toot left Confusion, which cancelled the Sleep before it.
const STATUS_BIT_ORDER = ['Crystal', 'Dead', 'Undead', 'Charging', 'Jump', 'Defending', 'Performing',
  'Petrify', 'Invite', 'Darkness', 'Confusion', 'Silence', 'Blood Suck', 'Cursed', 'Treasure',
  'Oil', 'Float', 'Reraise', 'Transparent', 'Berserk', 'Chicken', 'Frog', 'Critical',
  'Poison', 'Regen', 'Protect', 'Shell', 'Haste', 'Slow', 'Stop', 'Wall',
  'Faith', 'Innocent', 'Charm', 'Sleep', "Don't Move", "Don't Act", 'Reflect', 'Death Sentence'];
const bitRank = (st) => { const i = STATUS_BIT_ORDER.indexOf(st); return i < 0 ? -1 : i; };
function statusDeltaSeq(t, list) {
  if (list.includes('Death') && !immuneTo(t, 'Death')) return killDelta(t);
  const S = statusSetOf(t), before = statusScore(t, S), had = S.has('Sleep');
  for (const st of [...list].sort((a, b) => bitRank(b) - bitRank(a))) {
    if (immuneTo(t, st) || (STATUS_BLOCKED_BY[st] || []).some((b) => (b === 'Undead' ? isUndead(t) : S.has(b)))) continue;
    for (const c of STATUS_CANCELS[st] || []) if (!isPermanent(t, c)) S.delete(c);
    S.add(st);
  }
  const now = S.has('Sleep');
  return (statusScore(t, S) - before) * TUNE.statusWeight + sleepSwing(t, now && !had ? ['Sleep'] : [], had && !now ? ['Sleep'] : []);
}
// Change in t's value when its HP moves to newHp (still alive): the HP term plus Critical.
const hpDelta0 = (t, newHp) => HP_BAR * (newHp - t.hp) / t.maxHp + ((isCrit(t, newHp) ? 1 : 0) - (isCrit(t, t.hp) ? 1 : 0)) * STATUS_W.Critical;
// Killing t: its HP term and statuses go, Dead's weight comes in.
const killDelta0 = (t) => STATUS_W.Dead - (HP_BAR * t.hp / t.maxHp + statusScore(t, statusSetOf(t)));
// The game scores each option on the board after its look-ahead, which plays out the pending charges and dances
// (0x8019D558): an option is worth what it changes THERE. While the pick list is scored, pendingHp holds the damage
// those will deal each unit (pendingDamage), and the deltas below measure from it. FFTBG's Cursed Geomancer at 11 HP,
// with a Wiznaibus in progress, ranked Chakra on itself first: healed it lives through the dance, else it dies.
let pendingHp = null, pendingPerf = null;
// Songs and dances pass over sleepers, so while the list is scored, waking a unit also gives it the pending ones
// that reach its side (pendingPerf: what they're worth to it), and putting one to sleep spares it them. FFTBG's Monk
// threw a stone at its sleeping Bard and Earth Slashed its sleeping Monk with a Nameless Song in progress: each woken
// unit gets the song (+134 for the Monk, over the slash's own damage).
function sleepSwing(t, add, drop) {
  const w = pendingPerf && pendingPerf.get(t);
  if (!w) return 0;
  if (drop.includes('Sleep') && hasStatus(t, 'Sleep') && !isPermanent(t, 'Sleep')) return w;
  if (add.includes('Sleep') && !hasStatus(t, 'Sleep') && !immuneTo(t, 'Sleep') && !blockedOn(t, 'Sleep')) return -w;
  return 0;
}
const atHp = (t, hp) => (hp <= 0 ? killDelta0(t) : hpDelta0(t, Math.min(t.maxHp, hp))); // t at hp, from where it is now
const pendingOf = (t) => (pendingHp && pendingHp.get(t)) || 0;
const hpDelta = (t, newHp) => { const P = pendingOf(t); return P ? atHp(t, newHp - P) - atHp(t, t.hp - P) : hpDelta0(t, newHp); };
const killDelta = (t) => { const P = pendingOf(t); return P ? killDelta0(t) - atHp(t, t.hp - P) : killDelta0(t); };
// Raising a dead unit to `hp`.
const reviveDelta = (t, hp) => HP_BAR * hp / t.maxHp + (isCrit(t, hp) ? STATUS_W.Critical : 0) - STATUS_W.Dead;
// `dmg` landing on t (negative = absorbed, which heals). A hit that doesn't kill also ends the statuses
// damage ends (FFTBG's status help: Sleep, Charm, Confusion, Transparent), and the game's trial counts
// that: a Transparent Minotaur hit for 48 of 487 HP scored -51 = 12.6 for the HP + 38 for Transparent.
const ENDED_BY_DAMAGE = ['Sleep', 'Charm', 'Confusion', 'Transparent'];
const damageDelta = (t, dmg) => (dmg >= t.hp ? killDelta(t) : hpDelta(t, Math.min(t.maxHp, t.hp - dmg))
  + (dmg > 0 ? statusDelta(t, [], ENDED_BY_DAMAGE.filter((st) => hasStatus(t, st))) : 0));
// The MP term: 64 × MP/MaxMP, scaled by magic reliance and halved below a per-unit MP cost the
// game stores (6–34 in our dump; which spell it comes from is unknown, so we use the cheapest).
function mpTerm(t, mp) {
  if (!t.maxMp) return 0;
  const costs = mpSpells(t).map((a) => a.mp);
  const low = costs.length && mp < Math.min(...costs) ? 0.5 : 1;
  return 64 * mp / t.maxMp * low * mpReliance(t) / 4;
}
const mpDelta = (t, d) => mpTerm(t, Math.max(0, Math.min(t.maxMp ?? 0, (t.mp || 0) + d))) - mpTerm(t, t.mp || 0);
// +1 for a unit on the actor's side (itself included), −1 for the side it's fighting.
const sideOf = (u, t) => (t !== u && t.team === foeTeamOf(u) ? -1 : 1);
// A unit's whole value on the scoreboard (0x8019D808): its HP bar, statuses and MP term, and 51 for each of its
// 7 gear slots still as it started (not on monsters); a body counts Dead and whatever else it still carries. (The game also adds a
// small per-side counter term, left out.) The board is the sum, the actor's foes negative.
function unitWorth(t) {
  const gear = t.gender === 'Monster' ? 0 : GEAR_W * (7 - (t.removed ? t.removed.size : 0));
  if (!t.alive) { const S = statusSetOf(t); if (t.crystallized || S.has('Crystal')) { S.delete('Dead'); S.add('Crystal'); } else S.add('Dead'); return statusScore(t, S) * TUNE.statusWeight + gear + mpTerm(t, t.mp || 0); } // a body keeps its other statuses (a dead Undead with Float: -192 - 39 + 12); a crystal is Crystal, not Dead too
  return Math.floor(HP_BAR * t.hp / t.maxHp) + statusScore(t, statusSetOf(t)) * TUNE.statusWeight + gear + mpTerm(t, t.mp || 0);
}
const boardWorth = (u, units) => units.reduce((sum, o) => sum + sideOf(u, o) * unitWorth(o), 0);
export const unitWorthOf = (t) => unitWorth(t); // (diagnostics: the replay compares it with the game's)
// What the per-panel trial adds to the board wherever the unit stands: the pending charges that don't depend on
// its panel (their effect on everyone but the mover; chargeExposure has the rest) and its own Regen/Poison tick
// at the end of its turn. It cancels out between panels, but not against the own panel's leftover key.
function trialConstant(u, units, live = liveCharges(units)) {
  let v = 0;
  for (const c of units) {
    const ch = c.charging, a = ch && ch.action;
    if (c === u || !c.alive || !a || !live.has(c)) continue;
    if (ch.mpDue) v += sideOf(u, c) * mpDelta(c, -ch.mpDue); // the caster pays its MP as it lands
    if (a.centerSelf) continue;
    if (ch.target === u && !ch.panel && !ch.center) continue; // follows the mover: chargeExposure
    let caught;
    if (ch.panel || ch.center) caught = (a.aoe || 0) > 0 ? collectTargets(c, a, {}, units, ch.center || ch.panel) : units.filter((o) => o.alive && o.x === (ch.center || ch.panel).x && o.y === (ch.center || ch.panel).y);
    else if (ch.target && ch.target.alive) {
      const o = ch.target;
      if (!cantMove(o) && (ch.ctr ?? effChargeOf(c, a)) > ticksUntilTurn(o)) continue; // it walks off first
      caught = (a.aoe || 0) > 0 ? collectTargets(c, a, o, units, o) : [o];
    } else continue;
    for (const o of caught) if (o !== u && (o === ch.target || pendingFinds(c, ch, o))) v += sideOf(c, u) * landedValue(c, a, o);
  }
  if (u.status && !hasStatus(u, 'Petrify')) {
    if (u.status.Poison) v += u.hp - statusTick(u) <= 0 ? killDelta(u) : hpDelta(u, u.hp - statusTick(u));
    else if (u.status.Regen) v += hpDelta(u, Math.min(u.maxHp, u.hp + statusTick(u)));
  }
  // While charges are pending the trial plays on until the last one lands, and every turn that comes up in the
  // meantime ends with its unit's Regen or Poison tick (FFTBG's trial took 16 off a poisoned ally whose turn came
  // two ticks before a Float landed). A charge and a turn on the same tick: the charge first.
  let W = 0; for (const c of live) W = Math.max(W, c.charging.ctr ?? effChargeOf(c, c.charging.action));
  // ... and the clock runs that long, so timed statuses with fewer ticks left than that wear off (FFTBG's trial
  // took Protect and Shell off a unit 4 ticks from losing them, the Battle Song 9 away)
  if (W > 0) for (const o of units) {
    if (!o.alive || !o.status) continue;
    const gone = Object.keys(o.status).filter((k) => STATUS_TICKS[k] != null && o.status[k] < PERMANENT && o.status[k] < W && !isPermanent(o, k)); // (one with exactly W left: the charge lands first)
    if (gone.length) v += sideOf(u, o) * statusDelta(o, [], gone);
  }
  if (W > 0) for (const o of units) {
    if (o === u || !o.alive || !o.status || hasStatus(o, 'Petrify') || !(o.status.Poison || o.status.Regen)) continue;
    const T = ticksUntilTurn(o); if (T >= W) continue;
    const turns = 1 + Math.floor((W - 1 - T) / Math.max(1, Math.ceil(100 / effSpeed(o))));
    const hp = o.status.Poison ? o.hp - turns * statusTick(o) : Math.min(o.maxHp, o.hp + turns * statusTick(o));
    v += sideOf(u, o) * (hp <= 0 ? killDelta(o) : hpDelta(o, hp));
  }
  return v;
}
// Value of a stat change, priced in HP before the board scale existed (STAT_VALUE etc.):
// the game's board has no term for PA or Speed (and its CT term isn't decoded yet), so these keep
// their old prices, as a share of the bar.
const hpEquiv = (t, v) => v * HP_BAR / Math.max(1, t.maxHp);
// Would the game's AI consider adding status `st` to `t`? Its "AI Ability Use Decisions" routine
// (0x8019E5D8) keeps only statuses the target lacks, then drops some by per-target flags that
// "Set type of target for all targets" (0x80194314) sets from HP at the start of each AI turn:
// Protect/Shell/Wall/Float/Faith/Reflect only on a unit at or under 80% HP, Regen/Reraise only
// under 50%. Silence only on a unit that relies on magic, Darkness only on one that relies on
// physical attacks at the game's level 2 (our 0/1 approximation never reaches it), Frog only on an
// enemy, Haste/Slow never on a Stopped or Sleeping unit. An ability is a candidate if any of its
// statuses survives; its value is still the board change of all of them (effectValue).
const AT_80_PCT = new Set(['Protect', 'Shell', 'Wall', 'Float', 'Faith', 'Reflect']);
function aiWantsStatus(u, t, st) {
  if (hasStatus(t, st) || immuneTo(t, st)) return false;
  if (AT_80_PCT.has(st)) return 4 * t.maxHp >= 5 * t.hp;
  if (st === 'Regen' || st === 'Reraise') return t.hp < t.maxHp / 2;
  if (st === 'Silence') return magicReliance(t) > 0;
  if (st === 'Darkness') return physReliance(t) >= 2;
  if (st === 'Frog') return sideOf(u, t) < 0;
  if (st === 'Haste' || st === 'Slow') return !hasStatus(t, 'Stop') && !hasStatus(t, 'Sleep');
  return true;
}
// The statuses of `action` the AI would want on `t` (see aiWantsStatus).
const usefulStatuses = (u, t, action) => (action.statuses || [action.status]).filter((st) => aiWantsStatus(u, t, st));

// What one target's share of `action` is worth to `u`, on the board scale. `from` = the tile the
// action is fired from (for hit direction).
function effectValue(u, action, t, from) {
  const side = sideOf(u, t);
  const succ = (action.success ?? 100) / 100;
  // (A "(Separate)" list counts at the quarter rate the AI gives it, as hitOf does, so value / hit is its
  // value if it lands, as the game ranks candidates.)
  // the chance it lands is the hit the AI lists (hitOf, evasion included), so value / hit is its value if it lands:
  // a Paralyze into 20% magic evasion was ranked at 64 / 0.8 = 80 where FFTBG's trial scored its 64 (VALTRACE)
  const faithS = action.kind === 'damage' ? 0 : Math.min(1, hitOf(u, action, t, from));
  switch (action.kind) {
    case 'damage': {
      const { dmg, p, mp } = strikeOf(u, action, t, from);
      if (!p) return 0;
      if (mp) { const d = Math.min(mp, t.mp || 0); return p * (side * mpDelta(t, -d) + mpDelta(u, d)); }
      let v = side * p * damageDelta(t, dmg);
      if (u.sicken && !action.magical && dmg > 0 && dmg < t.hp) v += side * p * statusDelta(t, ['Poison']); // Sicken: the hit poisons too
      // A charged skill's "Chance to add X" counts in full: the look-ahead lands the pending charge with its
      // status. FFTBG valued Stasis Sword at the hit plus Stop (162 over its do-nothing baseline; ours had 75
      // without it) and Justice Sword at the hit plus Death Sentence (161 / 61). An instant skill's rider
      // doesn't count: Hell Ivy (Stop) and Static Shock (Don't Act) scored level with a plain Attack.
      if (action.rider && !action.magical && (action.charge || 0) > 0 && dmg > 0 && dmg < t.hp) {
        const pool = action.rider.statuses.filter((st) => !hasStatus(t, st) && !immuneTo(t, st));
        if (pool.length) v += side * p * (action.rider.mode === 'one' ? statusDeltaSeq(t, pool) : pool.includes('Death') ? killDelta(t) : statusDelta(t, pool)); // ("(Random)": the whole list, as below)
      }
      if (action.drain === 'hp' && dmg > 0 && u.alive) v += p * hpDelta(u, Math.min(u.maxHp, u.hp + Math.min(dmg, t.hp))); // Night Sword feeds the caster
      return v;
    }
    case 'heal': {
      const heal = healOf(u, action, t);
      if (isUndead(t)) return side * damageDelta(t, heal);  // a heal sears the undead
      let v = side * hpDelta(t, Math.min(t.maxHp, t.hp + heal));
      if (action.mpPct) v += side * mpDelta(t, (t.maxMp ?? 0) * action.mpPct / 100); // Elixir tops MP too
      return v;
    }
    case 'revive':
      if (t.alive) return action.undeadKill && isUndead(t) ? side * faithS * killDelta(t) : 0; // a revive destroys the undead
      return side * faithS * reviveDelta(t, reviveHp(action, t));
    case 'status': case 'buff': {
      // A buff is eligible if any of its statuses passes the AI's gate (usefulStatuses), and is then
      // worth ALL the statuses it would add, as the game's board evaluation scores the real effect:
      // Protect Spirit (Defending + Protect) on a full-HP ally passes on Defending and is worth its
      // Protect. (Valuing only the gated ones made it 0, and our Treants never cast it.)
      const list = action.kind === 'buff'
        ? (usefulStatuses(u, t, action).length ? (action.statuses || [action.status]).filter((st) => !hasStatus(t, st) && !immuneTo(t, st)) : [])
        : (action.statuses && action.statusMode ? action.statuses : [action.status]);
      let d;
      // "(Random)" lands one status in the real battle, but the game's trial lands the whole list, in status-bit
      // order (statusDeltaSeq): its Plague's Look of Devil scored 235 with four of its five statuses on the target,
      // which is why it cast that over Doom 6 times of 6.
      // "(Separate)" rolls each at hit%/N, so about one lands, but the game's trial applies the whole
      // list: its Cheer Up candidates scored 132 = Float 12 + Reraise 51 + Transparent 38 + Regen 25 +
      // Faith 6 (Defending weighs nothing). We had the average, 22.
      if (action.statusMode === 'one' && list.length) d = statusDeltaSeq(t, list);
      else if (list.includes('Death') && !immuneTo(t, 'Death')) d = killDelta(t);
      else d = list.length ? statusDelta(t, list) : 0;
      return side * faithS * d;
    }
    case 'cleanse': {
      const present = (action.statuses || []).filter((st) => hasStatus(t, st));
      return present.length ? side * faithS * statusDelta(t, [], present) : 0;
    }
    case 'mpHeal': {
      const amt = action.pct ? (t.maxMp ?? 0) * action.pct / 100 : action.power;
      return side * mpDelta(t, amt);
    }
    case 'mpDamage': {
      const amt = action.pctMaxMp ? (t.maxMp ?? 0) * action.pctMaxMp / 100 : action.power;
      return side * Math.min(1, faithS) * mpDelta(t, -amt);
    }
    case 'ctset':
      if (action.ct >= 100) return side * faithS * hpEquiv(t, 70);            // Quick: hand an ally a near-instant turn
      return -side * faithS * hpEquiv(t, 30 + threat(t) * 0.3);               // Set CT 0: stall a foe
    case 'statmod': {
      let mv = 0;
      for (const mod of (action.mods || [])) mv += Math.abs(appliedDelta(t, mod)) * (STAT_VALUE[mod.stat] || 5) * Math.sign(mod.delta);
      return side * faithS * hpEquiv(t, mv);
    }
    case 'strip': {
      if (rendsAttack(action, t)) return effectValue(u, BASIC(u), t, from);
      if (side > 0 || (t.removed && t.removed.has(action.slot))) return 0;
      if (t._rebake) { // is there anything in that slot to take? (an empty slot changes nothing)
        const a = t._rebake({ paBonus: t.paBonus || 0, maBonus: t.maBonus || 0, removed: new Set([...(t.removed || []), action.slot]) });
        if (a.power === t.power && (a.power2 || 0) === (t.power2 || 0) && a.evade.front === t.evade.front && (a.evade.magic || 0) === (t.evade.magic || 0) && a.hp === t.maxHp && a.mp === t.maxMp) return 0;
      }
      return GEAR_W * faithS; // the item's 51 if it lands, at the hit the AI lists (FFTBG's trial: exactly 51)
    }
    default: return 0;
  }
}

// Value of a "(multihit)" attack (Void Storage, Dragon Pit, the Demons, Obsidian Blade): it strikes N times in
// battle, each on a random panel of its area, but the game's look-ahead strikes each unit in the area once. Its
// per-unit trial values matched one strike's, unit for unit (VALTRACE), and a Time Mage's Nether Demon that
// would kill a Chemist in one strike scored 99 above an Attack that killed a Swine: the Chemist's 266 against the
// Swine's 165. The game weighs these spells only one turn in four or so (shareRoll), which is what keeps them rare.
// (Valued as the expected number of strikes instead, they ranked below a plain Attack.)
function multihitScore(u, action, primary, units, from) {
  const charged = (action.charge || 0) > 0 && !action.keepsMovers; // as trialSets: who acts before it lands has walked off
  let v = 0;
  for (const t of units) {
    if (!t.alive || airborne(t) || manh(t, primary) > action.aoe || Math.abs(zOf(primary) - zOf(t)) > vSpreadOf(action)) continue;
    if (charged && t !== u && !landsInTrial(u, action, t)) continue;
    v += effectValue(u, action, t, from);
    if (t === primary && sideOf(u, t) < 0) v += strikeOf(u, action, t, from).p * targetBias(t);
  }
  return v;
}

// Expected value of an action aimed at `primary` (used to pick the best move), on FFT's board
// scale (effectValue). `from` is the tile the action is evaluated from (defaults to where u
// stands); it only affects physical hit direction, so flanking can be scored per-tile.
// What a pending charge would do to the board, as `u`'s side counts it (the game's look-ahead lets every
// pending charge land after the trial action, so stopping one is worth the opposite). Traced in the MIPS
// interpreter: an Odin that killed a Geomancer mid-cast scored 1077, the kill (311) plus the four allies
// its pending spell would have Stopped and frozen (+193 each).
function chargeImpact(u, caster, units) {
  const ch = caster.charging; if (!ch || !ch.action) return 0;
  const a = ch.action;
  const at = ch.panel ? { x: ch.panel.x, y: ch.panel.y, z: ch.target ? zOf(ch.target) : 0 } : ch.center ? ch.center : ch.target ? { x: ch.target.x, y: ch.target.y, z: zOf(ch.target) } : null;
  const hit = a.centerSelf ? collectTargets(caster, a, caster, units)
    : !((a.aoe || 0) > 0) ? (ch.target && ch.target.alive && at && ch.target.x === at.x && ch.target.y === at.y ? [ch.target] : [])
      : at ? collectTargets(caster, a, {}, units, at) : [];
  let v = 0;
  for (const o of hit) v += sideOf(u, o) * sideOf(caster, o) * effectValue(caster, a, o, caster);
  return v;
}
function scoreAction(u, action, primary, units, from = u, center = primary) {
  // Single-target magic that would just bounce off the target's Reflect is wasted.
  if (bounces(action) && (action.aoe || 0) === 0 && (action.kind === 'damage' || action.kind === 'status') && hasStatus(primary, 'Reflect')) return 0;
  if (!fieldWide(action) && !withinV(zOf(from), zOf(center), action)) return 0; // can't clear the height gap from this tile to the aim point (team-wide songs/dances ignore height)
  if (!elementalOk(action, from)) return 0; // Geomancy: the caster's panel doesn't give this skill
  if (action.multihit) return multihitScore(u, action, center, units, from);
  let v = 0;
  for (const t of trialTargets(u, action, primary, units, center, from)) {
    v += effectValue(u, action, t, from);
    if (action.kind === 'damage' && t !== u && t.charging && !action.multihit) { // a kill stops its pending spell
      const s = strikeOf(u, action, t, from);
      if (s.p && s.dmg >= t.hp) v -= s.p * chargeImpact(u, t, units);
    }
    if (action.kind === 'damage' && t === primary && sideOf(u, t) < 0) v += strikeOf(u, action, t, from).p * targetBias(t); // the AI guns for casters/squishies (at the strike's chance, like the rest of the value, so value / hit stays its worth if it lands)
    if ((action.kind === 'status' || action.kind === 'buff') && t !== u && t.charging) { // Sleep, Charm, Petrify... end its pending spell
      const adds = action.statusMode === 'one' ? [] : (action.statuses || [action.status]);
      if (adds.some((st) => st && endsCharge(t, st) && !immuneTo(t, st))) v -= chargeImpact(u, t, units);
    }
  }
  if (action.selfCost) v += damageDelta(u, action.selfCost); // the HP the caster pays (Energize, Destroy...)
  // ...and the MP: the game's trial spends it, so the caster's MP term drops (Haste: +19 for the ally, −2
  // for the Geomancer casting it, 17 in all, read off FFTBG's per-unit trial values).
  if (action.mp) v += mpDelta(u, -action.mp);
  return v;
}

// Elemental (Geomancy) is the skill the GEOMANCER's own panel gives (ELEMENTAL_BY_SURFACE, the game's
// table): standing on a wooden floor it's Will-O-Wisp, on grass Hell Ivy, whatever the target stands on.
// All 32 Elemental casts in our FFTBG battle logs match the caster's panel (13 the target's). So each
// stand tile allows one Elemental skill. The field of the turn being decided is kept here so the scorer
// can read surfaces; with no tile data (a flat or birb-terrain field) every one stays usable, as before.
const ELEMENTAL = new Set(ELEMENTAL_BY_SURFACE.filter(Boolean));
let activeField = null, activeCrystals = null; // the crystals on the board this turn (the Critical AI's retreat looks for them)
export const setActiveField = (f) => { activeField = f && typeof f === 'object' ? f : null; if (!activeField) activeCrystals = null; };
function elementalOk(action, from) {
  if (!ELEMENTAL.has(action.name) || !activeField || !activeField.surface || !from) return true;
  return ELEMENTAL_BY_SURFACE[activeField.surface(from.x, from.y)] === action.name;
}

// Who the AI's trial of an action counts (0x8019DD8C, run on each candidate before scoring the board):
// everyone in its area, except that a CHARGED cast skips each unit that will get a turn, and can move,
// before it lands, since the AI expects it to walk out of the area (the flag the trial sets at +3608 on
// each such turn). Helpful spells and songs keep them (keepsMovers). A cast aimed at a unit
// (centre = primary, for abilities that follow their target) follows it: if that unit walks first it
// still counts, but alone, since the area moves with it. Read off FFTBG's trials: Shiva aimed at a
// Geomancer who would move hit him for 136 and spared the Knight beside him; aimed at the Knight, who
// wouldn't, it hit the Knight alone. Our scorer used to count both, every time.
// The game ALSO searches the same ability by panel (type 5), where the walker drops out and whoever
// stays counts, so a unit-aimed candidate is worth the better reading: trialSets gives both.
function trialSets(u, action, primary, units, center = primary) {
  const all = collectTargets(u, action, primary, units, center);
  if (!((action.charge || 0) > 0) || fieldWide(action) || action.jump) return [all];
  const lands = (o) => o === u || !o.alive || landsInTrial(u, action, o);
  const stay = action.keepsMovers ? all : all.filter(lands);
  if (center === primary && action.follows && primary.alive && primary !== u && !lands(primary)) return [all.filter((o) => o === primary), stay];
  return [stay];
}
const setValue = (u, action, set, from) => set.reduce((v, t) => v + effectValue(u, action, t, from), 0);
function trialTargets(u, action, primary, units, center = primary, from = u) {
  const sets = trialSets(u, action, primary, units, center);
  return sets.length === 1 ? sets[0] : sets.reduce((a, b) => (setValue(u, action, b, from) > setValue(u, action, a, from) ? b : a));
}

// Summed threat of living enemies that could strike (x,y) on their next turn —
// the positional "danger" a tile carries (Manhattan move+range, ignores blockers).
function dangerAt(x, y, enemies) {
  let d = 0;
  for (const e of enemies) {
    if (Math.abs(e.x - x) + Math.abs(e.y - y) <= (e.move || 0) + Math.max(1, e.range || 1)) d += threat(e);
  }
  return d;
}

// Would a charged single-target action land before its target gets a turn of its own? A charge hits a
// panel, and a target that acts first can walk off it. "See if ability should be used based on CT"
// (0x8019F0D8) says the target escapes when its ticks until a turn are at most the charge, unless it
// can't act or move then (0x8019F15C: Stop, Sleep, Don't Move, Petrify, Crystal). It does NOT exempt a
// target that is itself charging. Ticks until a turn = ceil((100 − CT) / Speed), as "Calculate
// Clockticks Until Unit Acts" (0x80199B98) does. chooseAction uses this as a preference, not a veto
// (see there). For area charges the game uses it to map where units will stand, which we don't model.
const effChargeOf = (u, action) => Math.max(1, Math.ceil(action.charge * (u.shortCharge ? 0.5 : 1))); // what the AI believes
// How long a charge really takes. Short Charge halves it, except for Jump: the game's jump ignores
// Short Charge, but its AI still counts on it (effChargeOf), so a Short Charge Lancer jumps at
// targets it thinks it'll reach first and often lands on an empty panel.
const landChargeOf = (u, action) => (action.jump ? Math.max(1, Math.ceil(action.charge)) : effChargeOf(u, action));
// Diagnostics (the decision replay): why an (action, target) pair is or isn't one of u's candidates —
// in coarse reach, past the single-target charge check, and its value from u's own tile.
export function explainCandidate(u, action, target, units) {
  const charged = (action.charge || 0) > 0 && !(action.aoe > 0) && target !== u && target.alive;
  return {
    reachable: reachable(u, target, action),
    chargeOk: charged ? chargeLands(u, action, target) : true,
    ticks: charged ? { charge: effChargeOf(u, action), targetTurnIn: ticksUntilTurn(target), targetCt: target.ct } : null,
    value: scoreAction(u, action, target, units),
  };
}
// For the emulator harness (tools/emu-battle): per unit an action would hit from `from`, our damage, hit
// chance and value on the game's board scale, to diff against the game's own trial of the same candidate.
export function evalDetail(u, action, primary, units, from = u, center = primary) {
  if (!elementalOk(action, from)) return [];
  // a line attack fires a ray from the stand panel toward the aim point (lineUnits), not an area around it
  const dirTo = (c) => (Math.abs(c.x - from.x) >= Math.abs(c.y - from.y) ? { x: Math.sign(c.x - from.x), y: 0 } : { x: 0, y: Math.sign(c.y - from.y) });
  const hit = action.linear ? lineUnits(from.x, from.y, dirTo(center), action.aoe, units) : trialTargets(u, action, primary, units, center, from);
  const rows = hit.map((t) => {
    const s = action.kind === 'damage' && !action.multihit ? strikeOf(u, action, t, from) : null;
    const cancel = s && t !== u && t.charging && s.dmg >= t.hp ? -s.p * chargeImpact(u, t, units) : 0;
    return { t, dmg: s ? s.dmg : null, p: s ? s.p : hitOf(u, action, t, from), value: effectValue(u, action, t, from) + cancel };
  });
  if (action.mp) rows.push({ t: u, dmg: null, p: 1, value: mpDelta(u, -action.mp), mpCost: true }); // the caster's MP spent
  return rows;
}
export const basicAttack = (u) => BASIC(u);
// Exported for tests: how long the AI thinks a charge takes, and how long it really does.
export const chargeTicks = (u, action) => ({ believed: effChargeOf(u, action), real: landChargeOf(u, action) });
const ticksUntilTurn = (t) => (t.ct >= 100 ? 0 : Math.ceil((100 - t.ct) / effSpeed(t)));
const chargeLands = (u, action, t) => cantMove(t) || effChargeOf(u, action) < ticksUntilTurn(t);
// Who the trial still finds in a charged area when it lands: a unit whose turn falls on the landing tick is
// still there, since a clocktick resolves charges before turns (FFTBG's trial caught a Paralyze target's
// neighbour 5 ticks from its turn under a 5-tick Paralyze). The stricter chargeLands above is the game's
// rule for whether the TARGET of a cast will have walked off (0x8019F0D8).
const landsInTrial = (u, action, t) => cantMove(t) || effChargeOf(u, action) <= ticksUntilTurn(t);
// A pending charge in the per-panel trial: does it still find `o` when it lands (its remaining ticks against o's
// next turn; songs, dances and other whole-side skills find everyone), and what it's worth there if it lands (the
// trial plays it out: FFTBG's Galaxy Stop cost each foe its full 193)?
const pendingFinds = (c, ch, o) => !!ch.action.keepsMovers || cantMove(o) || (ch.ctr ?? effChargeOf(c, ch.action)) <= ticksUntilTurn(o);
function landedValue(c, a, o) {
  const saved = pendingHp, savedP = pendingPerf; pendingHp = null; pendingPerf = null; // (the pending charges themselves, on the board as it stands)
  try {
    const v = effectValue(c, a, o, c), p = a.kind === 'damage' ? strikeOf(c, a, o, c).p : Math.min(1, hitOf(c, a, o, c));
    return p > 0 ? v / p : 0;
  } finally { pendingHp = saved; pendingPerf = savedP; }
}
// The HP each unit stands to lose to the pending charges and dances in the look-ahead (healing counts negative), as
// trialConstant finds whom each one catches. The deciding unit's own share depends on where it ends up, so for it
// only what reaches it anywhere counts: a whole-side song or dance, or a cast that follows it.
function pendingDamage(u, units) {
  const live = liveCharges(units), P = new Map();
  for (const c of live) {
    const ch = c.charging, a = ch.action;
    if (a.kind !== 'damage' && a.kind !== 'heal') continue;
    let caught;
    if (a.centerSelf) caught = collectTargets(c, a, c, units);
    else if (ch.panel || ch.center) caught = (a.aoe || 0) > 0 ? collectTargets(c, a, {}, units, ch.center || ch.panel) : units.filter((o) => o.alive && o.x === (ch.center || ch.panel).x && o.y === (ch.center || ch.panel).y);
    else if (ch.target && ch.target.alive) {
      const o = ch.target;
      if (o !== u && !cantMove(o) && (ch.ctr ?? effChargeOf(c, a)) > ticksUntilTurn(o)) continue; // it walks off first
      caught = (a.aoe || 0) > 0 ? collectTargets(c, a, o, units, o) : [o];
    } else continue;
    const anywhere = fieldWide(a) || a.homing || (ch.target === u && !ch.panel && !ch.center);
    for (const o of caught) {
      if (!o.alive || (o === u ? !anywhere : o !== ch.target && !pendingFinds(c, ch, o))) continue; // (the deciding unit has had its turn by then)
      const d = a.kind === 'heal' ? (isUndead(o) ? healOf(c, a, o) : -healOf(c, a, o)) : strikeOf(c, a, o, c).dmg;
      if (d) P.set(o, (P.get(o) || 0) + d);
    }
  }
  return P.size ? P : null;
}
// What each pending song or dance is worth to each unit of the side it reaches, asleep or not (as that unit sees it).
function pendingPerformances(units) {
  let M = null;
  for (const c of liveCharges(units)) {
    const a = c.charging.action;
    if (!a.performance) continue;
    const friendly = a.target === 'ally' || a.target === 'self';
    for (const o of units) {
      if (!o.alive || hasStatus(o, 'Petrify') || airborne(o) || o.team !== (friendly ? friendTeamOf(c) : foeTeamOf(c))) continue;
      const sts = a.statuses || (a.status ? [a.status] : []);
      const v = (a.kind === 'buff' || a.kind === 'status') && sts.length
        ? (() => { const saved = pendingPerf; pendingPerf = null; try { return statusDeltaSeq(o, sts.filter((st) => !hasStatus(o, st))); } finally { pendingPerf = saved; } })() // (worth to o; the gate is the caster's, not the trial's)
        : sideOf(c, o) * landedValue(c, a, o);
      if (v) { M = M || new Map(); M.set(o, (M.get(o) || 0) + v); }
    }
  }
  return M;
}
// The trial resolves the pending charges in the order they land (fewest ticks left first) until nobody is still
// charging (0x8019D558), so one that kills a later charge's caster, or lands a status that stops a charge, cancels
// it: FFTBG's Odin killed a Calculator mid-Galaxy Stop, and the trial never played the Galaxy Stop. The charging
// units whose charge survives (who's caught is read with everyone where they stand).
function liveCharges(units) {
  const list = units.filter((c) => c.alive && c.charging && c.charging.action).sort((p, q) => (p.charging.ctr ?? 0) - (q.charging.ctr ?? 0));
  const gone = new Set();
  for (const c of list) {
    if (gone.has(c)) continue;
    const ch = c.charging, a = ch.action;
    if (a.centerSelf) continue;
    const at = ch.panel || ch.center || ch.target;
    if (!at) continue;
    const caught = (a.aoe || 0) > 0 ? collectTargets(c, a, ch.center || ch.panel ? {} : ch.target, units, at) : units.filter((o) => o.alive && o.x === at.x && o.y === at.y);
    for (const o of caught) {
      if (o === c || !o.charging || gone.has(o) || (o.charging.ctr ?? 0) < (ch.ctr ?? 0)) continue;
      if (a.kind === 'damage' && strikeOf(c, a, o, c).dmg >= o.hp) gone.add(o);
      else if (a.kind === 'status' && [].concat(a.status || []).some((st) => endsCharge(o, st) && !immuneTo(o, st) && !blockedOn(o, st))) gone.add(o);
    }
  }
  return new Set(list.filter((c) => !gone.has(c)));
}

// ---- Linear (ray) attacks — Earth Slash & co. fire straight out from the caster.
const DIRS = [{ x: 0, y: -1 }, { x: 0, y: 1 }, { x: 1, y: 0 }, { x: -1, y: 0 }];

// Living units struck by a ray from (fx,fy) in direction d, up to `length` tiles
// (the ray starts one tile ahead and passes through everything — friendly fire).
export function lineUnits(fx, fy, d, length, units) {
  const out = [];
  for (let k = 1; k <= length; k++) {
    const x = fx + d.x * k, y = fy + d.y * k;
    for (const o of units) if (o.alive && !airborne(o) && o.x === x && o.y === y) out.push(o);
  }
  return out;
}

// Signed value of a linear attack fired from (fx,fy) in direction d: every unit on the ray,
// friend or foe, on the board scale.
function lineScore(u, action, fx, fy, d, units) {
  let v = 0, first = true;
  for (const t of lineUnits(fx, fy, d, action.aoe, units)) {
    v += effectValue(u, action, t, { x: fx, y: fy });
    // on the same scale as the other entries in the pick list (scoreAction): the first foe the line meets is
    // its target (FFTBG listed a Minotaur's Blow Fire and Shake Off at one Critical Mime level, -480 each)
    if (first && action.kind === 'damage' && sideOf(u, t) < 0) { v += strikeOf(u, action, t, { x: fx, y: fy }).p * targetBias(t); first = false; }
  }
  if (action.mp) v += mpDelta(u, -action.mp);
  return v;
}

// Best direction (and its value) for a linear attack fired from (fx,fy).
function bestLine(u, action, fx, fy, units) {
  let best = { d: DIRS[0], value: -Infinity };
  for (const d of DIRS) { const value = lineScore(u, action, fx, fy, d, units); if (value > best.value) best = { d, value }; }
  return best;
}

// Best (stand tile, direction) to fire a linear attack: over reachable tiles ×
// 4 directions, maximize line value − dangerWeight·danger. canMove=false pins the
// caster to its current tile (Don't Move). Returns { action, key, dir, value } or null.
export function bestLinearPlan(u, units, occupied, gridSize, config, canMove, onlyAction = null) {
  const linears = (onlyAction ? [onlyAction] : (u.actions || [])).filter((a) => a.linear && (a.mp || 0) <= u.mp);
  if (!linears.length) return null;
  const tiles = canMove ? reachableTiles(u, occupied, gridSize, units) : new Map([[`${u.x},${u.y}`, { x: u.x, y: u.y, parent: null }]]);
  const enemies = units.filter((e) => e.alive && !airborne(e) && e.team !== u.team);
  const dw = config.dangerWeight ?? 0.2;
  let best = null;
  for (const action of linears) {
    for (const node of tiles.values()) {
      if (!lockedOk(u, action, node, units)) continue; // a monster's starred skill: only beside an allied Beastmaster
      const bl = bestLine(u, action, node.x, node.y, units);
      const net = bl.value - dw * hpEquiv(u, dangerAt(node.x, node.y, enemies));
      if (!best || net > best.net) best = { action, key: `${node.x},${node.y}`, z: node.z ?? zOf(u), dir: bl.d, value: bl.value, net };
    }
  }
  return best;
}

// ---- Self-centered blasts (Draw Out, Monk punches, monster self-AoE) fire from the
// caster's OWN tile, so the AI picks the stand tile whose surrounding ring engulfs the
// most foes (and spares its own allies + itself), rather than aiming at one foe.

// Value of detonating self-centered `action` while standing at (cx,cy,cz): everyone in the
// ring, on the board scale. The caster counts only for beneficial auras (Chakra/Murasame/Kiyomori & co.).
// Who a self-centred blast set off at (cx,cy,cz) catches. The caster stands at the centre.
function selfBlastUnits(u, action, cx, cy, cz, units) {
  const from = { x: cx, y: cy };
  // A friendly blast includes the caster: heals, buffs and self pumps (Accumulate, Gather Power are
  // 0-range stat buffs on yourself, and scored 0 while the caster was skipped).
  const incCaster = action.sparesCaster != null ? !action.sparesCaster
    : (action.kind === 'heal' || action.kind === 'buff' || action.kind === 'mpHeal'
      || (action.kind === 'statmod' && action.buff) || (action.kind === 'cleanse' && !action.dispel));
  const out = [];
  for (const t of units) {
    if (!t.alive || airborne(t)) continue;
    if (t === u) { if (incCaster && !hasStatus(t, 'Petrify')) out.push(t); continue; } // caster spared by its own damage/debuff self-blast
    if (spared(u, action, t)) continue;
    if (manh(from, t) > (action.aoe || 0)) continue;
    if (Math.abs(cz - zOf(t)) > vSpreadOf(action)) continue;
    if (hasStatus(t, 'Petrify')) continue;
    out.push(t);
  }
  return out;
}
function selfBlastValue(u, action, cx, cy, cz, units) {
  const from = { x: cx, y: cy };
  return selfBlastUnits(u, action, cx, cy, cz, units).reduce((v, t) => v + effectValue(u, action, t, from), 0);
}

// Best stand tile to detonate a self-centered blast, over reachable tiles ×
// each self-centered action. Mirrors bestLinearPlan: maximize value − dangerWeight·
// danger. canMove=false pins the caster (Don't Move). Returns { action, key, value,
// net } or null. Exported for tests.
export function bestCenterSelfPlan(u, units, occupied, gridSize, config, canMove, onlyAction = null) {
  const silenced = hasStatus(u, 'Silence');
  const selfs = (onlyAction ? [onlyAction] : (u.actions || [])).filter((a) => a.centerSelf && a.kind !== 'statmod' && (a.mp || 0) <= u.mp && !(silenced && muted(a))); // stat pumps go through chooseAction (worth nothing, last resorts)
  if (!selfs.length) return null;
  const tiles = canMove ? reachableTiles(u, occupied, gridSize, units) : new Map([[`${u.x},${u.y}`, { x: u.x, y: u.y, parent: null, z: zOf(u) }]]);
  const enemies = units.filter((e) => e.alive && !airborne(e) && e.team !== u.team);
  const dw = config.dangerWeight ?? 0.2;
  let best = null;
  // A heal is a candidate only when it reaches someone the AI would heal, as in chooseAction: an ally
  // under half HP (not undead) or an undead foe. Our Treants used to pulse Spirit of Life over scratches
  // (12 casts where FFTBG's cast it 4, and 4 Protect Spirits where FFTBG's cast 8).
  const healWanted = (action, x, y, z) => units.some((o) => o.alive && !(o === u && action.sparesCaster) && manh(o, { x, y }) <= (action.aoe || 0) && Math.abs(zOf(o) - z) <= vSpreadOf(action)
    && (sideOf(u, o) > 0 ? o.hp < o.maxHp * 0.5 && !isUndead(o) : isUndead(o)));
  for (const action of selfs) {
    for (const node of tiles.values()) {
      if (action.kind === 'heal' && !healWanted(action, node.x, node.y, node.z || 0)) continue;
      if (!lockedOk(u, action, node, units)) continue; // a monster's starred skill (a Malboro's Bad Breath): only beside an allied Beastmaster
      const value = selfBlastValue(u, action, node.x, node.y, node.z || 0, units);
      if (value <= 0) continue;
      const net = value - dw * hpEquiv(u, dangerAt(node.x, node.y, enemies));
      if (!best || net > best.net) best = { action, key: `${node.x},${node.y}`, z: node.z || 0, value, net };
    }
  }
  return best;
}

// ---- Math Skill planning. A math-cast is free, instant and board-wide, so there's no
// tile/range to optimize — just value the units it would catch right now: foe hits +,
// friendly fire (allies AND the caster itself, if its own CT/height matches) −.
function mathScore(u, action, units) {
  // Math is a deliberate trade across BOTH teams: every unit whose CT/height matches is hit, the
  // caster's own side included, each valued on the board scale (400 to them for 200 to us is a win).
  let v = 0;
  for (const t of mathTargets(action, units)) v += effectValue(u, action, t, u);
  return v;
}

// Best math-cast the Calculator could fire this turn (highest net value), or null.
// Exported for tests.
export function bestMathPlan(u, units) {
  const silenced = hasStatus(u, 'Silence');
  const casts = (u.actions || []).filter((a) => a.mathCast && !(silenced && muted(a)));
  let best = null;
  for (const action of casts) {
    const value = mathScore(u, action, units);
    if (value > 0 && (!best || value > best.value)) best = { action, value };
  }
  return best;
}

// Where to act from this turn, as {key, attack}. Among reachable tiles, take the
// in-range one maximizing (action value − dangerWeight·danger): this flanks to
// low-evade tiles AND, for ranged units, attacks from the safest spot that still
// hits (kiting). If even the best attack tile is worse than fleeing, hold the
// attack and retreat to the safest reachable tile. key === null → target out of
// reach this turn (the caller advances).
// FFT's danger map (AI +372, filled by "Check for good units to target", 0x8019971C): for a panel, the sum of
// 10 - distance over every enemy within 10 tiles (as the crow flies) that gets its turn no later than the
// mover's next one and can act then: not Chicken, Stop, Charm, Sleep, Don't Act (0x801998C8), or stone.
// Turn timing is "Calculate Clockticks Until Unit Acts" (0x80199B98), ceil((100 - CT) / Speed), with the
// mover counted from the CT it will have after this turn. Checked against the game's own table on 40 searches.
const DANGER_OFF = ['Chicken', 'Stop', 'Charm', 'Sleep', "Don't Act", 'Petrify'];
function gameDangerFoes(u, units) {
  const ct = u.ct ?? 100, after = ct >= 100 ? ct - 100 : ct; // the sim takes its 100 off after the turn, the game before
  const limit = Math.ceil((100 - after) / effSpeed(u));
  return units.filter((e) => e.alive && e.team !== u.team && !airborne(e) && !DANGER_OFF.some((st) => hasStatus(e, st)) && ticksUntilTurn(e) <= limit);
}
export const standDanger = (u, units, x, y) => gameDanger(x, y, gameDangerFoes(u, units)); // for the decision replay's diagnostics
function gameDanger(x, y, foes) {
  let v = 0;
  for (const e of foes) { const d = Math.abs(e.x - x) + Math.abs(e.y - y); if (d < 11) v += 10 - d; }
  return v;
}

// FFT's "value of standing there" (AI +1524, filled by "AI Target Priority Super Routine" 0x801999C8): the
// board after every pending charge lands, with the unit moved onto the panel. Traced in the MIPS
// interpreter: on each candidate panel the game resolves the charging units' spells (their Charging bit
// clears, their targets lose HP). A blast aimed at a panel changes only the mover's own term, when it stands
// in it. One aimed at the mover itself follows it there, out of the caster's range or not (the game's keys
// stayed level over panels in and out of reach), and catches whoever stands around that panel: FFTBG's
// Critical Monk, with a Paralyze (vertical 0) coming for it, scored the panel level with the caster's
// teammate 64 better, since the Paralyze would freeze that teammate too. One aimed at another unit that acts
// before it lands counts for nothing: its target will have moved.
// Both the stand search and the move decision sort panels by this first, then by danger. Returns the change
// in value at (x, y), as the mover's side sees it.
function chargeExposure(u, units, x, y, z, live = liveCharges(units)) {
  let v = 0;
  for (const c of units) {
    const ch = c.charging;
    if (c === u || !ch || !c.alive || !ch.action || !live.has(c)) continue;
    const a = ch.action;
    if (ch.target === u && !ch.panel && !ch.center && !a.centerSelf) {
      const to = { x, y, z };
      const ox = u.x, oy = u.y, oz = u.z;
      u.x = x; u.y = y; u.z = z;
      try {
        const caught = (a.aoe || 0) > 0 ? collectTargets(c, a, u, units, to) : [u];
        for (const o of caught) if (o === u || pendingFinds(c, ch, o)) v += sideOf(c, u) * landedValue(c, a, o);
      } finally { u.x = ox; u.y = oy; u.z = oz; }
      continue;
    }
    // aimed at another unit that gets a turn before it lands (a tie goes to the charge): that unit walks off, and
    // the trial drops it
    if (ch.target && ch.target !== u && ch.target.alive && !ch.panel && !ch.center && !a.centerSelf && !cantMove(ch.target) && (ch.ctr ?? effChargeOf(c, a)) > ticksUntilTurn(ch.target)) continue;
    const at = ch.panel ? { x: ch.panel.x, y: ch.panel.y, z: ch.target ? zOf(ch.target) : 0 } : ch.center ? ch.center : ch.target ? { x: ch.target.x, y: ch.target.y, z: zOf(ch.target) } : null;
    if (!at && !a.centerSelf) continue;
    const ox = u.x, oy = u.y, oz = u.z;
    u.x = x; u.y = y; u.z = z;
    let hit;
    if (a.centerSelf) hit = collectTargets(c, a, c, units).includes(u);
    else if (!((a.aoe || 0) > 0)) hit = x === at.x && y === at.y;     // a single-target charge strikes its panel
    else hit = collectTargets(c, a, {}, units, at).includes(u);         // an area: whoever stands in it
    if (hit) v += sideOf(c, u) * landedValue(c, a, u);
    u.x = ox; u.y = oy; u.z = oz;
  }
  return v;
}
// The value of ending the turn on a panel as the game's per-panel trial sees it ("AI Target Priority Super
// Routine", 0x801999C8, which moves the unit there, ends its turn and keeps minus the board's value at AI +1524):
// what the pending charges do to it there, and Poison from a Poisoned marsh.
function standValue(u, units, f, x, y, z, charges) {
  // (With nothing charging the trial stops after its first event, and a Regen or Poison tick comes before the
  // marsh: FFTBG's Geomancer with Regen scored a marsh panel like any other.)
  const pending = charges instanceof Set ? charges.size > 0 : !!charges;
  const marsh = onPoisonMarsh(u, f, x, y) && (pending || !(hasStatus(u, 'Regen') || hasStatus(u, 'Poison')));
  return (charges ? chargeExposure(u, units, x, y, z, charges instanceof Set ? charges : undefined) : 0) + (marsh ? statusDelta(u, ['Poison']) : 0);
}
const anyCharging = (u, units) => units.some((c) => c !== u && c.alive && c.charging && c.charging.action);

// ---- Moving without acting, and moving after acting: FFTBG's "Map movement decision" (0x8019638C) and
// "AI Post Action Movement" (0x8019693C), replayed in a MIPS interpreter on 100 captured decisions (exact,
// with the routine's own tables) before porting.
// The distance it works with is the walk from each panel to the target: "See if able to move to target"
// (0x801994F8) floods out from the target with Move 124 and the mover's own movement, the step rules run
// backwards (fftPath reverse) and no unit in the way; a Teleporter uses straight-line distance instead.
// The walking-distance field depends only on the map, the target's panel and how the mover moves, so it's
// cached on the field (the move decision and the stand search both want it every turn).
function distToTarget(u, target, units, f) {
  if (u.moveBits && (u.moveBits.f94 & 0x0c)) return (x, y) => Math.abs(x - target.x) + Math.abs(y - target.y);
  const key = `${target.x},${target.y},${u.jumpStat ?? u.jump ?? 3},${u.canFly ? 1 : 0},${u.moveBits ? Object.values(u.moveBits).join('.') : ''},${f.tiles ? statusByte(u) : ''}`;
  const cache = f._distCache || (f._distCache = new Map());
  let fn = cache.get(key);
  if (!fn) { if (cache.size > 256) cache.clear(); fn = distField(u, target, f); cache.set(key, fn); }
  return fn;
}
function distField(u, target, f) {
  if (f.tiles) {
    const mover = { x: target.x, y: target.y, layer: 0, move: 124, jump: u.jumpStat ?? u.jump ?? 3, ...(u.moveBits || {}), st5a: statusByte(u) };
    const r = fftReach(f.tiles, mover, [], { reverse: true, waterCost: f.waterCost });
    const top = r.remain[target.y * f.w + target.x];
    return (x, y) => { const v = r.remain[y * f.w + x]; return v ? top - v : 255; };
  }
  const bfs = pathDistanceField(target.x, target.y, f, u.jump ?? 99, !!u.canFly);
  return (x, y) => bfs.get(`${x},${y}`) ?? 255;
}
// "Get Longest Range Ability or Move" (0x801962A0), despite its name, is Move plus the SHORTEST reach (range +
// area) among the unit's usable abilities aimed the main target's way: "Set AI ability considerations"
// (0x80195600) keeps the minimum over those aimed at foes (AI byte 0 & 0x02) in the unit's AI record +14, and
// over those aimed at allies in +15. Usually the basic Attack wins, so R is Move + 1: FFTBG's casters with
// range-5 spells read R = Move + 1 where ours took the longest spell and stepped back to safety instead of
// closing in (the game's stored R on 55 captured move decisions; ours had 9-11 where it had 4-6).
function longestReach(u, target) {
  const toAlly = target && sideOf(u, target) > 0;
  const usable = [...(u.noBasic ? [] : [BASIC(u)]), ...(u.actions || [])].filter((a) => (a.mp || 0) <= (u.mp ?? Infinity));
  const pool = usable.filter((a) => (toAlly ? a.target === 'ally' || a.target === 'self' : a.target !== 'ally' && a.target !== 'self'));
  if (!pool.length) return toAlly ? (u.move || 0) : 255;
  return (u.move || 0) + Math.min(...pool.map((a) => (a.range || 0) + (a.aoe || 0)));
}
// The unit the AI walks toward when it moves ("main target"): FFTBG sets it at the start of each AI turn
// (0x8019471C) with "Calculate Physical Target" in mode 4 (0x80199124), the nearest foe as the crow flies
// (ties to the lower unit) among those still worth chasing: alive, or a body with Reraise, and not petrified,
// crystallized, Blood Sucked or under Death Sentence unless it's charging (the per-unit AI flag +6192 & 0x80, which
// the AI's turn setup sets for any charging foe and otherwise only for one that isn't doomed). Its move decision measures distance to this unit, whatever the option it
// picked was aimed at. (FFTBG's Squire walked toward the foe a panel farther off when the nearer one had Death
// Sentence. Its version of the routine skips the low-HP branch, and a Transparent foe still counts.)
export function mainTargetOf(u, units) { return mainTarget(u, units); }
// A foe a pending charge will petrify or kill is done for too: the turn setup runs the pending charges in a trial
// (0x8019451C) and only units still standing keep the flag. FFTBG's Time Mage walked toward a foe 7 panels off
// rather than the one 2 away that its teammate's Petrify was coming for.
function finishedByCharge(o, units) {
  for (const c of units) {
    const ch = c.charging, a = ch && ch.action;
    if (c === o || !c.alive || !a) continue;
    const at = ch.panel || ch.center || (ch.target && ch.target !== o ? ch.target : null);
    const hit = ch.target === o && !ch.panel && !ch.center ? true : at ? (a.aoe > 0 ? manh(o, at) <= a.aoe : o.x === at.x && o.y === at.y) : false;
    if (!hit) continue;
    const sts = [].concat(a.status || []).filter((st) => (st === 'Petrify' || st === 'Death') && !immuneTo(o, st) && !blockedOn(o, st));
    if (a.kind === 'status' && sts.length) return true;
    if (a.kind === 'damage') { const { dmg } = strikeOf(c, a, o, c); if (dmg >= o.hp) return true; }
  }
  return false;
}
function mainTarget(u, units) {
  const foe = foeTeamOf(u);
  let best = null, bd = Infinity;
  for (const o of units) {
    if (o === u || o.team !== foe || o.crystallized) continue;
    if (!(o.alive || (o.dead != null && hasStatus(o, 'Reraise')))) continue;
    if (hasStatus(o, 'Petrify') || hasStatus(o, 'Blood Suck') || (hasStatus(o, 'Death Sentence') && !o.charging && !hasStatus(o, 'Charging'))) continue; // a doomed foe still counts while it charges (unit +349 set)
    if (finishedByCharge(o, units)) continue;
    const d = manh(u, o);
    if (d < bd) { bd = d; best = o; }
  }
  return best;
}
// ── The facing the AI picks at the end of its turn (BATTLE.BIN 0x8019ABB4) ──
// The game's facings 0-3 as grid steps (0 = toward y − 1); FACE in simulateBattle uses the same order.
const FACE_DIRS = [{ x: 0, y: -1 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 0 }];
// Which way `to` lies from `from` (0x801978E4): the longer axis; on an exact diagonal the y axis when
// dx = dy and the x axis when dx = −dy. 4 = the same panel.
export function directionTo(from, to) {
  const dx = to.x - from.x, dy = to.y - from.y, a = dy - dx, s = dx + dy;
  if (a > 0) return s > 0 ? 2 : 1;
  if (a === 0) return s > 0 ? 2 : s < 0 ? 0 : 4;
  return s >= 0 ? 3 : 0;
}
// Clockticks until a unit's next turn (0x80199B98): what CT still needs at its Speed, plus Stop's time
// left; a sleeper waits out its Sleep. Never, for Speed 0, a crystal, Petrify, or a body without Reraise.
function ticksToAct(u) {
  if (!(u.speed > 0) || u.crystallized || hasStatus(u, 'Petrify')) return Infinity;
  if (!u.alive && !hasStatus(u, 'Reraise')) return Infinity;
  let n = (u.status && u.status.Stop) || 0;
  const ct = u.ct ?? 0;
  if (ct < 100) n += Math.ceil((100 - ct) / u.speed);
  if (u.alive && u.status && u.status.Sleep && n < u.status.Sleep) n = u.status.Sleep;
  return n;
}
// Whether a status will still be on in `ticks` clockticks (0x8019EF24): the untimed ones (Chicken) and
// innate ones always; a timed one if its time left reaches that far.
const TIMED = new Set(['Stop', 'Sleep', 'Charm', "Don't Act"]);
function statusLasts(u, name, ticks) {
  if (!hasStatus(u, name)) return false;
  if (!TIMED.has(name) || isPermanent(u, name)) return true;
  const left = u.status && u.status[name];
  return left == null || left >= PERMANENT || left >= ticks;
}
// A foe that can act against the unit before its own next turn (0x801998C8, inverted): it gets a turn
// within `horizon` ticks, and no Stop, Don't Act, Sleep, Chicken or Charm holds it until then.
function threatens(o, horizon) {
  if (ticksToAct(o) > horizon) return false;
  return !['Stop', "Don't Act", 'Sleep', 'Chicken', 'Charm'].some((s) => statusLasts(o, s, horizon));
}
// Returns the facing (0-3). It looks only at the four panels around the unit: a side is open when that
// panel is on the map, walkable and less than 3 higher (0x8019AD74); all four count when none is. Then
// it turns toward the nearest threatening foe (an adjacent one more than 2 below doesn't count) if that
// side is open, else drops that foe and tries the next. With no threatening foe left it takes the
// nearest foe of any kind; if that side is closed it keeps its facing when open, else the last open side.
// `horizon` = the unit's own clockticks until its next turn (AI+3663, set when its turn data is read).
export function gameFacing(u, units, field, horizon = 0) {
  const f = field && field.tiles, open = [];
  const hHere = f ? f.tiles[(u.y * f.w + u.x) * 8 + 2] : field ? Math.floor(field.height(u.x, u.y)) : 0;
  let last = 0;
  for (let k = 0; k < 4; k++) {
    const x = u.x + FACE_DIRS[k].x, y = u.y + FACE_DIRS[k].y;
    let ok;
    if (f) ok = x >= 0 && y >= 0 && x < f.w && y < f.h && !(f.tiles[(y * f.w + x) * 8 + 6] & 1) && f.tiles[(y * f.w + x) * 8 + 2] - hHere < 3;
    else ok = !field || (field.walkable(x, y) && Math.floor(field.height(x, y)) - hHere < 3);
    open.push(ok);
    if (ok) last = k;
  }
  if (!open.some(Boolean)) open.fill(true);
  const foe = foeTeamOf(u);
  const standing = (o) => o !== u && o.team === foe && o.alive && !o.crystallized && !hasStatus(o, 'Petrify') && !airborne(o);
  const zc = (o) => (field ? field.height(o.x, o.y) : o.z || 0);
  const dropped = new Set();
  for (;;) {
    let t = null, bd = Infinity, any = false;
    for (const o of units) {
      if (!standing(o) || dropped.has(o) || !threatens(o, horizon)) continue;
      const d = manh(u, o);
      if (d < 2 && Math.trunc(zc(o) - zc(u)) < -2) continue;
      if (d < bd) { bd = d; t = o; }
    }
    if (!t) {
      any = true;
      for (const o of units) { if (!standing(o)) continue; const d = manh(u, o); if (d < bd) { bd = d; t = o; } }
    }
    const k = t ? directionTo(u, t) : 4;
    if (k < 4 && open[k]) return k;
    if (!any) { dropped.add(t); continue; }
    const cur = faceIndexOf(u.facing);
    return cur >= 0 && open[cur] ? cur : last;
  }
}
// What the unit did this turn, for the CT it gets back at the end (endTurnCt). Set by moveTo and resolveAction
// while it's that unit's turn; a charge begun counts as acting.
let turnFlags = null;
// The end of a turn's CT (0x80183C6C). The turn already cost 100 when it began; the unit gets 20 back for not
// moving (unless Don't Move kept it still) and 20 for not acting (unless Don't Act or Performing), and ends
// at 60 at most. So a turn costs 100, 80 or 60, and a unit at CT 150 doesn't keep its surplus.
export function endTurnCt(u, flags) {
  u.ct -= 100;
  if (!u.alive) return;
  const moved = flags.moved || hasStatus(u, "Don't Move"), acted = flags.acted || !!u.charging || hasStatus(u, "Don't Act") || hasStatus(u, 'Performing');
  if (!moved) u.ct += 20;
  if (!acted) u.ct += 20;
  if (u.ct > 60) u.ct = 60;
}
const faceIndexOf = (f) => (f ? FACE_DIRS.findIndex((d) => d.x === f.x && d.y === f.y) : -1);
// The end of a turn: the game turns the unit (gameFacing) unless it's in the air on a Jump or gone. `ct0` is
// its CT when the turn began: the horizon is read with the turn's full 100 already taken off.
function endTurnFacing(u, units, field, ct0) {
  if (!u.alive || u.crystallized || airborne(u)) return;
  const ct = (ct0 ?? 100) - 100;
  const horizon = (u.speed > 0 ? (ct < 100 ? Math.ceil((100 - ct) / u.speed) : 0) : Infinity);
  const k = gameFacing(u, units, asField(field), horizon);
  if (k < 4) u.facing = { ...FACE_DIRS[k] };
}
// Choose among `tiles`: mode 'safe' = lowest danger (ties: nearer the target, then the later tile in row
// order); mode 'near' = nearest the target (ties: lower danger, then later). `limit` caps the distance.
function pickMoveTile(cands, limit, mode) {
  let best = null;
  // the game's key: the value of standing there (pending charges, `exp`, higher first), then danger
  const key = (c) => [-(c.exp || 0), c.danger];
  const less = (a, b) => { const ka = key(a), kb = key(b); return ka[0] !== kb[0] ? ka[0] < kb[0] : ka[1] < kb[1]; };
  const same = (a, b) => { const ka = key(a), kb = key(b); return ka[0] === kb[0] && ka[1] === kb[1]; };
  for (const c of cands) {
    if (c.dd > limit) continue;
    const later = !best || c.node.y > best.node.y || (c.node.y === best.node.y && c.node.x >= best.node.x);
    if (!best) best = c;
    else if (mode === 'safe' ? (less(c, best) || (same(c, best) && (c.dd < best.dd || (c.dd === best.dd && later))))
      : (c.dd < best.dd || (c.dd === best.dd && (less(c, best) || (same(c, best) && later))))) best = c;
  }
  return best;
}
// The panel to move to on a turn with nothing to do from here (`after` false), or after acting in place
// (`after` true: hit and run). Returns a key from `tiles`, or null to stay.
//  - target within reach next turn (Move + range >= its walking distance d): the safest tile no farther
//    than d + Move; after acting, the safest tile within R - 1 of it, else the nearest;
//  - out of reach: only tiles nearer than d, the safest within R (R/2 one time in 7), else the nearest.
export function gameMoveTile(u, target, units, f, tiles, rng = Math.random, after = false, dbg = null) {
  if (!target || !tiles.size) return null;
  const dist = distToTarget(u, target, units, f), foes = gameDangerFoes(u, units);
  const d = dist(u.x, u.y), R = longestReach(u, target);
  if (dbg) Object.assign(dbg, { d, R });
  const charges = anyCharging(u, units) ? liveCharges(units) : null;
  const all = [...tiles.values()].map((node) => ({ node, dd: dist(node.x, node.y), danger: gameDanger(node.x, node.y, foes), exp: standValue(u, units, f, node.x, node.y, node.z || 0, charges) }));
  let best;
  if (after) {
    if (R < d) return null;
    best = pickMoveTile(all, R - 1, 'safe') || pickMoveTile(all, Infinity, 'near');
  } else if (R >= d) {
    const c = all.filter((c) => c.dd <= d + (u.move || 0));
    best = pickMoveTile(c.length ? c : all, Infinity, 'safe');
  } else {
    const nearer = all.filter((c) => c.dd <= d - 1), c = nearer.length ? nearer : all;
    const r = rng() * 7 < 1 ? R >> 1 : R;
    best = pickMoveTile(c, r, 'safe') || pickMoveTile(c, Infinity, 'near');
  }
  return best ? `${best.node.x},${best.node.y}` : null;
}
// ---- The Critical AI ("AI Critical or Run Like Rabbit", 0x80198E60). A unit in Critical (or a Frog) whose
// side still has someone who isn't, two or more alive in all, decides differently ("Some targetability
// setting", 0x80194570; Death Sentence opts out). FFTBG runs it on ~11% of AI turns, in 14 of 15 battles.
// It runs to the safest panel it can reach and acts only if something beats standing there, trying itself
// first ("Find which units are active" in mode 3: only the actor is a target), then everyone else (mode 0),
// and otherwise just retreats. The retreat ("AI Cowardly Movement Routine", 0x8019905C) is the move
// decision's panel picker with no distance limit: the best value of standing there, then the lowest danger,
// ties to the panel nearer its main target, which is the nearest crystal or nearest ally who can heal
// (AI_HEALERS), itself included, else itself ("Calculate Physical Target", mode 5). Checked in a MIPS
// interpreter on FFTBG's own RAM: the rule picks the game's retreat on 26 of 26 captured turns.
const frogOrCritical = (o) => critical(o) || frogged(o);
export function criticalMode(u, units) {
  if (!frogOrCritical(u) || hasStatus(u, 'Death Sentence')) return false;
  const side = units.filter((o) => o.alive && o.team === u.team && !hasStatus(o, 'Petrify') && !airborne(o));
  return side.length >= 2 && side.some((o) => !frogOrCritical(o));
}
const canHeal = (o) => (o.actions || []).some((a) => AI_HEALERS.has(a.name) && (a.mp || 0) <= (o.mp || 0) && !(hasStatus(o, 'Silence') && muted(a)));
function criticalMainTarget(u, units, crystals) {
  let best = null, bd = Infinity;
  const consider = (p, d) => { if (d < bd) { bd = d; best = p; } };
  for (const k of crystals || []) { const [x, y] = k.split(',').map(Number); consider({ x, y, z: 0 }, Math.abs(x - u.x) + Math.abs(y - u.y)); }
  for (const o of units) if (o.alive && o.team === u.team && !hasStatus(o, 'Petrify') && !airborne(o) && canHeal(o)) consider(o, manh(o, u));
  return best || u;
}
export function criticalRetreat(u, units, f, tiles, crystals) {
  if (!tiles.size) return null;
  const m = criticalMainTarget(u, units, crystals);
  const dist = distToTarget(u, m, units, f), foes = gameDangerFoes(u, units), charges = anyCharging(u, units) ? liveCharges(units) : null;
  const all = [...tiles.values()].map((node) => ({ node, dd: dist(node.x, node.y), danger: gameDanger(node.x, node.y, foes), exp: standValue(u, units, f, node.x, node.y, node.z || 0, charges) }));
  const best = pickMoveTile(all, 255, 'safe');
  return best ? `${best.node.x},${best.node.y}` : null;
}

// Move there (walking the path), pick up a crystal, face the target.
function gameMove(u, target, units, occupied, crystals, f, rng, log, after = false) {
  const tiles = reachableTiles(u, occupied, f, units);
  const key = gameMoveTile(u, target, units, f, tiles, rng, after);
  if (key && key !== `${u.x},${u.y}`) { moveTo(u, key, tiles, occupied); absorbCrystal(u, crystals, log); if (log) log.push(`${u.name} ${after ? 'falls back' : 'moves toward ' + target.name}`); }
  faceToward(u, target);
}

// Where to stand for `action` on `target`, as FFTBG's "Find Highest Target Priority in Ability Range"
// (0x8019BF2C) does it (read off BATTLE.BIN and replayed in a MIPS interpreter on 40 captured searches,
// which reproduce the game's chosen record byte for byte). The game pops reachable panels safest first
// (lowest danger; a tie goes to the later panel in row order) and scores each target panel once, from the
// FIRST popped panel that reaches it: the safest tile that can hit, not the best angle. A splash spell
// scores each centre that catches the target the same way and keeps the best; equal scores go to the safer
// stand panel, then to the first found. The game doesn't hold fire to back away here (no kiting): moving
// off after acting is its post-action routine. `enemies` / `dangerWeight` are unused, kept for callers.
// The reachable panels in the order the stand search pops them: out of pending blasts first, then safest,
// then the later panel in row order. The same for every ability a unit weighs in one turn.
export function standOrderFor(u, units, tiles) { return standOrderOf(u, units, tiles); }
// The stand search pops panels from the move decision's plans ("Check AI Target Type", 0x80196ADC): plan 1
// first, the reachable panels within a walk of the main target (its distance plus Move when it's in reach
// this turn, else one closer than now) and the panel the unit stands on; then plan 2, the rest. Within a plan
// the key is minus the board's value with the unit on the panel (AI +1524, after pending charges, so only
// the unit's own exposure differs), then danger, then the later panel. FFTBG's Time Mage, its foes far off,
// cast Haste from a plan-1 panel a step closer to them rather than from a safer one behind it.
// The Critical AI's plans hold only the panel the unit stands on and its retreat (plan 2 is empty), so it acts
// from one of those two or not at all: FFTBG's Critical Knight hit the foe beside it from where it stood rather
// than step to the foe's back, then ran.
function critStandKeys(u, units, tiles) {
  if (!criticalMode(u, units)) return null;
  const keys = new Set([`${u.x},${u.y}`]);
  if (!cantMove(u) && tiles.size) { const r = criticalRetreat(u, units, activeField || asField(10), tiles, activeCrystals); if (r) keys.add(r); }
  return keys;
}
// The panel keys live in one table (AI +1524) that every unit's AI shares and nothing clears, so a panel not
// keyed this turn holds what the last unit to key it left there, 0 at the start of a battle. All 112 non-zero
// leftovers in our captured searches were the previous AI turn's entry for that panel. It matters for the own
// panel when it joins plan 1 late: it pops first when its leftover is below the others' keys.
// (Kept for the length of a battle; outside one, or until a replay seeds it from the game's RAM, every entry is 0.)
let standKeys = null;
export const resetStandKeys = (m = null) => { standKeys = m; };
// The AI never acts from deep water (depth 2+) unless the unit floats or has Swim or Waterwalking (AI +6585,
// checked in the stand search's panel mask, 0x8019C3D4): of 145 deep-water panels in reach across our captured
// searches, none was offered. The engine itself would let it act there (a Knight held by Don't Move in a river
// still used Armor Rend).
const deepOk = (u) => hasStatus(u, 'Float') || !!(u.moveBits && (u.moveBits.f95 & 0xc0));
function standOrderOf(u, units, tiles) {
  const crit = critStandKeys(u, units, tiles);
  if (crit) tiles = new Map([...tiles].filter(([k]) => crit.has(k)));
  const fd = activeField;
  if (fd && fd.depth && !deepOk(u)) tiles = new Map([...tiles].filter(([, n]) => fd.depth(n.x, n.y) < 2));
  const foes = gameDangerFoes(u, units);
  const charges = anyCharging(u, units) ? liveCharges(units) : null;
  const f = activeField, m = f && !crit ? mainTarget(u, units) : null;
  let plan1 = null, ownLate = false;
  if (m) {
    const dist = distToTarget(u, m, units, f), d = dist(u.x, u.y), R = longestReach(u, m);
    const limit = R >= d ? d + (u.move || 0) : d - 1;
    plan1 = (n) => (n.x === u.x && n.y === u.y) || dist(n.x, n.y) <= limit;
    ownLate = R < d; // out of reach: its own panel is past the limit and joins plan 1 only after the keys are made
  }
  // The key is minus the board's whole value after ending the turn on the panel. The own panel, when it joins
  // late, keeps whatever the shared table holds there (standKeys).
  const board = boardWorth(u, units) + trialConstant(u, units, charges || undefined);
  return [...tiles.values()].map((node) => {
    const exp = standValue(u, units, f, node.x, node.y, node.z || 0, charges);
    const own = node.x === u.x && node.y === u.y, tier = plan1 && !plan1(node) ? 2 : 1, k = `${node.x},${node.y}`;
    let key;
    if (own && ownLate) key = (standKeys && standKeys.get(k)) || 0;
    else { key = -(board + exp); if (tier === 1 && standKeys) standKeys.set(k, key); }
    return { node, danger: gameDanger(node.x, node.y, foes), exp, tier, key };
  }).sort((a, b) => (a.tier - b.tier) || (a.key - b.key) || (a.danger - b.danger) || (b.node.y - a.node.y) || (b.node.x - a.node.x));
}
function pickStandTile(u, action, target, units, tiles, enemies, dangerWeight, field = null, order = null) {
  // On itself (a potion, Gather Power, Praise): the game aims at the panel the unit stands on now, and
  // while it weighs a stand panel it puts the unit there, so only its own panel still has it to hit.
  if (target === u && !action.centerSelf) return { key: `${u.x},${u.y}`, attack: true, center: target };
  const foes = gameDangerFoes(u, units);
  if (!order) order = standOrderOf(u, units, tiles);
  const splash = splashAim(action) ? action.aoe : 0;
  const onMap = (x, y) => (field && field.w ? x >= 0 && y >= 0 && x < field.w && y < field.h && field.walkable(x, y) : x >= 0 && y >= 0);
  const tz = zOf(target);
  const lofField = field && field.tiles ? field : activeField;
  const reaches = (node, c) => { const az = node.z || 0; return elementalOk(action, node) && lockedOk(u, action, node, units) && withinV(az, c.z ?? tz, action) && lineOk(action, node, c) && manh(node, c) <= effRange(action, az, c.z ?? tz) && lofOk(lofField, u, action, node, c, units) && arcOk(lofField, u, action, node, c, units); }; // (an Elemental skill only from a panel that gives it; a direct one only with a clear shot)
  // Short evadable strikes (the basic Attack with a melee weapon, Rends, Steals, monster bites...) search
  // every tile and keep the best result, so they take the angle with the best hit chance; equal results go
  // to the safer tile, then the first in row order. Everything else takes the safest tile that reaches.
  const mode = action.standSearch || (action.name === 'Attack' ? standSearch('Attack', action.range || 1) : 'first');
  if (!splash && mode === 'all') {
    let best = null;
    // plan 1 first: only if none of its panels reaches does the search go on to plan 2
    const tier = new Map(order.map((o) => [o.node, o.tier]));
    const t1 = order.some((o) => o.tier === 1 && reaches(o.node, target));
    for (const node of tiles.values()) {
      if (!tier.has(node) || !reaches(node, target) || (t1 && tier.get(node) !== 1)) continue;
      const net = scoreAction(u, action, target, units, node), danger = gameDanger(node.x, node.y, foes);
      if (!best || net > best.net || (net === best.net && (danger < best.danger || (danger === best.danger && (node.y < best.node.y || (node.y === best.node.y && node.x < best.node.x)))))) best = { net, danger, node };
    }
    return best ? { key: `${best.node.x},${best.node.y}`, attack: true, center: target } : { key: null, attack: false };
  }
  if (!splash) {
    const hit = order.find(({ node }) => reaches(node, target));
    return hit ? { key: `${hit.node.x},${hit.node.y}`, attack: true, center: target } : { key: null, attack: false };
  }
  // splash: every centre that catches the target, each scored from the first popped panel that reaches it
  // A charged spell is weighed aimed at the unit first (its type-6 option, centred on the target's own panel),
  // then at panels; another centre takes over only if it scores strictly better (no safer-panel tiebreak for
  // these). FFTBG's Oracle cast Paralyze, and its Summoner Shiva, centred on the target where every centre tied,
  // the Summoner from a panel 6 more dangerous than the safest that reached another centre.
  let best = null;
  const offs = [];
  for (let dx = -splash; dx <= splash; dx++) for (let dy = -splash + Math.abs(dx); dy <= splash - Math.abs(dx); dy++) offs.push([dx, dy]);
  const charged = (action.charge || 0) > 0;
  // only a skill that helps its targets can gain from the caster standing in it (a blast would hit itself)
  const helps = ['heal', 'buff', 'revive', 'cleanse', 'mpHeal'].includes(action.kind) || (action.kind === 'statmod' && action.buff);
  if (charged) offs.sort((a, b) => (a[0] || a[1] ? 1 : 0) - (b[0] || b[1] ? 1 : 0));
  if (action.unitAimed) offs.splice(0, offs.length, [0, 0]); // only the entry centred on the target's own panel (the game lists each centre separately)
  for (const [dx, dy] of offs) {
    {
      const cx = target.x + dx, cy = target.y + dy;
      if (!onMap(cx, cy)) continue;
      const c = (dx || dy) ? { x: cx, y: cy, z: tz } : target;
      let from = order.find(({ node }) => reaches(node, c));
      if (!from) continue;
      let net = scoreAction(u, action, target, units, from.node, c === target ? undefined : c);
      // Standing inside its own area puts the caster in the blast too (a Geomancer hasted itself along with the
      // ally it stood beside, FFTBG's pick over the safest panel 3 away). The game scores the centre from each
      // stand panel, so a panel in the area that reaches it takes over when it scores strictly better.
      for (const o of (helps ? order : [])) {
        if (o === from || manh(o.node, c) > splash || !reaches(o.node, c)) continue;
        const ox = u.x, oy = u.y, oz = u.z;
        u.x = o.node.x; u.y = o.node.y; u.z = o.node.z ?? u.z;
        let v;
        try { v = scoreAction(u, action, target, units, o.node, c === target ? undefined : c); } finally { u.x = ox; u.y = oy; u.z = oz; }
        if (v > net) { net = v; from = o; }
      }
      if (globalThis.STANDDBG) globalThis.STANDDBG.push(`${action.name} c${cx},${cy} from ${from.node.x},${from.node.y} d${from.danger} net ${Math.round(net)}`);
      if (!best || net > best.net || (net === best.net && from.danger < best.danger && !charged)) best = { net, danger: from.danger, key: `${from.node.x},${from.node.y}`, center: c };
    }
  }
  return best ? { key: best.key, attack: true, center: best.center } : { key: null, attack: false };
}

// Exported for tests: the tile `u` would move to for `target` right now (pass
// enemies + dangerWeight to exercise kiting; omit for plain flanking).
export function bestStandTile(u, action, target, units, occupied, gridSize, enemies = [], dangerWeight = 0) {
  const prev = activeField, f = asField(gridSize);
  if (f && f.w) activeField = f;
  try { return pickStandTile(u, action, target, units, reachableTiles(u, occupied, gridSize, units), enemies, dangerWeight, f).key; } finally { activeField = prev; }
}

// Where a unit that CAN'T usefully act should run to: the reachable tile fewest foes
// can strike next turn (least summed threat-in-reach), tie-broken by distance from the
// nearest foe — so a disabled unit puts ground between itself and the enemy instead of
// loitering in melee. Returns a tile key (its own if nowhere is safer). For tests too.
export function fleeTile(u, foes, occupied, gridOrField, units = null) {
  const tiles = reachableTiles(u, occupied, gridOrField, units);
  let bestKey = `${u.x},${u.y}`, bestDanger = Infinity, bestDist = -Infinity;
  for (const node of tiles.values()) {
    const danger = dangerAt(node.x, node.y, foes);
    const dist = foes.length ? Math.min(...foes.map((e) => manh(node, e))) : 0;
    if (danger < bestDanger || (danger === bestDanger && dist > bestDist)) { bestDanger = danger; bestDist = dist; bestKey = `${node.x},${node.y}`; }
  }
  return bestKey;
}

// How many of a buff's statuses would actually help `t` (usefulStatuses); a "pick one" buff
// (statusMode 'one'/'each') lands one of its list, so it's worth the useful fraction.
function usefulBuffs(u, t, action) {
  const n = usefulStatuses(u, t, action).length;
  return action.statuses && action.statusMode !== 'all' ? n / action.statuses.length : n;
}

// A candidate's chance to land as the game's AI records it (the +29 byte of its candidate record):
// an attack's hit chance after evasion, a status or buff's success (faith-scaled), 100% for a heal,
// and "always" (255) for a Jump.
function hitOf(u, action, t, from = u) {
  if (action.jump) return 1;
  if (rendsAttack(action, t)) return hitOf(u, BASIC(u), t, from);
  if (action.kind === 'damage' && !action.multihit) return Math.min(1, strikeOf(u, action, t, from).p);
  if (['heal', 'revive', 'mpHeal'].includes(action.kind) && action.success == null) return 1;
  // A "(Separate)" list: the AI's hit byte is a quarter of the rate (FFTBG's Cheer Up, Hit (MA + 87)% from an
  // MA-9 Dancer, recorded 24), so the cascade takes it about one time in four.
  const base = action.hit ? successOf(u, action, t) / 100 : (action.faith ? (action.success ?? 100) / 100 * effFaith(t) / 50 : (action.success ?? 100) / 100);
  const ev = action.evade && action.evade !== 'none' ? evadeHit(u, action, t, from) : 1;
  // then the hit through it, rounded down: ⌊pass × hit / 100⌋ (the same routine, into the action's hit at +42)
  const hit = ev < 1 ? Math.floor(Math.round(base * 100) * Math.round(ev * 100) / 100) / 100 : base;
  // a quarter, rounded down (FFTBG's Cheer Up at 94% listed 23); a list with no Hit clause already rolls 25 each
  // (Parasite listed 25)
  return Math.max(0, Math.min(1, action.statusMode === 'each' && action.hit ? Math.floor(Math.round(hit * 100) / 4) / 100 : hit));
}

// An area's hit, as FFTBG's AI lists it: the average, rounded down, over the units its trial counts (charged
// casts leave out whoever moves first) that are on the side it's meant for and that it would change: any foe
// for damage, one without the status for a status or buff, one carrying a status for a cleanse. FFTBG's Haste
// on two allies at 74% and 64% listed 69, its Bio on foes at 80% and 100% listed 90, its Immobilize that also
// caught its caster listed the foe's 91, and its Stabilize Time ignored an ally with only Don't Act on. null
// when nobody counts.
function countsForHit(u, action, o) {
  if (!o.alive && action.kind !== 'revive') return false;
  if (action.kind !== 'damage' && ((action.target === 'enemy' && sideOf(u, o) > 0) || (action.target === 'ally' && sideOf(u, o) < 0))) return false;
  if (action.kind === 'cleanse') return (action.statuses || []).some((st) => hasStatus(o, st));
  const sts = action.statuses || (action.status ? [action.status] : null);
  if ((action.kind === 'status' || action.kind === 'buff') && sts) return sts.some((st) => !immuneTo(o, st));
  return true;
}
export function areaHit(u, action, primary, units, center = primary, from = u) {
  if (action.linear) { // a line: whoever stands on the ray from the stand panel toward the aim point (Earth Slash
    // with two foes clear of evade and one at 67% listed 89)
    const dx = center.x - from.x, dy = center.y - from.y;
    const d = Math.abs(dx) >= Math.abs(dy) ? { x: Math.sign(dx), y: 0 } : { x: 0, y: Math.sign(dy) };
    const on = lineUnits(from.x, from.y, d, action.aoe, units).filter((o) => countsForHit(u, action, o));
    return on.length ? Math.floor(on.reduce((a, o) => a + Math.round(100 * hitOf(u, action, o, from)), 0) / on.length) / 100 : null;
  }
  // (a charged cast that follows a target who walks off: the hit counts that target and everyone who stays, the
  // two sets the trial weighs separately: FFTBG's Sleep on a foe who'd move, beside a sleeping one, listed 70 =
  // their average)
  const counted = [...new Set(trialSets(u, action, primary, units, center).flat())].filter((o) => countsForHit(u, action, o));
  if (!counted.length) return null;
  return Math.floor(counted.reduce((a, o) => a + Math.round(100 * hitOf(u, action, o, from)), 0) / counted.length) / 100;
}

// Can `u` strike any of `enemies` this turn (move + its longest offensive reach)?
function canEngageOf(u, enemies) {
  const maxAtkReach = Math.max(u.range || 1, ...(u.actions || []).filter((a) => a.kind === 'damage' || a.kind === 'status').map((a) => a.range || 1));
  return enemies.some((e) => manh(u, e) <= (u.move || 0) + maxAtkReach);
}
// Value AI: rank the reachable (action, target) candidates by what they'd be worth if they land, then
// pick the way FFT's AI does, accepting each in turn with its hit chance (see the end). Deterministic
// (the top one) with no rng. The fourth argument is unused (it was an `explore` noise knob, which the
// game's own hit-chance roll replaced).
// The options weighed with their own stand (and direction) search: each line attack and self-centred blast at its
// best panel, and the best Math cast. chooseAction lists them beside the rest.
export function turnPlans(u, units, occupied, gridSize, config, canMove) {
  const saved = pendingHp, savedP = pendingPerf; pendingHp = pendingDamage(u, units); pendingPerf = pendingPerformances(units);
  try { return turnPlans0(u, units, occupied, gridSize, config, canMove); } finally { pendingHp = saved; pendingPerf = savedP; }
}
function turnPlans0(u, units, occupied, gridSize, config, canMove) {
  const plans = [];
  for (const a of (u.actions || []).filter((x) => x.linear)) { const p = bestLinearPlan(u, units, occupied, gridSize, config, canMove, a); if (p && p.value > 0) plans.push({ kind: 'linear', ...p }); }
  for (const a of (u.actions || []).filter((x) => x.centerSelf)) { const p = bestCenterSelfPlan(u, units, occupied, gridSize, config, canMove, a); if (p && p.value > 0) plans.push({ kind: 'self', ...p }); }
  const m = bestMathPlan(u, units); if (m && m.value > 0) plans.push({ kind: 'math', ...m });
  return plans;
}

export function chooseAction(u, units, rng, field = null, debug = null, only = null, plans = null) {
  const saved = pendingHp, savedP = pendingPerf; pendingHp = pendingDamage(u, units); pendingPerf = pendingPerformances(units);
  try { return chooseAction0(u, units, rng, field, debug, only, plans); } finally { pendingHp = saved; pendingPerf = savedP; }
}
function chooseAction0(u, units, rng, field = null, debug = null, only = null, plans = null) { // debug: an array to receive the scored candidates (diagnostics); only: 'self' / 'others' (the Critical AI's two passes)
  if (field) setActiveField(field); // a field passed in (the harness) is the one to read surfaces from
  const foe = foeTeamOf(u), friend = friendTeamOf(u); // swapped while Charmed
  const enemies = units.filter((e) => e.alive && !airborne(e) && e.team === foe && e !== u && !hasStatus(e, 'Petrify')); // (Charm makes own team the foe — never target self); petrified = out of the fight; airborne jumpers are untargetable
  const allies = units.filter((a) => a.alive && a.team === friend && !hasStatus(a, 'Petrify'));
  const foesWorthAiming = enemies.filter((e) => !hasStatus(e, 'Death Sentence') || e.charging || hasStatus(e, 'Charging'));
  const deadAllies = units.filter((a) => !a.alive && a.dead != null && a.team === friend); // bodies only — a crystal can't be raised
  const basic = frogged(u) ? frogBasic(u) : BASIC(u);
  const silenced = hasStatus(u, 'Silence'); // can't cast magic / faith abilities
  // Wild Beast (nearly every monster, simPrep `noBasic`): no basic Attack action of its own. It
  // still swings when it counters, or when Berserk/Confusion take over (separate paths).
  const beastmasterNear = units.some((o) => o !== u && o.alive && o.beastmaster && o.team === u.team);
  const actions = frogged(u) ? [basic] : [...(u.noBasic ? [] : [basic]), ...(u.actions || []).filter((a) => !(a.locked && !beastmasterNear) && (a.mp || 0) <= u.mp && !a.linear && !(a.centerSelf && a.kind !== 'statmod') && !a.mathCast && !(silenced && muted(a)))]; // Frog: weak basic only; linear rays + self-centered blasts + math-casts are planned separately (self pumps such as Accumulate stay here, as last resorts)


  // The rolls the game makes before it weighs an ability (with an rng; deterministic calls skip them, which
  // is the game's roll of 0). A random multi-hit spell (Void Storage, Dragon Pit, Sky Demon...) is weighed
  // only with chance (⌊128 × such / usable⌋ + 1) / 128, its share of the unit's usable abilities, so a
  // Dancer with 2 such of 9 weighs each about one turn in four (FFTBG's lists mostly lacked them). Yell,
  // Praise and Accumulate take each target only on a coin flip.
  const usable = actions.length, shares = actions.filter((a) => a.shareRoll).length;
  const shareOk = () => !rng || Math.floor(rng() * 128) <= Math.floor(128 * shares / Math.max(1, usable));
  const rolled = rng ? actions.filter((a) => !a.shareRoll || shareOk()) : actions;
  const coinOk = (action) => !rng || !action.coinFlip || rng() < 0.5;
  const cands = [];
  // Targets are gathered per action first (`pending`, before the reach test, like the game's
  // targetable set) so the charge-time preference below can look at all of them at once.
  let pending = [];
  const consider = (action, target) => { if (target && coinOk(action) && canAffect(action, target) && !(action.noSelf && target === u) && !(only === 'self' && target !== u) && !(only === 'others' && target === u)) pending.push(target); }; // noSelf: the game won't aim it at its user (Cheer Up)
  const lastResort = []; // charges at targets that will walk off the panel: worth nothing (see below)
  // An Elemental skill is valued from the reachable panels that give it (see elementalOk): the best of
  // them that reaches the target, as the game's stand search tries every reachable panel.
  let geoTiles = null;
  const elementalFrom = (action, target) => {
    if (!geoTiles) {
      geoTiles = [];
      if (activeField && activeField.surface && !cantMove(u)) {
        const occ = new Set(units.filter((o) => o.alive && o !== u).map((o) => `${o.x},${o.y}`));
        for (const n of reachableTiles(u, occ, activeField, units).values()) geoTiles.push({ x: n.x, y: n.y, z: activeField.height(n.x, n.y), el: ELEMENTAL_BY_SURFACE[activeField.surface(n.x, n.y)] });
      }
    }
    let best = 0;
    for (const t of geoTiles) if (t.el === action.name && manh(t, target) <= effRange(action, t.z, zOf(target))) best = Math.max(best, scoreAction(u, action, target, units, t));
    return best;
  };
  // Like the game, list only what can really be done this turn: some panel the unit can reach (or its own,
  // if it can't move) within the ability's range and height limits of the target, an area's splash
  // included. The coarse check above (Move + range as the crow flies) let our Squires rank a Death
  // Sentence on a foe behind a wall, where FFTBG's list held only Yell and Praise.
  // The panels the stand search would pop, in its order (standOrderOf: plan 1 first; a Critical unit has only
  // its own panel and its retreat), shared by the reach check and the hit-angle lookup below.
  let standOrder = null, standTiles = null;
  const standOrderHere = (f) => {
    if (!standOrder) {
      const occ = new Set(units.filter((o) => (o.alive || o.dead != null) && o !== u).map((o) => `${o.x},${o.y}`));
      const map = cantMove(u) ? new Map([[`${u.x},${u.y}`, { x: u.x, y: u.y, z: zOf(u) }]]) : reachableTiles(u, occ, f, units);
      const prev = activeField; activeField = f;
      try { standOrder = standOrderOf(u, units, map).map((o) => ({ ...o, node: { x: o.node.x, y: o.node.y, z: o.node.z ?? f.height(o.node.x, o.node.y) } })); } finally { activeField = prev; }
    }
    return standOrder;
  };
  const canReach = (action, target, direct = false) => { // direct: aimed at the unit itself, no splash
    if (target === u || fieldWide(action)) return true;
    const f = field && typeof field === 'object' ? field : activeField;
    if (!f) return true;
    if (!standTiles) standTiles = standOrderHere(f).map((o) => o.node);
    const tz = zOf(target), splash = !direct && splashAim(action) ? action.aoe : 0;
    if (!splash) return standTiles.some((n) => elementalOk(action, n) && lockedOk(u, action, n, units) && withinV(n.z, tz, action) && lineOk(action, n, target) && manh(n, target) <= effRange(action, n.z, tz) && lofOk(f, u, action, n, target, units) && arcOk(f, u, action, n, target, units));
    // an area aimed at a panel beside the target: that centre must be within range of a reachable panel, and
    // the target within the area's height spread of it (FFTBG's Time Mage on low ground had no Demi 2 for a
    // foe on a cliff 8 panels up, two panels out of range: Demi 2's area spreads only 4 up or down)
    const centres = [];
    for (let dx = -splash; dx <= splash; dx++) for (let dy = -splash + Math.abs(dx); dy <= splash - Math.abs(dx); dy++) {
      const cx = target.x + dx, cy = target.y + dy;
      if (f.w && (cx < 0 || cy < 0 || cx >= f.w || cy >= f.h)) continue;
      const cz = (dx || dy) ? f.height(cx, cy) : tz;
      if (Math.abs(tz - cz) <= vSpreadOf(action)) centres.push({ x: cx, y: cy, z: cz });
    }
    return standTiles.some((n) => elementalOk(action, n) && lockedOk(u, action, n, units) && centres.some((c) => withinV(n.z, c.z, action) && manh(n, c) <= effRange(action, n.z, c.z) && lofOk(f, u, action, n, c, units) && arcOk(f, u, action, n, c, units)));
  };
  // Where the hit chance is read from. Evade by facing depends on the side it comes from, and the game reads
  // the hit at the panel its stand search picks for that target, not from where the unit stands now (FFTBG's
  // Knight read Armor Rend at 82% from the target's back, where its current panel faced the shield side at
  // 37%). That's pickStandTile's panel, found here without scoring every panel: a short strike (mode 'all')
  // keeps the best result, which for a single target is the best angle, ties to the safer panel and then
  // the first in row order; anything else takes the safest panel that reaches.
  const angleFrom = (action, target) => {
    if (target === u) return u;
    const f = field && typeof field === 'object' ? field : activeField;
    if (!f) return u;
    const order = standOrderHere(f);
    const tz = zOf(target);
    const reaches = (n) => elementalOk(action, n) && lockedOk(u, action, n, units) && withinV(n.z, tz, action) && lineOk(action, n, target) && manh(n, target) <= effRange(action, n.z, tz) && lofOk(f, u, action, n, target, units) && arcOk(f, u, action, n, target, units);
    const mode = action.standSearch || (action.name === 'Attack' ? standSearch('Attack', action.range || 1) : 'first');
    let pick = null;
    if (mode === 'all' && !splashAim(action)) {
      let bh = -1;
      const t1 = order.some((o) => o.tier === 1 && reaches(o.node)); // plan 1 only, if any of it reaches
      for (const o of order) {
        if (!reaches(o.node) || (t1 && o.tier !== 1)) continue;
        const h = evadeHit(u, action, target, o.node);
        if (h > bh || (h === bh && (o.danger < pick.danger || (o.danger === pick.danger && (o.node.y < pick.node.y || (o.node.y === pick.node.y && o.node.x < pick.node.x)))))) { bh = h; pick = o; }
      }
    } else pick = order.find((o) => reaches(o.node));
    return pick ? pick.node : u;
  };
  // The centre the stand search would aim an area at for `target` (pickStandTile), or null.
  const splashCentre = (action, target) => {
    const f = field && typeof field === 'object' ? field : activeField;
    if (!f || target === u) return null;
    if (!standMap) {
      const occ = new Set(units.filter((o) => (o.alive || o.dead != null) && o !== u).map((o) => `${o.x},${o.y}`));
      standMap = cantMove(u) ? new Map([[`${u.x},${u.y}`, { x: u.x, y: u.y, z: zOf(u) }]]) : reachableTiles(u, occ, f, units);
    }
    if (!splashOrder) splashOrder = standOrderOf(u, units, standMap);
    const pick = pickStandTile(u, action, target, units, standMap, [], 0, f, splashOrder);
    if (!pick.key) return null;
    const n = standMap.get(pick.key);
    return { center: pick.center, from: n ? { x: n.x, y: n.y, z: n.z ?? f.height(n.x, n.y) } : u };
  };
  let standMap = null, splashOrder = null, tieFoes = null;
  // the danger at the panel a candidate is used from: the game's last tiebreak between equal entries
  const dangerOf = (p) => gameDanger(p.x, p.y, tieFoes || (tieFoes = gameDangerFoes(u, units)));
  const evaluate = (action, target, escapes) => {
    const W = globalThis.WHY, why = (r) => { if (W) (W[action.name] = W[action.name] || []).push(`${target.name}:${r}`); };
    if (W) why('considered');
    if (!reachable(u, target, action)) { why('out of move+range'); return; }
    if (!canReach(action, target)) { why('no panel reaches'); return; }
    // valued from the panel the stand search would take for it, not from where the unit stands now (FFTBG's
    // Squire listed a Dash at a foe 1.5 below it that ours, measuring the height from its own panel, valued 0)
    const stand = angleFrom(action, target);
    let v = ELEMENTAL.has(action.name) && !elementalOk(action, u) ? elementalFrom(action, target) : scoreAction(u, action, target, units, stand);
    // A Jump is worth nothing to the game's AI too: its board evaluation leaves the jump out (read
    // off FFTBG's per-unit scores: after a Jump candidate no enemy has lost anything, the jumper's own
    // value is a few points up, the same as a turn spent doing nothing), so it jumps only when no
    // other option is worth anything, typically when no foe is in Attack reach but one is in jump range.
    let hit = hitOf(u, action, target, stand), standAt = stand;
    // An area is valued at the centre the stand search would aim it (often an empty panel beside the target that
    // catches more, pickStandTile; only worth searching when some other unit stands near enough to share an
    // area with the target), and its hit is the average over the units it counts (areaHit).
    if ((action.aoe || 0) > 0 && !fieldWide(action) && !action.multihit && !action.linear) {
      let c = target, from = stand;
      if (splashAim(action) && units.some((o) => o !== target && o.alive && manh(o, target) <= 2 * action.aoe)) {
        const sc = splashCentre(action, target);
        if (sc && sc.center && sc.center !== target) { c = sc.center; from = sc.from; standAt = from; v = scoreAction(u, action, target, units, from, c); }
      }
      const ah = areaHit(u, action, target, units, c, from);
      if (ah != null) hit = ah;
    }
    // Stat changes are worth nothing to the game's board evaluation too (no PA/MA/Speed/Brave/Faith term):
    // in FFTBG's own candidate scores Praise and Yell tie exactly, and Accumulate, Yell and Praise tie
    // with each other, i.e. with doing nothing. So a song, dance, break, Chicken or Accumulate is a last
    // resort, cast when nothing else is worth anything (our old per-point prices had our Byblos casting
    // Chicken 15 times where FFTBG's cast it 4).
    // (A cast that follows its target is the exception: the trial follows the unit as it walks and counts
    // it, see trialTargets.)
    // CT changes (Quick, a single-target CT to 0) as well: the scoreboard's turn-timing term is worth a few
    // points at most, and FFTBG's Time Mages listed Quick below Haste or not at all (ours had it first).
    // Songs and dances that reach the whole side are worth nothing too: in FFTBG's candidate scores Wiznaibus
    // and Nameless Song tie exactly with an Attack that reaches no one, and Last Dance or Last Song sit a few
    // points above it. So a Dancer dances when nothing else is worth anything (FFTBG's never chose Nameless
    // Dance; ours did, valuing eight statuses on every foe).
    if ((escapes && !(action.follows && !action.jump)) || action.jump || action.kind === 'statmod' || action.kind === 'ctset' || fieldWide(action)) { if (v > 0) lastResort.push({ action, target, v: 0, raw: v, hit, score: 0, danger: dangerOf(standAt), stand: standAt }); return; }
    // A weapon throw is listed at half its hit: the AI ranks it by what it's worth if it lands, but takes it
    // only half as often, the throw landing as one of two items (FFTBG listed every Axe, Stick, Wand, Knife and
    // Dictionary throw at exactly half, its Shurikens and Bombs, whose item it picks, at the full rate).
    const listed = action.throwPick === 'random' ? Math.floor(Math.round(hit * 100) / 2) / 100 : hit;
    if (!(v > 0)) why(`value ${Math.round(v)}`); else if (W) why(`v${v.toFixed(1)} h${hit.toFixed(2)}`);
    if (v > 0) cands.push({ action, target, v, hit: listed, score: hit > 0 ? v / hit : v, danger: dangerOf(standAt), stand: standAt });
  };
  for (const action of rolled) {
    pending = [];
    gatherTargets(action);
    let targets = pending;
    // Charge-time preference ("AI ability use control routine", 0x8019B0F8, via 0x8019F01C): for a
    // single-target charge, drop the targets that will get a turn (and can move) before it lands.
    // Among the targets in reach: if any of those is left, choose among them; if none is, the check is
    // ignored. (FFTBG's Oracle, its one staying foe out of reach, listed Dark Holy at a Swine that would
    // move first, at its full value since the cast follows it; read as "skip the ability", ours had none.)
    // If every target would escape, the check is ignored and the ability stays a candidate, but the
    // board evaluation runs the clock forward to the landing and leaves out every unit that got a
    // turn first (0x8019DF0C), so it is worth nothing: the AI uses it only when it has no better
    // option, which is when FFTBG's Lancers jump at a unit that walks away (4 of 51 such turns).
    // Only enemies are screened (0x8019F01C checks a per-unit byte that is set on the actor's
    // foes), so an ally waiting on a charged heal or buff always stays a target.
    let escapes = false;
    if ((action.charge || 0) > 0 && !(action.aoe > 0)) {
      const inReach = targets.filter((t) => reachable(u, t, action) && canReach(action, t));
      const landing = inReach.filter((t) => t === u || !t.alive || t.team !== foe || chargeLands(u, action, t));
      if (landing.length) targets = landing; else escapes = true;
    }
    // A charged area spell aimed at a panel (0x8019F01C) drops each foe that will get a turn first (on the landing
    // tick too) from its aim points, and isn't listed at all if none is left. Such a foe stays only for a cast aimed
    // at the unit itself, which follows it. FFTBG's Wizard, whose only foe in reach (by splash) had its turn on the
    // 6th tick, listed Fire 2 (4 ticks) and Ice (3) but not Fire 3 or Bolt 3 (6).
    if ((action.charge || 0) > 0 && (action.aoe || 0) > 0 && !fieldWide(action) && !action.centerSelf && !action.linear && !action.multihit) {
      targets = targets.filter((t) => t === u || t.team !== foe || !t.alive || chargeLands(u, action, t) || (action.follows && canReach(action, t, true)));
    }
    for (const t of targets) evaluate(action, t, escapes);
  }
  function gatherTargets(action) {
    // A foe under Death Sentence isn't aimed at unless it's charging: the same "worth chasing" flag as the main
    // target (per-unit +4 & 0x80). In FFTBG's own lists no option was ever aimed at a doomed foe (ours: 60).
    const enemies = foesWorthAiming;
    // Damage at an ally is weighed too when it would end something (Sleep, Charm, Confusion): FFTBG's Bards and
    // Monks threw at sleeping teammates to wake them, sometimes as their best option (a Shuriken that woke a Monk
    // topped a Bard's list). The board evaluation decides; for any other ally a hit is only a loss.
    if (action.kind === 'damage') { enemies.forEach((e) => consider(action, e)); allies.forEach((a) => { if (a !== u && ENDED_BY_DAMAGE.some((st) => st !== 'Transparent' && hasStatus(a, st))) consider(action, a); }); }
    else if (action.kind === 'status') enemies.forEach((e) => { if (usefulStatuses(u, e, action).length) consider(action, e); });
    // The AI heals only the genuinely hurt (<50% HP), not every scratch — and never
    // an undead ally (a heal would damage it) — but it DOES sear an undead foe.
    else if (action.kind === 'heal') { allies.forEach((a) => { if (a.hp < a.maxHp * 0.5 && !isUndead(a)) consider(action, a); }); enemies.forEach((e) => { if (isUndead(e)) consider(action, e); }); }
    // Proactive buffs apply any time; the rest only on an already-damaged ally.
    else if (action.kind === 'buff') (action.target === 'self' ? [u] : allies).forEach((a) => { if (usefulBuffs(u, a, action) > 0) consider(action, a); });
    // Revive a fallen ally; Phoenix Down also instakills a living undead foe.
    // A charged revive isn't aimed at a body that crystallizes first ("AI Ability Use Decisions" calls
    // 0x80199C88: clockticks until it does, ceil((100 × (counter + 1) − CT) / Speed), against the charge).
    // A body with Reraise brings itself back, so it's no revive target either (its AI byte +6195 has 0x40).
    else if (action.kind === 'revive') { deadAllies.forEach((d) => { if (hasStatus(d, 'Reraise') || ((action.charge || 0) > 0 && crystalTicks(d) < effChargeOf(u, action))) return; consider(action, d); }); if (action.undeadKill) enemies.forEach((e) => { if (isUndead(e)) consider(action, e); }); }
    // MP burn a foe (worth most against a mana-rich caster); restore a mana-starved ally.
    else if (action.kind === 'mpDamage') enemies.forEach((e) => { if ((e.mp || 0) > 0) consider(action, e); });
    else if (action.kind === 'mpHeal') allies.forEach((a) => { if ((a.mp || 0) < (a.maxMp ?? 0) * 0.6 && mpAbilityCount(a)) consider(action, a); });
    // Cleanse an afflicted ally (Esuna) / Dispel a buffed foe — only when there's something to remove.
    // (a petrified ally is out of the fight for everything else, but a Soft or Esuna can still cure it: FFTBG's
    // Chemist listed Soft on its stoned teammate)
    else if (action.kind === 'cleanse') (action.dispel ? enemies : units.filter((a) => a.alive && a.team === friend)).forEach((o) => { if ((action.statuses || []).some((s) => hasStatus(o, s))) consider(action, o); });
    // Quick (CT→100) hands an ally a near-instant turn; Set CT 0 stalls a foe.
    else if (action.kind === 'ctset') { if (action.ct >= 100) allies.forEach((a) => { if (a !== u) consider(action, a); }); else enemies.forEach((e) => consider(action, e)); }
    // Stat songs buff the team; dances/breaks/talk debuff a foe. (scoreAction returns 0
    // once a stat is capped, so the bard naturally stops re-singing.) Buffs are cast even with no
    // foe in reach, as the game's Squires Yell and Accumulate; takeTurn closes in while buffing.
    else if (action.kind === 'statmod') { if (action.buff) { [u, ...allies].forEach((a) => consider(action, a)); } else enemies.forEach((e) => consider(action, e)); }
    // Steal/Rend a foe's gear (scoreAction returns ~0 if the slot's loss wouldn't weaken them).
    else if (action.kind === 'strip') enemies.forEach((e) => { if (action.fallbackAttack || !(e.removed && e.removed.has(action.slot))) consider(action, e); });
  }
  // How the game picks (FFTBG's BATTLE.BIN): "Set Highest Target Priority" keeps the 8 best
  // candidates ranked by their score IF THEY LAND, hit chance aside (only a tiebreak); then "Randomly
  // Transfer Data" (0x801970BC) walks that list in order and takes entry k when rand() % 100 is
  // under its hit % (the record's +29), falling back to the first. So an Attack at 68% is taken 68%
  // of the time and otherwise the roll falls through, often to a Jump (hit 255: always taken), which
  // is why FFTBG's Lancers jump about a third of the time they could attack. The worthless
  // candidates (jumps, charges at escaping targets) sit at the bottom of the list to catch those
  // fall-throughs. With no rng, the first entry (deterministic, for tests).
  // Ties: integer score as the game's, then hit % (0x80196DB0), then the lower danger at the stand panel
  // (0x80196E20, AI +372), then whichever was listed first. The worthless entries all score the game's
  // do-nothing baseline, so among them only hit and danger count.
  // Line attacks, self-centred blasts and Math Skill are weighed apart (takeTurn: their own stand and direction
  // search) but listed here like any option, one entry per skill, so the same score order and hit cascade pick
  // among them: FFTBG's Minotaur listed Blow Fire 8 points above Shake Off and took it, where ours, comparing the
  // plans by value outside the list, never fired its Blow Fire.
  // Each is listed at the average hit over the units it counts, as an area is (areaHit): FFTBG's Earth Slash
  // through two foes clear of evade and an ally listed 100, 67 and 100 as 89.
  for (const p of plans || []) {
    const stand = p.key ? { x: +p.key.split(',')[0], y: +p.key.split(',')[1], z: p.z ?? zOf(u) } : { x: u.x, y: u.y, z: zOf(u) };
    let hit = 1;
    if (p.kind === 'linear') hit = areaHit(u, p.action, u, units, { x: stand.x + p.dir.x, y: stand.y + p.dir.y }, stand) ?? 1;
    else if (p.kind === 'self') {
      const on = selfBlastUnits(u, p.action, stand.x, stand.y, stand.z, units).filter((o) => countsForHit(u, p.action, o));
      if (on.length) hit = Math.floor(on.reduce((a, o) => a + Math.round(100 * hitOf(u, p.action, o, stand)), 0) / on.length) / 100;
    } else if (p.kind === 'math') { // a Math Skill cast too: FFTBG's Calculator listed a math Slow at 83, not 100
      const on = mathTargets(p.action, units).filter((o) => countsForHit(u, p.action, o));
      if (on.length) hit = Math.floor(on.reduce((a, o) => a + Math.round(100 * hitOf(u, p.action, o, u)), 0) / on.length) / 100;
    }
    cands.push({ action: p.action, target: u, v: p.value, hit, score: hit > 0 ? p.value / hit : p.value, danger: dangerOf(stand), stand, plan: p });
  }
  cands.sort((a, b) => (Math.round(b.score) - Math.round(a.score)) || (b.hit - a.hit) || (a.danger - b.danger));
  lastResort.sort((a, b) => (b.hit - a.hit) || (a.danger - b.danger));
  // One entry per skill unless it charges: an instant skill (or a whole-side song or dance, or a Jump) goes
  // through the game's panel search alone, which returns the skill's single best use. A charged spell also
  // gets an entry per target unit. In FFTBG's lists Attack never appeared twice, Wiznaibus never twice;
  // Haste on two allies did.
  const once = (a) => !((a.charge || 0) > 0) || fieldWide(a) || a.jump;
  const seen = new Set();
  const ranked = [...cands, ...lastResort].filter((c) => { if (!once(c.action)) return true; if (seen.has(c.action)) return false; seen.add(c.action); return true; });
  if (debug) debug.push(...ranked);
  if (!ranked.length && only) return null; // the Critical AI's passes don't fall back to an advance
  if (!ranked.length) return enemies.length ? { action: basic, target: mainTarget(u, units) || nearest(u, enemies), ...(u.noBasic && !frogged(u) ? { advanceOnly: true } : {}) } : null; // advance
  let pick = ranked[0];
  if (rng) {
    const top = ranked.slice(0, 8);
    pick = top.find((c) => rng() * 100 < c.hit * 100) || ranked[0];
  }
  // Ninja Throw nerf (FFTBG): once throwing is chosen, the projectile is a COINFLIP — 50%
  // the best (highest-damage) throw, 50% the basic (lowest-power, i.e. shuriken/bomb) —
  // rather than always the optimum. (Throws all fly the same distance, so a perfect AI
  // would always pick the hardest hitter; the real bot doesn't.)
  if (rng && pick.action.thrown) {
    const throws = (u.actions || []).filter((a) => a.thrown && (a.mp || 0) <= u.mp);
    if (throws.length > 1) {
      const best = throws.reduce((b, a) => (a.power > b.power ? a : b), throws[0]);
      const basicT = throws.reduce((b, a) => (a.power < b.power ? a : b), throws[0]);
      return { action: rng() < 0.5 ? basicT : best, target: pick.target };
    }
  }
  return { action: pick.action, target: pick.target, ...(pick.plan ? { plan: pick.plan } : {}) };
}

const unitAt = (x, y, units) => units.find((o) => o.alive && o.x === x && o.y === y) || null;

// Apply one action's effect to a single target (shared by normal resolution and
// the mime reaction). Exported for tests.
// What an ability can affect at all. "Hit: Human;" (the talk skills) skips monsters, "Hit: Undead;" (Seal Evil)
// everything but the undead, and FFTBG's Sleep help: "Unit is unaffected by Talk Skill and recurring
// abilities" (FFTBG's Squire, a sleeping Monk in reach, listed only Yell and Praise; no Death Sentence). Recurring
// means the songs and dances (simPrep `performance`): a Galaxy Stop, whole-side but not one, still stopped sleepers
// in the game's look-ahead.
const canAffect = (action, t) => !((action.only === 'Human' && (t.gender === 'Monster' || hasStatus(t, 'Sleep')))
  || (action.only === 'Undead' && !isUndead(t)) || ((action.performance ?? fieldWide(action)) && hasStatus(t, 'Sleep')));
export function applyEffect(u, action, t, occupied, rng, log) {
  if (!canAffect(action, t)) { if (log) log.push(`${t.name} is unaffected by ${u.name}'s ${action.name}`); return; }
  if (rendsAttack(action, t)) { if (log) log.push(`${t.name} has no ${action.slot}: ${u.name}'s ${action.name} strikes instead`); dealAttack(u, BASIC(u), t, occupied, rng, log, false); return; }
  // An evadable status or other effect (Slow and Stop by M-Ev, Beak and Goo by evade by facing) can be dodged
  // too; a damaging one rolls its own in dealAttack.
  if (action.kind !== 'damage' && action.evade && action.evade !== 'none') {
    const ev = evadeHit(u, action, t, u);
    if (ev < 1 && rng() >= ev) { if (log) log.push(`${t.name} evades ${u.name}'s ${action.name}`); return; }
  }
  if (action.kind === 'damage') {
    dealAttack(u, action, t, occupied, rng, log, false);
  } else if (action.kind === 'heal') {
    const amt = Math.max(1, Math.round(healOf(u, action, t))); // items a share of max HP; Cure-type by Faith; zodiac where it applies
    if (isUndead(t)) { // healing burns the undead — Cure/Potion/Regen deal that amount as damage
      t.hp -= amt;
      if (log) log.push(`${u.name}'s ${action.name} sears undead ${t.name} −${amt} → ${Math.max(0, t.hp)}`);
      if (t.hp <= 0) kill(t, occupied, log);
    } else {
      const before = t.hp; t.hp = Math.min(t.maxHp, t.hp + amt);
      if (log) log.push(`${u.name}'s ${action.name} heals ${t.name} +${t.hp - before} → ${t.hp}`);
    }
    if (action.mpPct) t.mp = Math.min(t.maxMp ?? t.mp, (t.mp || 0) + Math.round((t.maxMp ?? 0) * action.mpPct / 100)); // Elixir also tops MP
  } else if (action.kind === 'mpHeal') { // Ether/Rehabilitate — restore MP (flat or % of max)
    const amt = action.pct ? Math.round((t.maxMp ?? 0) * action.pct / 100) : action.power;
    const before = t.mp || 0; t.mp = Math.min(t.maxMp ?? t.mp, before + amt);
    if (log) log.push(`${u.name}'s ${action.name} restores ${t.mp - before} MP to ${t.name}`);
  } else if (action.kind === 'mpDamage') { // Bizen Boat/Witch Hunt/Mute — burn MP (flat or % of max), faith-gated
    const succ = action.success != null ? successOf(u, action, t) : 100;
    if (rng() * 100 < succ) {
      const amt = action.pctMaxMp ? Math.round((t.maxMp ?? 0) * action.pctMaxMp / 100) : action.power;
      const d = Math.min(amt, t.mp || 0); t.mp -= d;
      if (action.drain === 'mp' && u.alive) u.mp = Math.min(u.maxMp ?? u.mp, (u.mp || 0) + d); // Siphon (AbsorbMP) feeds the caster
      if (log) log.push(`${u.name}'s ${action.name} ${action.drain === 'mp' ? 'drains' : 'burns'} ${d} MP from ${t.name}`);
    } else if (log) log.push(`${t.name} resists ${u.name}'s ${action.name}`);
  } else if (action.kind === 'cleanse') { // Esuna/Stabilize Time strip afflictions; Dispel Magic strips a foe's buffs
    const succ = action.success != null || action.hit ? successOf(u, action, t) : 100;
    if (rng() * 100 < succ) {
      const removed = (action.statuses || []).filter((s) => hasStatus(t, s) && !isPermanent(t, s)); // gear/class statuses can't be stripped
      for (const s of removed) delete t.status[s];
      if (log && removed.length) log.push(`${u.name}'s ${action.name} cleanses ${t.name} (${removed.join(', ')})`);
    } else if (log) log.push(`${t.name} resists ${u.name}'s ${action.name}`);
  } else if (action.kind === 'ctset') { // Quick (→100, a near-instant turn) / Set CT 0 (stall), faith-gated
    const succ = action.success != null || action.hit ? successOf(u, action, t) : 100;
    if (rng() * 100 < succ) { t.ct = action.ct; if (log) log.push(`${u.name}'s ${action.name}: ${t.name} CT → ${action.ct}`); }
    else if (log) log.push(`${t.name} resists ${u.name}'s ${action.name}`);
  } else if (action.kind === 'statmod') { // songs/dances/talk/breaks: ±stat (PA/MA re-bake, Speed/Brave/Faith live), capped
    const succ = action.success != null || action.hit ? successOf(u, action, t) : 100;
    if (rng() * 100 < succ) {
      let reb = false;
      for (const mod of (action.mods || [])) {
        const d = appliedDelta(t, mod); if (!d) continue;
        if (mod.stat === 'pa') { t.paBonus = (t.paBonus || 0) + d; reb = true; }
        else if (mod.stat === 'ma') { t.maBonus = (t.maBonus || 0) + d; reb = true; }
        else if (mod.stat === 'speed') t.speed += d;
        else if (mod.stat === 'brave') t.brave = (t.brave ?? 50) + d;
        else if (mod.stat === 'faith') t.faith = (t.faith ?? 50) + d;
      }
      if (reb) rebake(t); // PA/MA changed → refresh this unit's baked damage numbers
      if (log) log.push(`${u.name}'s ${action.name}: ${t.name} ${(action.mods || []).map((m) => `${m.delta > 0 ? '+' : ''}${m.delta} ${m.stat.toUpperCase()}`).join(', ')}`);
    } else if (log) log.push(`${t.name} resists ${u.name}'s ${action.name}`);
  } else if (action.kind === 'strip') { // Steal/Rend — drop an equipment slot, then re-derive the target's stats
    const succ = action.hit ? successOf(u, action, t) : (action.success ?? 50);
    if (rng() * 100 < succ) {
      t.removed = t.removed || new Set();
      if (!t.removed.has(action.slot)) { t.removed.add(action.slot); rebake(t); if (log) log.push(`${u.name}'s ${action.name} strips ${t.name}'s ${action.slot}`); }
      else if (log) log.push(`${t.name} has no ${action.slot} to take`);
    } else if (log) log.push(`${t.name} resists ${u.name}'s ${action.name}`);
  } else if (action.kind === 'revive') {
    const succ = action.success != null || action.hit ? successOf(u, action, t) : 100;
    if (!(rng() * 100 < succ)) { if (log) log.push(`${u.name}'s ${action.name} fails on ${t.name}`); }
    else if (action.undeadKill && t.alive && isUndead(t)) { // a revive on a living undead unit kills it
      t.hp = 0; kill(t, occupied, log);
      if (log) log.push(`${u.name}'s ${action.name} destroys undead ${t.name}`);
    } else if (!t.alive) { t.alive = true; t.dead = null; t.hp = reviveHp(action, t); occupied.add(`${t.x},${t.y}`); if (log) log.push(`${u.name}'s ${action.name} revives ${t.name} → ${t.hp}`); }
  } else if (action.kind === 'status' || action.kind === 'buff') {
    const succ = action.success != null || action.hit ? successOf(u, action, t) : 100; // faith magic: real target faith gates it (Innocent ⇒ ~0)
    const put = (st) => { if (st === 'Death') { if (!immuneTo(t, 'Death')) { t.hp = 0; kill(t, occupied, log); } } else addStatus(t, st); }; // each status's own FFT length (STATUS_TICKS / OWN_TURNS)
    hitAdds = new Set();
    try {
    if (action.statuses && action.statusMode) { // multi-status: each (Bad Breath) / one (Nameless Song) / all (Goo)
      const got = [];
      if (action.statusMode === 'one') { if (rng() * 100 < succ) { const st = action.statuses[Math.floor(rng() * action.statuses.length)]; put(st); got.push(st); } }
      else if (action.statusMode === 'all') { if (rng() * 100 < succ) for (const st of action.statuses) { if (!t.alive) break; put(st); got.push(st); } }
      // 'each' ("(Separate)"): every status gets its own roll, at the hit chance shared across the
      // list. In FFTBG's build Cheer Up (6 statuses) landed ~1 per cast, Noxious Gas ~1, Bad Breath
      // (9) 1–5; a full-chance roll per status had them landing nearly all six to nine.
      else for (const st of action.statuses) { if (!t.alive) break; if (rng() * 100 < succ / action.statuses.length) { put(st); got.push(st); } }
      if (log) log.push(got.length ? `${u.name}'s ${action.name}: ${t.name} → ${got.join(', ')}` : `${t.name} resists ${u.name}'s ${action.name}`);
    } else if (rng() * 100 < succ) { // single status/buff
      put(action.status);
      if (log) log.push(`${u.name}'s ${action.name}: ${t.name} → ${action.status}`);
    } else if (log) log.push(`${t.name} resists ${u.name}'s ${action.name}`);
    } finally { hitAdds = null; }
  }
}

// A mime copies an ally's action from its OWN tile at the same relative offset.
// Single-target hits whoever stands on the offset tile (or whiffs into empty
// space); AoE blankets that tile (friendly fire). Un-clever, exactly the offset —
// the mime never re-aims. Uses the mime's own stats (its mimicTable entry).
function resolveMimic(m, action, cx, cy, units, occupied, rng, log) {
  // Self-centered echo (Draw Out & co.): the burst is centered on the MIME and spares it
  // exactly as it spared the original caster (Draw Out never hits the user). Reuse the
  // normal centerSelf collection so the caster-exclusion + vertical band are honored.
  if (action.centerSelf) {
    for (const t of collectTargets(m, action, m, units)) applyEffect(m, action, t, occupied, rng, log);
    return;
  }
  // Homing echo (songs/dances): stays TEAM-scoped from the mime's side (don't blanket
  // everyone at the offset) — a mimed song buffs the mime's team, a mimed dance hits foes.
  if (action.homing) {
    const friendly = action.target === 'ally' || action.target === 'self';
    for (const o of units) if (o.alive && o.team === (friendly ? friendTeamOf(m) : foeTeamOf(m))) applyEffect(m, action, o, occupied, rng, log);
    return;
  }
  if (!(action.aoe > 0)) {
    const t = unitAt(cx, cy, units);
    if (t) applyEffect(m, action, t, occupied, rng, log);
    else if (log) log.push(`${m.name}'s mimic hits nothing`);
    return;
  }
  const center = { x: cx, y: cy };
  for (const o of units) {
    if (manh(o, center) > action.aoe) continue;
    if (action.kind === 'revive') { if (!o.alive && o.dead != null) applyEffect(m, action, o, occupied, rng, log); }
    else if (o.alive) applyEffect(m, action, o, occupied, rng, log);
  }
}

// A mime echoes a teammate's MATH-CAST: it re-runs the same calculation (attribute ×
// number) with the mime's OWN spell power, re-evaluated NOW — so it reads the current
// board, including the original caster's post-turn CT (107→7), which can catch units the
// original cast spared. (The mime has no calc params of its own; it copies the math.)
function triggerMathMimics(actor, action, units, occupied, rng, log) {
  for (const m of units) {
    if (!m.mimic || !m.alive || m === actor || m.team !== actor.team || stopped(m)) continue;
    const base = m.mimicTable && m.mimicTable[action.baseName]; // the mime's own-stat copy of the spell
    if (!base) continue;
    const a = { ...base, mathCast: action.mathCast, mp: 0, charge: 0, range: 99, aoe: 0, name: action.name };
    if (log) log.push(`${m.name} mimics ${action.name}`);
    for (const t of mathTargets(a, units)) applyEffect(m, a, t, occupied, rng, log);
  }
}

// When a non-mime acts, each living mime teammate echoes the action from its own
// position at the same target offset (a reaction, not a chosen turn). It uses the
// mime's own-stat copy of that ability (mimicTable), or its own basic for 'Attack'.
function triggerMimics(actor, action, primary, units, occupied, rng, log) {
  const dx = primary.x - actor.x, dy = primary.y - actor.y;
  // Charge+N is a synthesized boosted basic (not a parser ability, so not in the mimic
  // table) — a mime echoes it as its OWN basic swing, like a plain Attack.
  const basicLike = action.name === 'Attack' || /^Charge\+\d+$/.test(action.name);
  for (const m of units) {
    if (!m.mimic || !m.alive || m === actor || m.team !== actor.team || stopped(m)) continue;
    const a = basicLike ? { ...BASIC(m) } : (m.mimicTable && m.mimicTable[action.name]);
    if (!a) continue;
    const cx = m.x + dx, cy = m.y + dy;
    faceToward(m, { x: cx, y: cy });
    if (log) log.push(`${m.name} mimics ${action.name}`);
    resolveMimic(m, a, cx, cy, units, occupied, rng, log);
  }
}

// Resolve a linear attack: ray from the caster's tile in direction d, hitting every
// unit along it (friendly fire). Teammate mimes echo the same ray from their tile.
function resolveLinear(u, action, d, units, occupied, rng, log) {
  for (const t of lineUnits(u.x, u.y, d, action.aoe, units)) applyEffect(u, action, t, occupied, rng, log);
  if (u.mimic) return;
  for (const m of units) {
    if (!m.mimic || !m.alive || m === u || m.team !== u.team || stopped(m)) continue;
    const a = m.mimicTable && m.mimicTable[action.name];
    if (!a) continue;
    m.facing = { ...d };
    if (log) log.push(`${m.name} mimics ${action.name}`);
    for (const t of lineUnits(m.x, m.y, d, a.aoe, units)) applyEffect(m, a, t, occupied, rng, log);
  }
}

// A "(multihit)" attack: strike Random(lo,hi) times, each landing on a RANDOM
// panel in the AoE diamond around the aim point — a strike on an empty/off-map
// panel is wasted; a panel with a unit (either team — friendly fire) gets hit.
function resolveMultihit(u, action, primary, units, occupied, rng, log) {
  const { lo, hi } = action.multihit;
  const n = lo + Math.floor(rng() * (hi - lo + 1));
  const panels = [];
  for (let dx = -action.aoe; dx <= action.aoe; dx++)
    for (let dy = -action.aoe; dy <= action.aoe; dy++)
      if (Math.abs(dx) + Math.abs(dy) <= action.aoe) panels.push([primary.x + dx, primary.y + dy]);
  if (log) log.push(`${u.name}'s ${action.name} strikes ${n}× across the area`);
  for (let k = 0; k < n; k++) {
    const [px, py] = panels[Math.floor(rng() * panels.length)];
    const t = units.find((o) => o.alive && !airborne(o) && o.x === px && o.y === py);
    if (t && withinV(zOf(u), zOf(t), action)) dealAttack(u, action, t, occupied, rng, log, false);
  }
}

// FFTBG's status help: "Transparent: Until the unit acts or is damaged, attacks used by it cannot be
// evaded." Acting ends it, once the action has gone off (its own attack still can't be dodged).
function resolveAction(u, action, primary, units, occupied, rng, log, center = primary) {
  if (turnFlags && turnFlags.unit === u) turnFlags.acted = true;
  try { resolveActionOn(u, action, primary, units, occupied, rng, log, center); } finally { if (u.status && u.status.Transparent && !isPermanent(u, 'Transparent')) delete u.status.Transparent; }
}
function resolveActionOn(u, action, primary, units, occupied, rng, log, center = primary) {
  // Math Skill: instant, board-wide, free. Apply the spell single-target to every unit
  // whose CT/height satisfies the number — both teams (friendly fire), no range/LOS.
  if (action.mathCast) {
    const targets = mathTargets(action, units);
    if (log) log.push(`${u.name} calculates ${action.name} (${targets.length} hit)`);
    for (const t of targets) applyEffect(u, action, t, occupied, rng, log);
    if (!u.mimic) triggerMathMimics(u, action, units, occupied, rng, log); // teammates' mimes re-run the calculation
    return;
  }
  // Reflect: single-target magic bounces off the target toward the caster's side.
  if (bounces(action) && (action.aoe || 0) === 0 && (action.kind === 'damage' || action.kind === 'status') && hasStatus(primary, 'Reflect')) {
    if (log) log.push(`${primary.name} reflects ${action.name}`);
    const back = units.filter((o) => o.alive && !airborne(o) && o.team === u.team).sort((a, b) => manh(primary, a) - manh(primary, b))[0];
    if (!back) { faceToward(u, primary); return; }
    primary = back;
  }
  if (action.multihit) { // Obsidian Blade & co. — random scattered strikes, not a single blast
    resolveMultihit(u, action, center, units, occupied, rng, log);
    faceToward(u, center);
    if (!u.mimic) triggerMimics(u, action, center, units, occupied, rng, log);
    return;
  }
  // A direct shot (lineOfFire) when it's carried out: a unit in its path takes it instead, and terrain in the way
  // wastes it (the game's 0x801B0818 returns no target, so nothing is hit). Aimed at a bare panel, it hits only a
  // unit that gets in the way. The AI's stand search has already ruled out blocked panels for most direct actions;
  // skills that take a direct weapon's range aren't checked there, so this is where their shot gets stopped.
  if (primary !== u && shootsDirect(u, action) && activeField && activeField.tiles) {
    const aim = center && center.alive ? center : (units.find((o) => o.alive && !airborne(o) && o.x === center.x && o.y === center.y) || { x: center.x, y: center.y });
    const r = lineOfFire(activeField, u, u, aim, units);
    if (r.unit) { if (log) log.push(`${r.unit.name} is in the way of ${u.name}'s ${action.name}`); primary = center = r.unit; }
    else if (!r.clear || aim.alive === undefined) {
      if (log) log.push(`${u.name}'s ${action.name} is blocked`);
      faceToward(u, center);
      if (!u.mimic) triggerMimics(u, action, center, units, occupied, rng, log);
      return;
    }
  }
  // A bow's arrow when it's fired (0x801AFF18): if neither arc gets through, a foe in the way takes it, or nobody.
  if (primary !== u && primary && primary.alive && shootsArc(u, action) && activeField && activeField.tiles) {
    const r = bowArc(activeField, u, u, primary, units);
    if (!r.clear && r.unit !== primary) {
      if (r.unit) { if (log) log.push(`${r.unit.name} is in the way of ${u.name}'s ${action.name}`); primary = center = r.unit; }
      else {
        if (log) log.push(`${u.name}'s ${action.name} is blocked`);
        faceToward(u, center);
        if (!u.mimic) triggerMimics(u, action, center, units, occupied, rng, log);
        return;
      }
    }
  }
  const targets = collectTargets(u, action, primary, units, center);
  for (const t of targets) applyEffect(u, action, t, occupied, rng, log);
  if (action.selfCost && u.alive) { // "DamageCaster": the caster pays HP for it
    u.hp -= action.selfCost; if (log) log.push(`${u.name} pays ${action.selfCost} HP → ${Math.max(0, u.hp)}`);
    if (u.hp <= 0) kill(u, occupied, log);
  }
  if (primary !== u) faceToward(u, center); // a self-centered cast keeps the facing the caller set (toward the foes)
  if (!u.mimic) triggerMimics(u, action, center, units, occupied, rng, log); // teammates' mimes echo it (same offset as the blast)
}

function takeTurn(u, units, occupied, crystals, gridSize, rng, log, config) {
  setActiveField(gridSize); activeCrystals = crystals;
  tickStatus(u, occupied, log);
  if (!u.alive) return;
  if (stopped(u)) return;           // Stop / Sleep / Petrify — no act, no move

  const canMove = !cantMove(u);
  const realFoes = () => units.filter((e) => e.alive && !airborne(e) && e.team !== u.team);

  // Berserk: uncontrollable — charge the nearest enemy and basic-attack (×1.5).
  if (berserk(u)) {
    const foes = realFoes();
    if (!foes.length) return;
    const target = nearest(u, foes);
    if (canMove) { moveToward(u, target, u.range, occupied, gridSize, units); absorbCrystal(u, crystals, log); }
    faceToward(u, target);
    if (manh(u, target) <= u.range && lineOk(BASIC(u), u, target)) resolveAction(u, { ...BASIC(u), power: Math.round((u.power || 1) * 1.5) }, target, units, occupied, rng, log);
    else if (log) log.push(`${u.name} rushes ${target.name} (berserk)`);
    return;
  }

  // Confusion: uncontrollable — basic-attack the NEAREST unit, friend or foe (can
  // hurt its own team; a hit snaps it out, which is why allies sometimes whack it).
  if (hasStatus(u, 'Confusion')) {
    const all = units.filter((o) => o.alive && !airborne(o) && o !== u && !hasStatus(o, 'Petrify'));
    if (!all.length) return;
    const target = nearest(u, all);
    if (canMove) { moveToward(u, target, u.range, occupied, gridSize, units); absorbCrystal(u, crystals, log); }
    faceToward(u, target);
    if (manh(u, target) <= u.range && lineOk(BASIC(u), u, target)) resolveAction(u, BASIC(u), target, units, occupied, rng, log);
    else if (log) log.push(`${u.name} stumbles about (confused)`);
    return;
  }

  // Don't Act (but can still move): FLEE. A disabled unit can't retaliate, so loitering
  // in melee just hands the enemy free hits — the real AI runs from danger. Move to the
  // safest reachable tile (fewest foes can reach it / farthest from the nearest), facing
  // the nearest threat. (Was: advance toward the enemy, which made Don't Act far too weak
  // a debuff in the sim.)
  if (cantAct(u)) {
    if (canMove) {
      const foes = realFoes();
      if (foes.length) {
        const key = fleeTile(u, foes, occupied, gridSize, units);
        if (key !== `${u.x},${u.y}`) { moveTo(u, key, reachableTiles(u, occupied, gridSize, units), occupied); absorbCrystal(u, crystals, log); }
        faceToward(u, nearest(u, foes));
      }
    }
    if (log) log.push(`${u.name} can't act`);
    return;
  }

  const enemies = realFoes();

  // The Critical AI (criticalMode): its move, before or after acting, is the retreat, and it never walks
  // toward a target it can't reach. (This replaced a guess: below 30% HP, beeline for the nearest crystal.)
  const crit = criticalMode(u, units);
  const retreat = crit && canMove ? criticalRetreat(u, units, asField(gridSize), reachableTiles(u, occupied, gridSize, units), crystals) : null;
  const retreatMove = () => {
    if (retreat && retreat !== `${u.x},${u.y}`) { moveTo(u, retreat, reachableTiles(u, occupied, gridSize, units), occupied); absorbCrystal(u, crystals, log); if (log) log.push(`${u.name} retreats`); }
    if (enemies.length) faceToward(u, nearest(u, enemies));
  };

  // The move after acting in place (see planAfter below): plan 1 of the game's move decision.
  const planAfterFor = (me, tgt) => {
    if (crit) return retreat;
    const foesNow = realFoes();
    const main = mainTarget(me, units) || (tgt && tgt.team !== me.team ? tgt : (foesNow.length ? nearest(me, foesNow) : null)); // its main target, as the game's move decision
    return main ? gameMoveTile(me, main, units, asField(gridSize), reachableTiles(me, occupied, gridSize, units), rng) : null;
  };
  const moveAfterTo = (plan) => {
    if (!plan || plan === `${u.x},${u.y}` || !u.alive || cantMove(u) || airborne(u)) return;
    const now = reachableTiles(u, occupied, asField(gridSize), units);
    if (now.has(plan)) { moveTo(u, plan, now, occupied); absorbCrystal(u, crystals, log); if (log) log.push(`${u.name} moves after acting`); }
  };

  // Special plans that don't aim at a single foe: linear rays (Earth Slash & co.) fire
  // a straight ray from the caster; self-centered blasts (Draw Out & co.) detonate
  // around the caster. Each picks its own best stand tile and is listed beside the
  // ordinary actions. (Frog can only flail a weak basic — no casting.)
  const cands = []; // every scored candidate, best first (for the reach fallback below)
  // each line attack and self-centred blast with its best stand panel (and direction), and the best Math cast:
  // entries in chooseAction's list
  const plans = frogged(u) ? [] : turnPlans(u, units, occupied, gridSize, config, canMove);
  // the Critical AI tries itself first, and takes it: a self-centred skill is aimed at its own panel, so it's
  // one of those (FFTBG's Geomancer at 11 HP took Chakra)
  const selfChoice = crit ? chooseAction(u, units, rng, 0, cands, 'self', plans.filter((p) => p.kind === 'self')) : null;
  const choice = selfChoice || chooseAction(u, units, rng, 0, cands, crit ? 'others' : null, plans);
  const picked0 = choice && choice.plan;
  const mathPlan = picked0 && picked0.kind === 'math' ? picked0 : null, selfPlan = picked0 && picked0.kind === 'self' ? picked0 : null, linearPlan = picked0 && picked0.kind === 'linear' ? picked0 : null;

  // Math Skill (Calculator): a free, instant, board-wide cast, fired where it stands.
  if (mathPlan) {
    resolveAction(u, mathPlan.action, u, units, occupied, rng, log); // primary unused on the mathCast path
    return;
  }

  // Self-centered blast (Draw Out & co.): center on the caster, never the caster's own
  // tile damaging itself — that's handled in collectTargets.
  if (selfPlan) {
    const inPlace = selfPlan.key === `${u.x},${u.y}`;
    const plan = inPlace && canMove ? planAfterFor(u, null) : null; // cast where it stands, then take the move (planAfter)
    if (canMove && !inPlace) { moveTo(u, selfPlan.key, reachableTiles(u, occupied, gridSize, units), occupied); absorbCrystal(u, crystals, log); }
    const en = realFoes(); if (en.length) faceToward(u, nearest(u, en));
    if (selfPlan.action.charge > 0) {
      const ctr = Math.max(1, Math.ceil(selfPlan.action.charge * (u.shortCharge ? 0.5 : 1)));
      u.charging = { action: selfPlan.action, target: u, ctr, mpDue: selfPlan.action.mp || 0 }; // target self → always "alive" when the charge lands
      if (log) log.push(`${u.name} begins ${selfPlan.action.name}`);
    } else {
      u.mp -= (selfPlan.action.mp || 0);
      if (log) log.push(`${u.name} unleashes ${selfPlan.action.name}`);
      resolveAction(u, selfPlan.action, u, units, occupied, rng, log); // primary = self ⇒ centered on the caster
    }
    moveAfterTo(plan);
    return;
  }

  if (linearPlan) {
    if (canMove && linearPlan.key !== `${u.x},${u.y}`) { moveTo(u, linearPlan.key, reachableTiles(u, occupied, gridSize, units), occupied); absorbCrystal(u, crystals, log); }
    u.facing = { ...linearPlan.dir };
    u.mp -= (linearPlan.action.mp || 0);
    if (log) log.push(`${u.name} unleashes ${linearPlan.action.name}`);
    resolveLinear(u, linearPlan.action, linearPlan.dir, units, occupied, rng, log);
    return;
  }

  if (!choice || !choice.target) { if (crit) retreatMove(); return; }
  // Nothing it may use is in reach and it can't basic-attack (Wild Beast): just close in.
  if (choice.advanceOnly) {
    if (crit) { retreatMove(); return; }
    if (canMove) gameMove(u, choice.target, units, occupied, crystals, asField(gridSize), rng, log);
    return;
  }
  let { action, target } = choice;

  // chooseAction's reach check is coarse (move + range as the crow flies), so its pick can have no
  // tile to act from once walls, height and blocked paths count. FFT scores options from the tiles a
  // unit can really stand on, so then take the best candidate that IS usable from here this turn
  // rather than walking toward the first. (FFTBG's Ochu Goo'd the Hydra beside it where ours walked
  // toward a Ninja it couldn't reach; our Dancer walked where the game danced.) Kept although the
  // backtest dipped when it was first tried: the goal is the game's actions, not a better score.
  const dw = config.dangerWeight ?? 0.2;
  const tiles = canMove ? reachableTiles(u, occupied, gridSize, units) : new Map([[`${u.x},${u.y}`, { x: u.x, y: u.y, z: zOf(u) }]]);
  let picked = fieldWide(action) ? null : pickStandTile(u, action, target, units, tiles, enemies, canMove ? dw : 0, gridSize);
  if (picked && picked.key == null) {
    for (const c of cands) {
      if (c.plan || (c.action === action && c.target === target)) continue;
      if (fieldWide(c.action)) { action = c.action; target = c.target; picked = null; break; }
      const p = pickStandTile(u, c.action, c.target, units, tiles, enemies, canMove ? dw : 0, gridSize);
      if (p.key != null && p.attack) { action = c.action; target = c.target; picked = p; break; }
    }
  }

  // Acting from where it stands leaves the move for after. The game marks this case itself: when the
  // panel its stand search pops is the unit's own (0x8019C20C sets AI +20), it evaluates the action from
  // there and swaps in the move plan of the active slot, plan 1: what its move decision chose before any
  // action was weighed (0x8019638C, toward the main target). So the tile is decided before acting, and
  // walked to after. A Jump leaves the field and doesn't walk.
  const planAfter = () => (canMove ? planAfterFor(u, target) : null);
  const moveAfter = moveAfterTo;

  // 0-range songs/dances reach a whole team regardless of position — perform in place
  // (no approach, no range/height gate), then keep performing on later turns. Summons are
  // homing too but POSITIONAL (real range), so they fall through to the normal cast path
  // below and approach into range first.
  if (fieldWide(action)) {
    const en = realFoes(); if (en.length) faceToward(u, nearest(u, en));
    const plan = planAfter();
    if (action.charge > 0) {
      const ctr = landChargeOf(u, action);
      u.charging = { action, target, ctr, mpDue: action.mp || 0 };
      if (log) log.push(`${u.name} begins ${action.name}`);
    } else {
      u.mp -= (action.mp || 0);
      if (log) log.push(`${u.name} performs ${action.name}`);
      resolveAction(u, action, target, units, occupied, rng, log);
    }
    moveAfter(plan);
    return;
  }

  let center = target; // the panel the action is aimed at (a nearby tile for an off-unit splash)
  const act = () => {
    if (!withinV(zOf(u), zOf(center), action) || !lineOk(action, u, center) || manh(u, center) > effRange(action, zOf(u), zOf(center))) return; // height-adjusted reach + vertical tolerance
    // A charged skill's MP is paid when it lands, not when the charge starts (FFTBG's Calculator still had 123 of
    // 123 MP mid-Galaxy Stop and 59 after), so one that can't pay by then fizzles and an abandoned one costs nothing.
    if (!(action.charge > 0)) u.mp -= (action.mp || 0);
    if (action.charge > 0) {
      // CTR is in clockticks (one sim tick = one CT increment for all units), and
      // counts down independent of the caster's Speed — Haste grants more turns but
      // does NOT shorten a charge. Short Charge halves the CTR (BMG 4.2), except a Jump's.
      // A Jump lands on the PANEL its target stood on (`panel`), whoever is there by then.
      const ctr = landChargeOf(u, action);
      u.charging = center === target ? { action, target, ctr, mpDue: action.mp || 0 } : { action, target, ctr, center, mpDue: action.mp || 0 };
      // Aimed at a unit (the game's flag; Holy, Flare, the summons...), it follows that unit wherever it goes,
      // out of the caster's range or not: FFTBG's Sylph caught a target that had walked 6 panels off a caster
      // of range 4. Anything else is aimed at the panel and lands there, as a Jump does.
      if (action.jump || (action.follows === false && center === target && !(action.aoe > 0))) u.charging.panel = { x: target.x, y: target.y };
      else if (action.follows === false && center === target) u.charging.center = { x: target.x, y: target.y, z: zOf(target) };
      if (log) log.push(`${u.name} begins ${action.name}`);
    } else {
      resolveAction(u, action, target, units, occupied, rng, log, center);
    }
  };

  // Don't Move: act only from the current tile (no repositioning).
  if (!canMove) {
    if (picked.center) center = picked.center;
    faceToward(u, center); act(); return;
  }

  // Where to act from is the game's stand search (pickStandTile): nothing in reach → its move decision;
  // the unit's own tile → act, then move; any other tile → move there, then act.
  const { key, attack } = picked;
  if (picked.center) center = picked.center;
  if (key == null) {
    if (crit) { retreatMove(); return; } // the Critical AI doesn't close in
    gameMove(u, mainTarget(u, units) || target, units, occupied, crystals, asField(gridSize), rng, log); // out of reach: FFT's move decision, toward its main target
    return;
  } else if (!attack) {
    moveTo(u, key, tiles, occupied); // kite: reposition to safety, hold the attack
    absorbCrystal(u, crystals, log);
    if (enemies.length) faceToward(u, nearest(u, enemies));
    if (log) log.push(`${u.name} repositions`);
    return;
  } else if (key !== `${u.x},${u.y}`) {
    moveTo(u, key, tiles, occupied);
  } else {
    const plan = planAfter(); // acting from where it stands: act, then take the move (see planAfter)
    act();
    moveAfter(plan);
    return;
  }
  absorbCrystal(u, crystals, log); // stepped onto a crystal en route
  act();
}

// Seedable PRNG (mulberry32). A `config.seed` makes a single battle fully
// deterministic — same teams + config + seed ⇒ the identical fight — which is what
// makes a fight reproducible from a `repro` blob (serializeRepro / replayRepro below).
// runBattles never sets a seed, so the N-run odds keep their variance.
export const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
};

export function simulateBattle(teamA, teamB, config = {}, rng = Math.random) {
  try { return simulateBattleOn(teamA, teamB, config, rng); } finally { setActiveField(null); resetStandKeys(null); }
}
function simulateBattleOn(teamA, teamB, config, rng) {
  setTune(config); resetStandKeys(new Map());
  if (config.seed != null) rng = mulberry32(config.seed); // reproducible run
  const field = makeField(config);
  const maxTurns = config.maxTurns ?? 200;
  const undeadReviveChance = config.undeadReviveChance ?? 0.625; // undead reanimate (vs crystallize) at this rate: FFTBG's Undead help says 62.5%, at the start of the 4th turn with Death
  const log = (config.log || config.frames) ? [] : null;
  const units = [];

  const mkUnit = (s, team, x, y, facing) => ({
    ...s, status: { ...(s.status || {}) }, charging: null,
    speed: Math.max(1, s.speed || 1), mp: s.mp || 0, maxMp: s.mp || 0,
    brave: s.brave ?? 50, reaction: s.reaction ?? null,
    team, maxHp: s.hp, hp: s.hp,
    removed: new Set(s.removed), // per-battle copy — team specs are reused across all runs, so don't share the strip set
    x, y, z: field.height(x, y), facing: { ...facing }, // z = standing-tile height (combat height math)
    ct: Math.floor(rng() * (s.speed || 1)), alive: true,
  });
  // Gear/class statuses (simPrep): permanent ones never run out, initial ones start their usual timer.
  const withTraits = (u) => { for (const st of u.permStatus || []) u.status[st] = PERMANENT; for (const st of u.initStatus || []) addStatus(u, st); return u; };

  // Place on the map's authoritative ENTD start tiles (team A = Player 1, B = P2),
  // or — with no terrain — on opposite rows of the flat box.
  const FACE = { North: { x: 0, y: -1 }, South: { x: 0, y: 1 }, East: { x: 1, y: 0 }, West: { x: -1, y: 0 } };
  const starts = config.terrain && Array.isArray(config.terrain.starts) ? config.terrain.starts : null;
  if (starts && starts.some((s) => s.team === 'Player 1') && starts.some((s) => s.team === 'Player 2')) {
    const slots = (label) => starts.filter((s) => s.team === label).sort((a, b) => (a.unit || 0) - (b.unit || 0));
    const placeOn = (specs, team, sl) => specs.forEach((s, i) => { const t = sl[i] || sl[sl.length - 1] || { x: 0, y: 0 }; units.push(withTraits(mkUnit(s, team, t.x, t.y, FACE[t.facing] || { x: 0, y: 1 }))); });
    // Left/first team (A) is ALWAYS Player 1, right team (B) Player 2 — a fixed,
    // deterministic placement (roster slot i → start tile i). Randomizing the side
    // shuffled positions, which shifts who-reaches-whom and turn order on speed ties.
    placeOn(teamA, 'A', slots('Player 1'));
    placeOn(teamB, 'B', slots('Player 2'));
  } else {
    const place = (specs, team, row, facing) => {
      const startCol = Math.max(0, Math.floor((field.w - specs.length) / 2));
      specs.forEach((s, i) => units.push(withTraits(mkUnit(s, team, startCol + i, row, facing))));
    };
    place(teamA, 'A', 0, { x: 0, y: 1 });
    place(teamB, 'B', field.h - 1, { x: 0, y: -1 });
  }

  const occupied = new Set(units.map((u) => `${u.x},${u.y}`));
  const crystals = new Set(); // tile keys holding a crystal (walkable; full HP/MP on step)
  const aliveTeam = (t) => units.some((u) => u.team === t && u.alive && !hasStatus(u, 'Petrify')); // an all-petrified team is out (stone = unable to fight)

  // Snapshot the board to the log, but only when something actually changed
  // (skips the duplicate lines from move-only / wasted turns).
  // Structured per-action frames for the visual replay (FightModal's BattleViewer).
  // One frame per board change (same trigger as the log snapshot); the viewer
  // derives who-got-hit from HP deltas between frames, so we only carry the actor.
  const frames = config.frames ? [] : null;
  const boardSnapshot = () => units.filter((u) => !u.crystallized).map((u) => ({
    name: u.name, team: u.team, x: u.x, y: u.y, hp: Math.max(0, u.hp), maxHp: u.maxHp,
    alive: u.alive, dead: u.dead ?? null, dir: { ...u.facing }, status: u.status ? { ...u.status } : {},
    cls: u.cls, gender: u.gender, raidBoss: u.raidBoss,
  }));
  let lastSig = null, frameMark = 0;
  const pushState = (actor) => {
    if (!log) return;
    const sig = units.map((u) => `${u.crystallized ? 'z' : u.alive ? u.hp : 'd' + u.dead}@${u.x},${u.y}`).join('|') + '#' + [...crystals].sort().join(',');
    if (sig === lastSig) return;
    lastSig = sig;
    logState(units, log);
    if (frames) {
      const caption = log.slice(frameMark, log.length - 1).filter((l) => !l.startsWith('    ·')).join(' · ');
      frames.push({ turn: turns, actor: actor || null, caption, board: boardSnapshot(), crystals: [...crystals] });
      frameMark = log.length;
    }
  };

  let turns = 0;
  if (frames) frames.push({ turn: 0, actor: null, caption: 'Battle begins.', board: boardSnapshot(), crystals: [] });
  while (turns < maxTurns && aliveTeam('A') && aliveTeam('B')) {
    for (const u of units) {
      if (!u.alive && u.dead == null) continue; // crystallized / fully gone — no CT
      if (u.alive) tickTimers(u);
      if (u.charging) {
        u.charging.ctr -= 1; // count down the charge by one clocktick (Speed-independent)
        if (u.charging.ctr <= 0) {
          const c = u.charging; u.charging = null;
          // A charge aimed at a panel (`panel`: a Jump, or a single-target skill that doesn't follow) lands on
          // whoever stands there, and whiffs if its mark walked off; one aimed at a unit follows it.
          const singleAimed = (c.action.aoe || 0) === 0 && !c.action.homing && !c.action.centerSelf && !c.action.linear && !c.action.mathCast;
          if (c.panel) { // a Jump comes down on its panel: whoever stands there takes it, or nobody
            const on = unitAt(c.panel.x, c.panel.y, units);
            if (on && on !== c.target) c.target = on;
          }
          const dodged = singleAimed && c.target.alive && !!c.panel && (c.target.x !== c.panel.x || c.target.y !== c.panel.y);
          if (c.mpDue && (u.mp || 0) < c.mpDue) { if (log) log.push(`${u.name}'s ${c.action.name} fails: not enough MP`); }
          else {
            u.mp -= c.mpDue || 0; // paid as it lands
            if (dodged) { if (log) log.push(`${c.target.name} steps out of ${u.name}'s ${c.action.name}`); }
            else if (c.center || c.target.alive || c.action.homing) { resolveAction(u, c.action, c.target, units, occupied, rng, log, c.center || c.target); pushState(u.name); } // a homing dance still sweeps the team even if its aim point died mid-charge; a panel-aimed blast (c.center) goes off on its panel whoever stands there
            else if (log) log.push(`${u.name}'s ${c.action.name} fizzles`);
          }
        }
      }
      // CT keeps building while a unit charges or is up on a Jump, and going off doesn't reset it (the logged
      // battles: a Wizard charging Fire 2 went 8 -> 17 -> 35 and on to 62 after it landed).
      u.ct += effSpeed(u);
    }
    const ready = units.filter((u) => (u.alive || u.dead != null) && !airborne(u) && u.ct >= 100).sort((a, b) => (b.ct - a.ct) || (rng() - 0.5));
    for (const u of ready) {
      if (turns >= maxTurns || !aliveTeam('A') || !aliveTeam('B')) break;
      if (u.crystallized) continue;
      if (!u.alive && u.dead != null) { // a corpse's AT: tick the countdown, then crystallize (undead may reanimate)
        if (--u.dead < 0) { if (isUndead(u) && rng() < undeadReviveChance) reviveUndead(u, rng, log); else crystallize(u, occupied, crystals, log); }
        u.ct -= 100; pushState(u.name); continue;
      }
      if (!u.alive || airborne(u)) continue;
      // A turn that comes up before its charge has gone off: the unit acts afresh, and choosing an action drops
      // the charge (the AI clears it, 0x8019D5F8; a Ninja's Galaxy Stop was lost this way in a logged battle).
      if (u.charging) { if (log) log.push(`${u.name} abandons ${u.charging.action.name}`); u.charging = null; }
      const ct0 = u.ct;
      turnFlags = { unit: u, moved: false, acted: false };
      takeTurn(u, units, occupied, crystals, field, rng, log, config);
      const flags = turnFlags; turnFlags = null;
      turnEndTick(u, occupied, log);
      marshPoison(u, field && typeof field === 'object' ? field : null, log);
      endTurnFacing(u, units, field, ct0);
      pushState(u.name);
      endTurnCt(u, flags);
      turns++;
    }
  }

  let winner;
  if (!aliveTeam('A') && !aliveTeam('B')) winner = 'draw';
  else if (!aliveTeam('B')) winner = 'A';
  else if (!aliveTeam('A')) winner = 'B';
  else {
    // Turn-cap tiebreak: more living units wins (a tactics match is decided by
    // bodies, not HP bars); summed HP only breaks a body-count tie.
    const living = (t) => units.filter((u) => u.team === t && u.alive && !hasStatus(u, 'Petrify'));
    const la = living('A'), lb = living('B');
    if (la.length !== lb.length) winner = la.length > lb.length ? 'A' : 'B';
    else {
      const sum = (us) => us.reduce((s, u) => s + u.hp, 0);
      const ha = sum(la), hb = sum(lb);
      winner = ha > hb ? 'A' : hb > ha ? 'B' : 'draw';
    }
  }
  return {
    winner, turns, log, frames,
    units: units.map((u) => ({ name: u.name, team: u.team, alive: u.alive })),
  };
}

// Decision replay (tools/emu-battle/replay.mjs): run ONE unit's turn on a board the
// caller has already built (unit objects shaped like simulateBattle's), and report
// where it ended up and what it logged. It mutates the units it's given, so pass
// copies. Default rng = () => 0: every roll succeeds and there's no exploration
// noise, so this is the AI's own best choice. Doesn't change any battle behaviour.
export function decideTurn(u, units, config = {}, rng = () => 0) {
  setTune(config);
  const field = makeField(config);
  const occupied = new Set(units.filter((o) => o.alive).map((o) => `${o.x},${o.y}`));
  const log = [];
  const ct0 = u.ct;
  try { takeTurn(u, units, occupied, new Set(), field, rng, log, config); endTurnFacing(u, units, field, ct0); } finally { setActiveField(null); }
  return { x: u.x, y: u.y, log, charging: u.charging ? { action: u.charging.action.name, target: u.charging.target && u.charging.target.name } : null };
}

export function runBattles(teamA, teamB, opts = {}, rng = Math.random) {
  const n = opts.n ?? opts.runs ?? 150;
  let a = 0, b = 0, d = 0;
  const survival = new Map(); // team+name -> { name, team, survived }
  for (let i = 0; i < n; i++) {
    const res = simulateBattle(teamA, teamB, opts, rng);
    if (res.winner === 'A') a++; else if (res.winner === 'B') b++; else d++;
    for (const u of res.units) {
      const k = u.team + ' ' + u.name;
      const rec = survival.get(k) || { name: u.name, team: u.team, survived: 0 };
      if (u.alive) rec.survived++;
      survival.set(k, rec);
    }
  }
  const unitStats = [...survival.values()].map((r) => ({ name: r.name, team: r.team, survivalPct: r.survived / n }));
  return { n, aWins: a, bWins: b, draws: d, aWinPct: a / n, bWinPct: b / n, unitStats };
}

// ---- Reproducible-battle blob. The combatant specs + config + seed are everything
// simulateBattle needs to replay a fight bit-for-bit, so a `repro` is a self-contained
// debug capsule: paste it into replayRepro() and get the identical fight back (log +
// frames). Sets (a spec's `removed`, its `elements` affinity sets, the field's tile
// sets) don't survive plain JSON, so we tag/restore them. Terrain is embedded whole so
// the capsule needs nothing external. `seed` defaults to a fresh random one if absent.
// Sets, and the map's tile table (a Uint8Array, as base64), survive the round trip.
const toB64 = (u8) => (typeof btoa === 'function' ? btoa(String.fromCharCode(...u8)) : Buffer.from(u8).toString('base64'));
const fromB64 = (s) => (typeof atob === 'function' ? Uint8Array.from(atob(s), (c) => c.charCodeAt(0)) : new Uint8Array(Buffer.from(s, 'base64')));
const setReplacer = (k, v) => (v instanceof Set ? { __set: [...v] } : v instanceof Uint8Array ? { __u8: toB64(v) } : v);
const setReviver = (k, v) => (v && typeof v === 'object' && Array.isArray(v.__set) ? new Set(v.__set) : v && typeof v === 'object' && typeof v.__u8 === 'string' ? fromB64(v.__u8) : v);
export function serializeRepro(teamA, teamB, config = {}, seed) {
  const { log, frames, ...knobs } = config; // drop output toggles; keep terrain + the tuning knobs
  const s = seed != null ? seed : Math.floor(Math.random() * 0x7fffffff);
  return JSON.stringify({ v: 1, seed: s, config: knobs, teamA, teamB }, setReplacer, 2);
}
// Rebuild a fight from a serializeRepro() string and run it (with log + frames on by
// default). `overrides` can tweak the replay (e.g. { frames: false } for a quick log).
export function replayRepro(json, overrides = {}) {
  const blob = typeof json === 'string' ? JSON.parse(json, setReviver) : json;
  return simulateBattle(blob.teamA, blob.teamB, { ...blob.config, seed: blob.seed, log: true, frames: true, ...overrides });
}
